import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const ADMIN_PIN = "5327";
const MASTER_PASSCODE = "532700";
const STORAGE_KEYS = {
  deals: "drivewitharturo_saved_deals_v2",
  leads: "drivewitharturo_crm_leads_v2",
  inventory: "drivewitharturo_inventory_v2",
  overlays: "drivewitharturo_studio_overlays_v2",
  postingTasks: "drivewitharturo_posting_tasks_v1",
  reviews: "drivewitharturo_review_tracker_v1",
  referrals: "drivewitharturo_referral_checks_v1",
  deliveries: "drivewitharturo_delivery_center_v1",
  lastBackup: "drivewitharturo_last_backup_v1",
};

type TaxAreaKey = "chicago" | "cook" | "other" | "custom";
type LeadStatus =
  | "New"
  | "Contacted"
  | "Appointment Set"
  | "Working Deal"
  | "Sold"
  | "Lost";
type LeadTemperature = "HOT" | "WARM" | "COLD";
type StudioStyle = "luxury" | "aggressive" | "clean" | "night" | "latino";
type VideoFormat = "9:16" | "1:1" | "16:9";
type StudioSpinSpeed = "slow" | "normal" | "fast";
type StudioFrameMode = "vehicle" | "delivery";
type WatermarkPosition =
  | "bottom-center"
  | "bottom-left"
  | "bottom-right"
  | "center"
  | "diagonal";
type DeliveryTemplate =
  | "congratulations"
  | "welcome"
  | "veteran"
  | "family"
  | "business"
  | "firstTime"
  | "referral";

type Deal = {
  customerName: string;
  phone: string;
  vehicleDescription: string;
  stockNumber: string;
  listPrice: string | number;
  taxArea: TaxAreaKey;
  customRate: string | number;
  outOfStateTaxable: boolean;
  tradeValue: string | number;
  tradePayoff: string | number;
  docFee: string | number;
  titleReg: string | number;
  governmentFees: string | number;
  taxableERTFee: string | number;
  prepFee: string | number;
  serviceContract: string | number;
  gap: string | number;
  shippingCost: string | number;
  downPayment: string | number;
  apr: string | number;
  term: string | number;
};

type CalculationResult = {
  price: number;
  trade: number;
  payoff: number;
  tradeAllowance: number;
  doc: number;
  prep: number;
  taxableERTFee: number;
  serviceContract: number;
  gap: number;
  shipping: number;
  taxableSubtotal: number;
  baseTax: number;
  cityTax: number;
  detailedTotalTax: number;
  totalTax: number;
  titleAndPlates: number;
  governmentFees: number;
  nonTaxableProducts: number;
  nonTaxableFees: number;
  totalBeforeDown: number;
  balanceAfterDown: number;
  monthlyPayment: number;
  taxSavings: number;
};

type SavedDeal = {
  id: number;
  date: string;
  salesperson: string;
  deal: Deal;
  result: CalculationResult;
};

type IdentificationType =
  | "SSN"
  | "ITIN"
  | "Both"
  | "Prefer To Discuss In Person";
type PreferredLanguage = "English" | "Español" | "Bilingual";

type TradeInfo = {
  year: string;
  make: string;
  model: string;
  trim: string;
  mileage: string;
  majorIssues: string;
  frameDamage: "Yes" | "No" | "Not Sure";
  rust: "Yes" | "No" | "Minor Surface Rust" | "Not Sure";
  titleStatus: "Clean" | "Rebuilt" | "Salvage" | "Not Sure";
  payoffAmount: string;
  lienholder: string;
};

type Lead = {
  id: number;
  createdAt: string;
  name: string;
  phone: string;
  email: string;
  vehicle: string;
  source: string;
  status: LeadStatus;
  temperature: LeadTemperature;
  notes: string;
  nextFollowUp: string;
  preferredLanguage: PreferredLanguage;
  monthlyBudget: string;
  downPaymentAvailable: string;
  employmentType: string;
  monthlyIncomeRange: string;
  residencyStatus: string;
  identificationType: IdentificationType;
  incomeVerification: string[];
  hasTrade: boolean;
  trade: TradeInfo;
};

type InventoryVehicle = {
  id: number;
  title: string;
  price: string | number;
  mileage: string | number;
  stockNumber: string;
  imageUrl: string;
  vehicleUrl: string;
  published: boolean;
  featured: boolean;
};

type OverlayTemplate = {
  id: number;
  text: string;
  position: "top" | "center" | "bottom";
  color: string;
  size: number;
};

type VehicleContentProfile = {
  vehicleType: string;
  contentScore: number;
  businessScore: number;
  familyScore: number;
  luxuryScore: number;
  firstTimeBuyerScore: number;
  reason: string;
};

type StoryboardScene = {
  scene: string;
  time: string;
  shot: string;
  text: string;
};

type MarketingPackage = {
  vehicle: string;
  profile: VehicleContentProfile;
  bpm: number;
  cutTiming: string;
  bestShotPlan: string[];
  hooks: string[];
  captions: string[];
  whatsappPost: string;
  marketplaceListing: string;
  offerUpListing: string;
  voiceover: string;
  storyboard: StoryboardScene[];
  commercialAudioNote: string;
  deliveryFacebookPost?: string;
  deliveryGoogleReviewRequest?: string;
  deliveryInstagramStory?: string;
  deliveryWhatsAppStatus?: string;
  deliveryTikTokCover?: string;
  thumbnailText?: string;
};

type ReelScene = {
  scene: string;
  time: string;
  shot: string;
  editDirection: string;
  onScreenText: string;
};

type ReelPlan = {
  title: string;
  platform: string;
  duration: number;
  bpm: number;
  cutTiming: string;
  hook: string;
  voiceover: string;
  thumbnailText: string;
  caption: string;
  cta: string;
  sceneOrder: ReelScene[];
  exportChecklist: string[];
};

type PostingPlatform =
  | "TikTok"
  | "Facebook"
  | "Instagram"
  | "YouTube Shorts"
  | "WhatsApp"
  | "Marketplace"
  | "OfferUp"
  | "Google Business";
type PostingStatus = "Not Started" | "Ready" | "Posted" | "Needs Update";

type PostingTask = {
  id: number;
  vehicle: string;
  stockNumber: string;
  platform: PostingPlatform;
  status: PostingStatus;
  caption: string;
  checklist: string[];
  createdAt: string;
  postedAt?: string;
};

type ReviewPlatform = "Google" | "CarGurus" | "Cars.com" | "Autotrader";
type ReviewStatus =
  | "Not Requested"
  | "Requested"
  | "Posted"
  | "Needs Follow-Up";

type ReviewRecord = {
  id: number;
  customerName: string;
  phone: string;
  email: string;
  vehicle: string;
  deliveryDate: string;
  platform: ReviewPlatform;
  status: ReviewStatus;
  linkSentAt: string;
  postedAt: string;
  screenshotSaved: boolean;
  referralConnected: boolean;
  notes: string;
};

type ReferralCheckStatus = "Pending" | "Approved" | "Paid" | "Not Eligible";

type ReferralRecord = {
  id: number;
  referrerName: string;
  referrerPhone: string;
  referredCustomer: string;
  vehiclePurchased: string;
  saleDate: string;
  checkStatus: ReferralCheckStatus;
  checkNumber: string;
  paidDate: string;
  amount: number;
  notes: string;
};

type DeliveryRecord = {
  id: number;
  customerName: string;
  vehicle: string;
  deliveryDate: string;
  leadSource: string;
  isReferral: boolean;
  isBusinessOwner: boolean;
  isFirstTimeBuyer: boolean;
  reviewRequested: boolean;
  reviewPosted: boolean;
  referralEligible: boolean;
  photoUploaded: boolean;
  videoUploaded: boolean;
  notes: string;
};

type CustomerType =
  | "First-Time Buyer"
  | "Credit Rebuild"
  | "ITIN Buyer"
  | "Business Owner"
  | "Family Buyer"
  | "Cash Buyer"
  | "Referral"
  | "General Shopper";

type LeadStrategy = {
  leadScore: number;
  appointmentProbability: number;
  customerType: CustomerType;
  mainObjection: string;
  nextBestAction: string;
  strategy: string;
  recommendedReply: string;
  reasons: string[];
};

type FollowUpPriority = "Overdue" | "Due Today" | "Due Tomorrow" | "Scheduled";

type TradeAnalysis = {
  estimatedValue: number;
  payoff: number;
  equity: number;
  rangeLow: number;
  rangeHigh: number;
  strategy: string;
  riskLevel: "Low" | "Medium" | "High";
};

const TAX_AREAS: Record<
  TaxAreaKey,
  { label: string; display: string; baseRate: number; cityRate: number }
> = {
  chicago: {
    label: "Chicago / Cook",
    display: "9.5% split",
    baseRate: 0.085,
    cityRate: 0.01,
  },
  cook: { label: "Cook County", display: "8.5%", baseRate: 0.085, cityRate: 0 },
  other: { label: "Other IL", display: "8.5%", baseRate: 0.085, cityRate: 0 },
  custom: {
    label: "Out of State",
    display: "Custom",
    baseRate: 0,
    cityRate: 0,
  },
};

const DEFAULT_DEAL: Deal = {
  customerName: "",
  phone: "",
  vehicleDescription: "",
  stockNumber: "",
  listPrice: 19500,
  taxArea: "chicago",
  customRate: 0,
  outOfStateTaxable: false,
  tradeValue: 0,
  tradePayoff: 0,
  docFee: 365,
  titleReg: 316,
  governmentFees: 0,
  taxableERTFee: 30,
  prepFee: 995,
  serviceContract: 0,
  gap: 0,
  shippingCost: 0,
  downPayment: 0,
  apr: 12.99,
  term: 72,
};

const DEFAULT_TRADE_INFO: TradeInfo = {
  year: "",
  make: "",
  model: "",
  trim: "",
  mileage: "",
  majorIssues: "",
  frameDamage: "No",
  rust: "No",
  titleStatus: "Not Sure",
  payoffAmount: "",
  lienholder: "",
};

const DEFAULT_LEAD_FORM: Omit<Lead, "id" | "createdAt"> = {
  name: "",
  phone: "",
  email: "",
  vehicle: "",
  source: "Facebook",
  status: "New",
  temperature: "WARM",
  notes: "",
  nextFollowUp: "",
  preferredLanguage: "Bilingual",
  monthlyBudget: "",
  downPaymentAvailable: "",
  employmentType: "",
  monthlyIncomeRange: "",
  residencyStatus: "",
  identificationType: "Prefer To Discuss In Person",
  incomeVerification: [],
  hasTrade: false,
  trade: { ...DEFAULT_TRADE_INFO },
};

const INCOME_VERIFICATION_OPTIONS = [
  "Most Recent Paystub",
  "Bank Statements",
  "Tax Returns",
  "Cash Income Documentation",
  "Proof of Residence",
  "Not Sure",
];

const DEFAULT_OVERLAYS: OverlayTemplate[] = [
  {
    id: 1,
    text: "AVAILABLE NOW",
    position: "center",
    color: "#ffffff",
    size: 34,
  },
  {
    id: 2,
    text: "HABLAMOS ESPAÑOL",
    position: "top",
    color: "#facc15",
    size: 28,
  },
  {
    id: 3,
    text: "DRIVE TODAY",
    position: "bottom",
    color: "#ef4444",
    size: 32,
  },
];

const AI_ARTURO = {
  name: "DriveWithArturo AI",
  dealership: "Chicago Cars US",
  phone: "(872) 364-0886",
  financeApplicationUrl:
    "https://www.chicagocarsus.com/finance-your-car/pre-approved",
  inventoryUrl: "https://www.chicagocarsus.com/pre-owned-cars",
  testimonialsUrl: "https://www.chicagocarsus.com/customer-testimonials",
  tone: "bilingual, trust-first, appointment-focused, high-energy",
  signatureFinanceLine:
    "I can't guarantee approvals, but I trust our finance manager more than the weather forecast in Chicago. Let's gather the right info and see what's possible.",
  signatureClose:
    "If the vehicle checks all the boxes and the numbers make sense, let's make it happen.",
  guardrails: [
    "Never collect SSN or ITIN numbers on DriveWithArturo.biz.",
    "Never promise approval, rate, payment, or lender decision.",
    "Send customers to the secure Chicago Cars US credit application for formal finance review.",
    "Be persuasive, but stay honest, helpful, and appointment-focused.",
  ],
};

const POSTING_PLATFORMS: PostingPlatform[] = [
  "TikTok",
  "Facebook",
  "Instagram",
  "YouTube Shorts",
  "WhatsApp",
  "Marketplace",
  "OfferUp",
  "Google Business",
];
const POSTING_STATUSES: PostingStatus[] = [
  "Not Started",
  "Ready",
  "Posted",
  "Needs Update",
];

const REVIEW_LINKS: Record<ReviewPlatform, string> = {
  Google: "https://share.google/rUXNhaMWNbNKClKaZ",
  CarGurus: "https://cargur.us/Wvlgq",
  "Cars.com":
    "https://www.cars.com/dealers/5254021/chicago-cars-us/dealer_reviews/write-a-review/",
  Autotrader: "",
};

const TIKTOK_LINKS = {
  chicagoCars: "https://www.tiktok.com/@chicagocars.us",
  driveWithArturo: "https://www.tiktok.com/@drivewitharturo",
};

const POPL_BUSINESS_CARD_URL = "https://popl.co/profile/drivewitharturo";
const REVIEW_PLATFORMS: ReviewPlatform[] = [
  "Google",
  "CarGurus",
  "Cars.com",
  "Autotrader",
];
const REVIEW_STATUSES: ReviewStatus[] = [
  "Not Requested",
  "Requested",
  "Posted",
  "Needs Follow-Up",
];

const EMPTY_REVIEW_RECORD: ReviewRecord = {
  id: 0,
  customerName: "",
  phone: "",
  email: "",
  vehicle: "",
  deliveryDate: "",
  platform: "Google",
  status: "Not Requested",
  linkSentAt: "",
  postedAt: "",
  screenshotSaved: false,
  referralConnected: false,
  notes: "",
};
const REFERRAL_STATUSES: ReferralCheckStatus[] = [
  "Pending",
  "Approved",
  "Paid",
  "Not Eligible",
];

function toNumber(value: unknown): number {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function cleanMoney(value: unknown): number {
  return Math.max(toNumber(value), 0);
}

function roundCurrency(value: unknown): number {
  return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
}

function formatPlainNumber(value: string | number): string {
  const numeric = Number(String(value || "0").replace(/,/g, ""));
  if (!Number.isFinite(numeric)) return "0";
  return numeric.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function money(value: unknown): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(value));
}

function safeJsonParse<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function cleanNumericInput(value: string): string {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned;
}

function normalizeTradeInfo(
  trade: Partial<TradeInfo> | undefined | null
): TradeInfo {
  return {
    ...DEFAULT_TRADE_INFO,
    ...(trade || {}),
    year: String(trade?.year ?? ""),
    make: String(trade?.make ?? ""),
    model: String(trade?.model ?? ""),
    trim: String(trade?.trim ?? ""),
    mileage: String(trade?.mileage ?? ""),
    majorIssues: String(trade?.majorIssues ?? ""),
    payoffAmount: String(trade?.payoffAmount ?? ""),
    lienholder: String(trade?.lienholder ?? ""),
  };
}

function normalizeLead(lead: Partial<Lead> | undefined | null): Lead {
  const safe = lead || {};
  return {
    id: Number(safe.id || Date.now()),
    createdAt: String(safe.createdAt || ""),
    name: String(safe.name || ""),
    phone: String(safe.phone || ""),
    email: String(safe.email || ""),
    vehicle: String(safe.vehicle || ""),
    source: String(safe.source || "Unknown"),
    status: (safe.status || "New") as LeadStatus,
    temperature: (safe.temperature || "WARM") as LeadTemperature,
    notes: String(safe.notes || ""),
    nextFollowUp: String(safe.nextFollowUp || ""),
    preferredLanguage: (safe.preferredLanguage ||
      "Bilingual") as PreferredLanguage,
    monthlyBudget: String(safe.monthlyBudget || ""),
    downPaymentAvailable: String(safe.downPaymentAvailable || ""),
    employmentType: String(safe.employmentType || ""),
    monthlyIncomeRange: String(safe.monthlyIncomeRange || ""),
    residencyStatus: String(safe.residencyStatus || ""),
    identificationType: (safe.identificationType ||
      "Prefer To Discuss In Person") as IdentificationType,
    incomeVerification: Array.isArray(safe.incomeVerification)
      ? safe.incomeVerification.map(String)
      : [],
    hasTrade: Boolean(safe.hasTrade),
    trade: normalizeTradeInfo(safe.trade),
  };
}

function createEmptyLeadForm(): Omit<Lead, "id" | "createdAt"> {
  return {
    ...DEFAULT_LEAD_FORM,
    incomeVerification: [],
    trade: normalizeTradeInfo(DEFAULT_TRADE_INFO),
  };
}

function createEmptyReviewRecord(): ReviewRecord {
  return { ...EMPTY_REVIEW_RECORD };
}

function createEmptyReferralRecord(): ReferralRecord {
  return {
    id: 0,
    referrerName: "",
    referrerPhone: "",
    referredCustomer: "",
    vehiclePurchased: "",
    saleDate: "",
    checkStatus: "Pending",
    checkNumber: "",
    paidDate: "",
    amount: 100,
    notes: "",
  };
}

function createEmptyDeliveryRecord(): DeliveryRecord {
  return {
    id: 0,
    customerName: "",
    vehicle: "",
    deliveryDate: new Date().toLocaleDateString(),
    leadSource: "",
    isReferral: false,
    isBusinessOwner: false,
    isFirstTimeBuyer: false,
    reviewRequested: false,
    reviewPosted: false,
    referralEligible: false,
    photoUploaded: false,
    videoUploaded: false,
    notes: "",
  };
}

function normalizeDeliveryRecord(
  delivery: Partial<DeliveryRecord> | undefined | null
): DeliveryRecord {
  const safe = delivery || {};
  return {
    ...createEmptyDeliveryRecord(),
    ...safe,
    id: Number(safe.id || Date.now()),
    customerName: String(safe.customerName || ""),
    vehicle: String(safe.vehicle || ""),
    deliveryDate: String(safe.deliveryDate || ""),
    leadSource: String(safe.leadSource || ""),
    isReferral: Boolean(safe.isReferral),
    isBusinessOwner: Boolean(safe.isBusinessOwner),
    isFirstTimeBuyer: Boolean(safe.isFirstTimeBuyer),
    reviewRequested: Boolean(safe.reviewRequested),
    reviewPosted: Boolean(safe.reviewPosted),
    referralEligible: Boolean(safe.referralEligible),
    photoUploaded: Boolean(safe.photoUploaded),
    videoUploaded: Boolean(safe.videoUploaded),
    notes: String(safe.notes || ""),
  };
}

function normalizeReviewRecord(
  review: Partial<ReviewRecord> | undefined | null
): ReviewRecord {
  const safe = review || {};
  return {
    ...createEmptyReviewRecord(),
    ...safe,
    id: Number(safe.id || Date.now()),
    customerName: String(safe.customerName || ""),
    phone: String(safe.phone || ""),
    email: String(safe.email || ""),
    vehicle: String(safe.vehicle || ""),
    deliveryDate: String(safe.deliveryDate || ""),
    platform: (safe.platform || "Google") as ReviewPlatform,
    status: (safe.status || "Not Requested") as ReviewStatus,
    linkSentAt: String(safe.linkSentAt || ""),
    postedAt: String(safe.postedAt || ""),
    screenshotSaved: Boolean(safe.screenshotSaved),
    referralConnected: Boolean(safe.referralConnected),
    notes: String(safe.notes || ""),
  };
}

function normalizeReferralRecord(
  referral: Partial<ReferralRecord> | undefined | null
): ReferralRecord {
  const safe = referral || {};
  return {
    ...createEmptyReferralRecord(),
    ...safe,
    id: Number(safe.id || Date.now()),
    referrerName: String(safe.referrerName || ""),
    referrerPhone: String(safe.referrerPhone || ""),
    referredCustomer: String(safe.referredCustomer || ""),
    vehiclePurchased: String(safe.vehiclePurchased || ""),
    saleDate: String(safe.saleDate || ""),
    checkStatus: (safe.checkStatus || "Pending") as ReferralCheckStatus,
    checkNumber: String(safe.checkNumber || ""),
    paidDate: String(safe.paidDate || ""),
    amount: cleanMoney(safe.amount || 100) || 100,
    notes: String(safe.notes || ""),
  };
}

function calculateOTD(input: Partial<Deal> = {}): CalculationResult {
  const deal = { ...DEFAULT_DEAL, ...input };
  const isOutOfState = deal.taxArea === "custom";
  const isTaxable = !isOutOfState || deal.outOfStateTaxable;
  const rate = isOutOfState
    ? {
        baseRate: isTaxable ? cleanMoney(deal.customRate) / 100 : 0,
        cityRate: 0,
      }
    : TAX_AREAS[deal.taxArea] || TAX_AREAS.chicago;

  const price = cleanMoney(deal.listPrice);
  const trade = cleanMoney(deal.tradeValue);
  const payoff = cleanMoney(deal.tradePayoff);
  const tradeAllowance = roundCurrency(trade - payoff);
  const doc = cleanMoney(deal.docFee);
  const prep = cleanMoney(deal.prepFee);
  const titleAndPlates = isOutOfState ? 0 : cleanMoney(deal.titleReg);
  const governmentFees = isOutOfState ? 0 : cleanMoney(deal.governmentFees);
  const taxableERTFee = cleanMoney(deal.taxableERTFee);
  const serviceContract = cleanMoney(deal.serviceContract);
  const gap = cleanMoney(deal.gap);
  const shipping = cleanMoney(deal.shippingCost);
  const downPayment = cleanMoney(deal.downPayment);

  // Deal amount always includes price/doc/prep/ERT/trade. Tax is optional for out-of-state deals.
  // Out-of-state default: not taxable, no title/plate/government fees, ERT remains charged.
  const dealSubtotal = Math.max(price + doc + prep + taxableERTFee - trade, 0);
  const taxableSubtotal = isTaxable ? dealSubtotal : 0;
  const baseTax = roundCurrency(taxableSubtotal * rate.baseRate);
  const cityTax = roundCurrency(taxableSubtotal * rate.cityRate);
  const detailedTotalTax = roundCurrency(baseTax + cityTax);
  const totalTax = roundCurrency(Math.round(detailedTotalTax));
  const nonTaxableProducts = roundCurrency(serviceContract + gap);
  const nonTaxableFees = roundCurrency(
    titleAndPlates + governmentFees + nonTaxableProducts + shipping
  );
  const totalBeforeDown = roundCurrency(
    dealSubtotal + totalTax + nonTaxableFees + payoff
  );
  const balanceAfterDown = Math.max(
    roundCurrency(totalBeforeDown - downPayment),
    0
  );
  const taxSavings = isTaxable
    ? roundCurrency(trade * (rate.baseRate + rate.cityRate))
    : 0;

  const apr = cleanMoney(deal.apr) / 100;
  const term = Math.max(cleanMoney(deal.term), 1);
  const monthlyRate = apr / 12;
  const monthlyPayment =
    monthlyRate === 0
      ? roundCurrency(balanceAfterDown / term)
      : roundCurrency(
          (balanceAfterDown * monthlyRate * Math.pow(1 + monthlyRate, term)) /
            (Math.pow(1 + monthlyRate, term) - 1)
        );

  return {
    price,
    trade,
    payoff,
    tradeAllowance,
    doc,
    prep,
    taxableERTFee,
    serviceContract,
    gap,
    shipping,
    taxableSubtotal: roundCurrency(taxableSubtotal),
    baseTax,
    cityTax,
    detailedTotalTax,
    totalTax,
    titleAndPlates,
    governmentFees,
    nonTaxableProducts,
    nonTaxableFees,
    totalBeforeDown,
    balanceAfterDown,
    monthlyPayment,
    taxSavings,
  };
}

function buildArturoReply(
  message: string,
  deal: Deal,
  result: CalculationResult
): string {
  const lower = message.toLowerCase();
  const vehicle = deal.vehicleDescription || "the vehicle you're interested in";

  if (
    lower.includes("approved") ||
    lower.includes("approval") ||
    lower.includes("credit") ||
    lower.includes("bad credit") ||
    lower.includes("itin")
  ) {
    return `${AI_ARTURO.signatureFinanceLine} We do not collect SSN or ITIN numbers here. For the formal finance review, use the secure Chicago Cars US application: ${AI_ARTURO.financeApplicationUrl}`;
  }

  if (
    lower.includes("ssn") ||
    lower.includes("social") ||
    lower.includes("itin number")
  ) {
    return "For your privacy, please do not send SSN or ITIN numbers here. You can simply tell us whether you have SSN, ITIN, both, or prefer to discuss in person. The formal application is completed securely through Chicago Cars US.";
  }

  if (lower.includes("trade")) {
    return "Great — a trade can sometimes help more than people realize. Send the year, make, model, trim, mileage, major issues, frame damage/rust status, payoff amount, and lienholder. Arturo can review it and give you the next best step.";
  }

  if (
    lower.includes("otd") ||
    lower.includes("out the door") ||
    lower.includes("price") ||
    lower.includes("payment")
  ) {
    return `For ${vehicle}, the current estimate in the calculator is ${money(
      result.totalBeforeDown
    )} OTD with an estimated balance of ${money(
      result.balanceAfterDown
    )} after down payment. Final numbers depend on taxes, fees, trade, address, and lender approval.`;
  }

  if (
    lower.includes("appointment") ||
    lower.includes("test drive") ||
    lower.includes("come in")
  ) {
    return `Perfect. The best way to know if ${vehicle} is the right fit is to see it and drive it. Text Arturo at ${AI_ARTURO.phone} with the best time for you.`;
  }

  if (lower.includes("available") || lower.includes("still have")) {
    return `I can help check availability on ${vehicle}. Inventory moves fast, so the strongest next step is to text Arturo at ${AI_ARTURO.phone} and schedule a time to see it.`;
  }

  return `I can help with availability, OTD price, credit questions, trade-ins, and appointments. ${AI_ARTURO.signatureClose} Text Arturo at ${AI_ARTURO.phone}.`;
}

export default function OTDPriceCalculatorApp() {
  const [activeTab, setActiveTab] = useState<
    | "dashboard"
    | "salesfloor"
    | "analytics"
    | "reviews"
    | "delivery"
    | "otd"
    | "crm"
    | "inventory"
    | "studio"
    | "admin"
  >("dashboard");
  const [language, setLanguage] = useState<"en" | "es">("en");
  const [deal, setDeal] = useState<Deal>(DEFAULT_DEAL);
  const [savedDeals, setSavedDeals] = useState<SavedDeal[]>(() =>
    safeJsonParse<SavedDeal[]>(STORAGE_KEYS.deals, [])
  );
  const [leads, setLeads] = useState<Lead[]>(() => {
    const data = safeJsonParse<Lead[]>(STORAGE_KEYS.leads, []);
    return Array.isArray(data) ? data.map(normalizeLead) : [];
  });
  const [inventory, setInventory] = useState<InventoryVehicle[]>(() =>
    safeJsonParse<InventoryVehicle[]>(STORAGE_KEYS.inventory, [])
  );
  const [inventoryForm, setInventoryForm] = useState<InventoryVehicle>({
    id: 0,
    title: "",
    price: "",
    mileage: "",
    stockNumber: "",
    imageUrl: "",
    vehicleUrl: "",
    published: true,
    featured: true,
  });
  const [leadForm, setLeadForm] = useState<Omit<Lead, "id" | "createdAt">>(() =>
    createEmptyLeadForm()
  );
  const [studioPhotos, setStudioPhotos] = useState<string[]>([]);
  const [studioFrame, setStudioFrame] = useState(0);
  const [studioPlaying, setStudioPlaying] = useState(false);
  const [studioSpinSpeed, setStudioSpinSpeed] =
    useState<StudioSpinSpeed>("normal");
  const [studioFrameMode, setStudioFrameMode] =
    useState<StudioFrameMode>("vehicle");
  const [clientNameForFrame, setClientNameForFrame] = useState("");
  const [deliveryVehicleForFrame, setDeliveryVehicleForFrame] = useState("");
  const [deliveryTemplate, setDeliveryTemplate] =
    useState<DeliveryTemplate>("congratulations");
  const [watermarkPosition, setWatermarkPosition] =
    useState<WatermarkPosition>("bottom-center");
  const [watermarkOpacity, setWatermarkOpacity] = useState(92);
  const [deliveryContentPackage, setDeliveryContentPackage] =
    useState<MarketingPackage | null>(null);
  const [studioStyle, setStudioStyle] = useState<StudioStyle>("aggressive");
  const [videoFormat, setVideoFormat] = useState<VideoFormat>("9:16");
  const [overlayTemplates, setOverlayTemplates] = useState<OverlayTemplate[]>(
    () =>
      safeJsonParse<OverlayTemplate[]>(STORAGE_KEYS.overlays, DEFAULT_OVERLAYS)
  );
  const [activeOverlay, setActiveOverlay] = useState<OverlayTemplate>(
    DEFAULT_OVERLAYS[0]
  );
  const [overlayDraft, setOverlayDraft] = useState<OverlayTemplate>({
    id: 0,
    text: "",
    position: "center",
    color: "#ffffff",
    size: 30,
  });
  const [exportingVideo, setExportingVideo] = useState(false);
  const [exportedVideoUrl, setExportedVideoUrl] = useState("");
  const [generatedHooks, setGeneratedHooks] = useState<string[]>([]);
  const [generatedFollowUp, setGeneratedFollowUp] = useState("");
  const [audioBpm, setAudioBpm] = useState(120);
  const [marketingPackage, setMarketingPackage] =
    useState<MarketingPackage | null>(null);
  const [reelDuration, setReelDuration] = useState(18);
  const [reelPlatform, setReelPlatform] = useState("TikTok / Reels / Shorts");
  const [reelPlan, setReelPlan] = useState<ReelPlan | null>(null);
  const [postingTasks, setPostingTasks] = useState<PostingTask[]>(() =>
    safeJsonParse<PostingTask[]>(STORAGE_KEYS.postingTasks, [])
  );
  const [reviewRecords, setReviewRecords] = useState<ReviewRecord[]>(() => {
    const data = safeJsonParse<ReviewRecord[]>(STORAGE_KEYS.reviews, []);
    return Array.isArray(data) ? data.map(normalizeReviewRecord) : [];
  });
  const [referralRecords, setReferralRecords] = useState<ReferralRecord[]>(
    () => {
      const data = safeJsonParse<ReferralRecord[]>(STORAGE_KEYS.referrals, []);
      return Array.isArray(data) ? data.map(normalizeReferralRecord) : [];
    }
  );
  const [deliveryRecords, setDeliveryRecords] = useState<DeliveryRecord[]>(
    () => {
      const data = safeJsonParse<DeliveryRecord[]>(STORAGE_KEYS.deliveries, []);
      return Array.isArray(data) ? data.map(normalizeDeliveryRecord) : [];
    }
  );
  const [reviewDraft, setReviewDraft] = useState<ReviewRecord>(() =>
    createEmptyReviewRecord()
  );
  const [referralDraft, setReferralDraft] = useState<ReferralRecord>(() =>
    createEmptyReferralRecord()
  );
  const [deliveryDraft, setDeliveryDraft] = useState<DeliveryRecord>(() =>
    createEmptyDeliveryRecord()
  );
  const [lastBackupDate, setLastBackupDate] = useState<string>(
    () => localStorage.getItem(STORAGE_KEYS.lastBackup) || "Never"
  );
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<
    { id: number; sender: "bot" | "user"; text: string }[]
  >([
    {
      id: 1,
      sender: "bot",
      text: "Hey, I’m Arturo’s AI assistant. Ask me about OTD pricing, down payment, trade-ins, appointments, or credit.",
    },
  ]);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [appUnlocked, setAppUnlocked] = useState(
    () => sessionStorage.getItem("dwa_master_unlock") === "true"
  );
  const [passcodeInput, setPasscodeInput] = useState("");
  const studioCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const result = useMemo(() => calculateOTD(deal), [deal]);
  const hotLeads = leads.filter((lead) => lead.temperature === "HOT").length;
  const appointments = leads.filter(
    (lead) => lead.status === "Appointment Set"
  ).length;
  const soldLeads = leads.filter((lead) => lead.status === "Sold").length;
  const totalPipeline = savedDeals.reduce(
    (sum, item) => sum + item.result.totalBeforeDown,
    0
  );
  const publishedInventory = inventory.filter((vehicle) => vehicle.published);
  const inventoryContentProfiles = inventory.map((vehicle) => ({
    vehicle,
    profile: detectVehicleType(vehicle.title),
  }));
  const topInventoryUnit = [...inventoryContentProfiles].sort(
    (a, b) => b.profile.contentScore - a.profile.contentScore
  )[0];
  const averageMarketScore = inventoryContentProfiles.length
    ? Math.round(
        inventoryContentProfiles.reduce(
          (sum, item) => sum + item.profile.contentScore,
          0
        ) / inventoryContentProfiles.length
      )
    : 0;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.deals, JSON.stringify(savedDeals));
  }, [savedDeals]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.leads, JSON.stringify(leads));
  }, [leads]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.inventory, JSON.stringify(inventory));
  }, [inventory]);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEYS.overlays,
      JSON.stringify(overlayTemplates)
    );
  }, [overlayTemplates]);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEYS.postingTasks,
      JSON.stringify(postingTasks)
    );
  }, [postingTasks]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.reviews, JSON.stringify(reviewRecords));
  }, [reviewRecords]);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEYS.referrals,
      JSON.stringify(referralRecords)
    );
  }, [referralRecords]);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEYS.deliveries,
      JSON.stringify(deliveryRecords)
    );
  }, [deliveryRecords]);

  useEffect(() => {
    if (!studioPlaying || studioPhotos.length <= 1) return;
    const speed =
      studioSpinSpeed === "slow"
        ? 850
        : studioSpinSpeed === "fast"
        ? 110
        : studioStyle === "luxury"
        ? 440
        : studioStyle === "aggressive"
        ? 160
        : 260;
    const timer = window.setInterval(
      () => setStudioFrame((frame) => (frame + 1) % studioPhotos.length),
      speed
    );
    return () => window.clearInterval(timer);
  }, [studioPlaying, studioPhotos.length, studioStyle, studioSpinSpeed]);

  const updateDeal = <K extends keyof Deal>(key: K, value: Deal[K]) => {
    setDeal((current) => ({ ...current, [key]: value }));
  };

  const updateLeadTrade = <K extends keyof TradeInfo>(
    key: K,
    value: TradeInfo[K]
  ) => {
    setLeadForm((current) => ({
      ...current,
      trade: { ...current.trade, [key]: value },
    }));
  };

  const toggleIncomeVerification = (option: string) => {
    setLeadForm((current) => {
      const exists = current.incomeVerification.includes(option);
      return {
        ...current,
        incomeVerification: exists
          ? current.incomeVerification.filter((item) => item !== option)
          : [...current.incomeVerification, option],
      };
    });
  };

  const getLeadReadinessScore = (
    lead: Omit<Lead, "id" | "createdAt"> | Lead
  ) => {
    let score = 0;
    if (lead.name) score += 10;
    if (lead.phone) score += 15;
    if (lead.vehicle) score += 10;
    if (lead.monthlyBudget) score += 10;
    if (lead.downPaymentAvailable) score += 15;
    if (lead.employmentType) score += 10;
    if (lead.monthlyIncomeRange) score += 10;
    if (lead.identificationType !== "Prefer To Discuss In Person") score += 10;
    if ((lead.incomeVerification || []).length > 0) score += 10;
    if (
      lead.hasTrade &&
      (normalizeTradeInfo(lead.trade).year ||
        normalizeTradeInfo(lead.trade).make ||
        normalizeTradeInfo(lead.trade).model)
    )
      score += 10;
    return Math.min(score, 100);
  };

  const getTradeEquityEstimate = (
    lead: Omit<Lead, "id" | "createdAt"> | Lead
  ) => {
    const trade = normalizeTradeInfo(lead.trade);
    const payoff = cleanMoney(trade.payoffAmount);
    const hasTradeBasics =
      lead.hasTrade && trade.year && trade.make && trade.model && trade.mileage;
    if (!hasTradeBasics) return null;

    const mileagePenalty = Math.min(cleanMoney(trade.mileage) / 100000, 2.5);
    const conditionPenalty =
      (trade.majorIssues ? 1500 : 0) +
      (trade.frameDamage === "Yes" ? 2500 : 0) +
      (trade.rust === "Yes" ? 1200 : 0);
    const estimatedValue = Math.max(
      1000,
      Math.round(8500 - mileagePenalty * 1200 - conditionPenalty)
    );
    return { estimatedValue, payoff, equity: estimatedValue - payoff };
  };

  const inferCustomerType = (
    lead: Omit<Lead, "id" | "createdAt"> | Lead
  ): CustomerType => {
    const combinedText =
      `${lead.source} ${lead.vehicle} ${lead.notes} ${lead.employmentType} ${lead.residencyStatus}`.toLowerCase();

    if (lead.identificationType === "ITIN" || combinedText.includes("itin"))
      return "ITIN Buyer";
    if (
      combinedText.includes("business") ||
      combinedText.includes("company") ||
      combinedText.includes("1099") ||
      combinedText.includes("self employed") ||
      combinedText.includes("contractor")
    )
      return "Business Owner";
    if (
      combinedText.includes("family") ||
      combinedText.includes("kids") ||
      combinedText.includes("suv") ||
      combinedText.includes("3rd") ||
      combinedText.includes("third row")
    )
      return "Family Buyer";
    if (combinedText.includes("referral")) return "Referral";
    if (combinedText.includes("cash")) return "Cash Buyer";
    if (
      combinedText.includes("first") ||
      lead.monthlyBudget === "Under $300" ||
      lead.monthlyBudget === "$300-$400"
    )
      return "First-Time Buyer";
    if (
      combinedText.includes("credit") ||
      combinedText.includes("repo") ||
      combinedText.includes("rebuild") ||
      combinedText.includes("bad")
    )
      return "Credit Rebuild";

    return "General Shopper";
  };

  const detectMainObjection = (
    lead: Omit<Lead, "id" | "createdAt"> | Lead
  ): string => {
    const text =
      `${lead.notes} ${lead.monthlyBudget} ${lead.downPaymentAvailable} ${lead.vehicle}`.toLowerCase();
    if (
      text.includes("credit") ||
      text.includes("repo") ||
      text.includes("bad")
    )
      return "Credit concern";
    if (text.includes("down") || lead.downPaymentAvailable === "$0")
      return "Down payment concern";
    if (text.includes("payment") || text.includes("budget"))
      return "Monthly payment concern";
    if (
      text.includes("think") ||
      text.includes("looking") ||
      text.includes("shop")
    )
      return "Still shopping";
    if (lead.hasTrade && normalizeTradeInfo(lead.trade).payoffAmount)
      return "Trade / payoff question";
    if (!lead.vehicle) return "No vehicle selected";
    return "Needs urgency and appointment";
  };

  const buildLeadStrategy = (
    lead: Omit<Lead, "id" | "createdAt"> | Lead
  ): LeadStrategy => {
    const readiness = getLeadReadinessScore(lead);
    const tradeInfo = getTradeEquityEstimate(lead);
    const customerType = inferCustomerType(lead);
    const mainObjection = detectMainObjection(lead);
    const reasons: string[] = [];

    if (lead.phone) reasons.push("Phone captured");
    if (lead.vehicle) reasons.push("Vehicle interest captured");
    if (lead.monthlyBudget) reasons.push("Budget range known");
    if (lead.downPaymentAvailable && lead.downPaymentAvailable !== "$0")
      reasons.push("Down payment available");
    if (lead.identificationType !== "Prefer To Discuss In Person")
      reasons.push(
        `${lead.identificationType} selected without collecting numbers`
      );
    if ((lead.incomeVerification || []).length > 0)
      reasons.push("Income verification available");
    if (lead.hasTrade) reasons.push("Trade opportunity");
    if (tradeInfo && tradeInfo.equity > 0)
      reasons.push(`Possible positive equity: ${money(tradeInfo.equity)}`);
    if (tradeInfo && tradeInfo.equity < 0)
      reasons.push(`Possible negative equity: ${money(tradeInfo.equity)}`);

    let appointmentProbability = readiness;
    if (lead.temperature === "HOT") appointmentProbability += 10;
    if (lead.status === "Appointment Set") appointmentProbability = 95;
    if (lead.status === "Lost")
      appointmentProbability = Math.min(appointmentProbability, 20);
    if (lead.status === "Sold") appointmentProbability = 100;
    if (lead.hasTrade) appointmentProbability += 5;
    appointmentProbability = Math.max(
      0,
      Math.min(100, Math.round(appointmentProbability))
    );

    let nextBestAction = "Call or text to confirm vehicle and appointment time";
    let strategy =
      "Keep it trust-first, bilingual if needed, and move toward a test drive.";
    let recommendedReply = `I can help with ${
      lead.vehicle || "the vehicle"
    }. If it checks the boxes and the numbers make sense, let's make it happen. What time works best for you to come in?`;

    if (mainObjection === "Credit concern") {
      nextBestAction =
        "Send secure credit application link after confirming vehicle and budget";
      strategy =
        "Reassure without promising approval. Focus on income, down payment, trade, and lender review.";
      recommendedReply = `${AI_ARTURO.signatureFinanceLine} We do not collect SSN or ITIN numbers here. The formal review is done securely through Chicago Cars US.`;
    } else if (mainObjection === "Down payment concern") {
      nextBestAction = "Ask what down payment is realistic today";
      strategy =
        "Do not pressure. Find the customer's real comfort zone and match inventory/payment structure.";
      recommendedReply =
        "No problem. What amount would be comfortable for you today if the vehicle and numbers made sense?";
    } else if (mainObjection === "Trade / payoff question") {
      nextBestAction = "Request trade photos and verify payoff/lienholder";
      strategy =
        "Use the trade as the bridge to structure the deal. Look for equity or negative equity early.";
      recommendedReply =
        "A trade can sometimes help more than people realize. Send me a few photos, mileage, payoff, and lienholder so Arturo can review the best path.";
    } else if (mainObjection === "No vehicle selected") {
      nextBestAction = "Ask vehicle needs and match inventory";
      strategy =
        "Do not jump into finance. First identify body style, budget, and use case.";
      recommendedReply =
        "What type of vehicle are you looking for — SUV, sedan, truck, van, or something for work?";
    }

    return {
      leadScore: readiness,
      appointmentProbability,
      customerType,
      mainObjection,
      nextBestAction,
      strategy,
      recommendedReply,
      reasons: reasons.length
        ? reasons
        : ["Needs more information before scoring accurately"],
    };
  };

  const getFollowUpPriority = (lead: Lead): FollowUpPriority => {
    if (!lead.nextFollowUp) return "Scheduled";
    const value = lead.nextFollowUp.toLowerCase();
    const now = new Date();
    const today = now.toLocaleDateString().toLowerCase();
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000)
      .toLocaleDateString()
      .toLowerCase();

    if (value.includes("overdue") || value.includes("yesterday"))
      return "Overdue";
    if (value.includes("today") || value.includes(today)) return "Due Today";
    if (value.includes("tomorrow") || value.includes(tomorrow))
      return "Due Tomorrow";
    return "Scheduled";
  };

  const buildTradeAnalysis = (lead: Lead): TradeAnalysis | null => {
    const tradeInfo = getTradeEquityEstimate(lead);
    if (!tradeInfo) return null;

    const safeTrade = normalizeTradeInfo(lead.trade);
    const conditionHit =
      (safeTrade.majorIssues ? 1 : 0) +
      (safeTrade.frameDamage === "Yes" ? 2 : 0) +
      (safeTrade.rust === "Yes" ? 1 : 0) +
      (safeTrade.titleStatus === "Salvage" ||
      safeTrade.titleStatus === "Rebuilt"
        ? 2
        : 0);

    const riskLevel: TradeAnalysis["riskLevel"] =
      conditionHit >= 3 ? "High" : conditionHit >= 1 ? "Medium" : "Low";
    const rangeLow = Math.max(
      500,
      Math.round(
        tradeInfo.estimatedValue *
          (riskLevel === "High" ? 0.72 : riskLevel === "Medium" ? 0.82 : 0.9)
      )
    );
    const rangeHigh = Math.round(
      tradeInfo.estimatedValue *
        (riskLevel === "High" ? 1.02 : riskLevel === "Medium" ? 1.08 : 1.15)
    );

    let strategy =
      "Use the trade as a deal structure tool. Verify condition and payoff before quoting final numbers.";
    if (tradeInfo.equity > 1500)
      strategy =
        "Possible positive equity. Lead with how the trade may reduce amount financed and help payment.";
    if (tradeInfo.equity < -1500)
      strategy =
        "Possible negative equity. Set expectations early and focus on down payment, vehicle selection, and lender flexibility.";
    if (riskLevel === "High")
      strategy =
        "High-risk trade. Request photos, VIN, title status, and manager review before relying on trade value.";

    return {
      estimatedValue: tradeInfo.estimatedValue,
      payoff: tradeInfo.payoff,
      equity: tradeInfo.equity,
      rangeLow,
      rangeHigh,
      strategy,
      riskLevel,
    };
  };

  const todayStamp = new Date().toLocaleDateString();
  const todaysLeads = leads.filter((lead) =>
    String(lead.createdAt || "").startsWith(todayStamp)
  );
  const workingDeals = leads.filter((lead) => lead.status === "Working Deal");
  const followUpDue = leads.filter(
    (lead) =>
      lead.nextFollowUp && lead.status !== "Sold" && lead.status !== "Lost"
  );
  const pendingTrades = leads.filter(
    (lead) => lead.hasTrade && lead.status !== "Sold" && lead.status !== "Lost"
  );
  const closeRatio = leads.length
    ? Math.round((soldLeads / leads.length) * 100)
    : 0;
  const potentialFrontGross = Math.max(
    0,
    Math.round(
      savedDeals.reduce(
        (sum, item) =>
          sum +
          Math.max(
            item.result.totalBeforeDown - cleanMoney(item.deal.listPrice),
            0
          ),
        0
      ) * 0.18
    )
  );
  const leadStrategyBoard = leads
    .map((lead) => ({ lead, strategy: buildLeadStrategy(lead) }))
    .sort(
      (a, b) =>
        b.strategy.leadScore +
        b.strategy.appointmentProbability -
        (a.strategy.leadScore + a.strategy.appointmentProbability)
    );
  const priorityLeads = leadStrategyBoard
    .filter(
      (item) => item.lead.status !== "Sold" && item.lead.status !== "Lost"
    )
    .slice(0, 5);
  const sourceCounts = leads.reduce<Record<string, number>>((acc, lead) => {
    const source = lead.source || "Unknown";
    acc[source] = (acc[source] || 0) + 1;
    return acc;
  }, {});
  const topLeadSources = (Object.entries(sourceCounts) as [string, number][])
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  const customerTypeCounts = leadStrategyBoard.reduce<Record<string, number>>(
    (acc, item) => {
      acc[item.strategy.customerType] =
        (acc[item.strategy.customerType] || 0) + 1;
      return acc;
    },
    {}
  );
  const topCustomerTypes = (
    Object.entries(customerTypeCounts) as [string, number][]
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  const objectionCounts = leadStrategyBoard.reduce<Record<string, number>>(
    (acc, item) => {
      acc[item.strategy.mainObjection] =
        (acc[item.strategy.mainObjection] || 0) + 1;
      return acc;
    },
    {}
  );
  const topObjections = (Object.entries(objectionCounts) as [string, number][])
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  const followUpQueue = followUpDue
    .map((lead) => ({
      lead,
      strategy: buildLeadStrategy(lead),
      priority: getFollowUpPriority(lead),
    }))
    .sort((a, b) => {
      const order: Record<FollowUpPriority, number> = {
        Overdue: 0,
        "Due Today": 1,
        "Due Tomorrow": 2,
        Scheduled: 3,
      };
      return (
        order[a.priority] - order[b.priority] ||
        b.strategy.leadScore - a.strategy.leadScore
      );
    });
  const appointmentBoard = {
    set: leads.filter((lead) => lead.status === "Appointment Set"),
    working: leads.filter((lead) => lead.status === "Working Deal"),
    sold: leads.filter((lead) => lead.status === "Sold"),
    lost: leads.filter((lead) => lead.status === "Lost"),
  };
  const tradeAnalysisBoard = pendingTrades
    .map((lead) => ({
      lead,
      analysis: buildTradeAnalysis(lead),
      strategy: buildLeadStrategy(lead),
    }))
    .filter(
      (
        item
      ): item is {
        lead: Lead;
        analysis: TradeAnalysis;
        strategy: LeadStrategy;
      } => Boolean(item.analysis)
    )
    .sort((a, b) => Math.abs(b.analysis.equity) - Math.abs(a.analysis.equity));
  const smartAutomationQueue = leadStrategyBoard
    .filter(
      (item) => item.lead.status !== "Sold" && item.lead.status !== "Lost"
    )
    .slice(0, 8);
  const priorityInventory = inventoryContentProfiles
    .slice()
    .sort(
      (a, b) =>
        b.profile.contentScore +
        b.profile.businessScore +
        b.profile.familyScore -
        (a.profile.contentScore +
          a.profile.businessScore +
          a.profile.familyScore)
    )
    .slice(0, 4);

  const leadsBySource = (
    Object.entries(
      leads.reduce<Record<string, number>>((acc, lead) => {
        const key = lead.source || "Unknown";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {})
    ) as [string, number][]
  ).sort((a, b) => b[1] - a[1]);
  const soldBySource = (
    Object.entries(
      leads
        .filter((lead) => lead.status === "Sold")
        .reduce<Record<string, number>>((acc, lead) => {
          const key = lead.source || "Unknown";
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {})
    ) as [string, number][]
  ).sort((a, b) => b[1] - a[1]);
  const postedByPlatform = (
    Object.entries(
      postingTasks
        .filter((task) => task.status === "Posted")
        .reduce<Record<string, number>>((acc, task) => {
          acc[task.platform] = (acc[task.platform] || 0) + 1;
          return acc;
        }, {})
    ) as [string, number][]
  ).sort((a, b) => b[1] - a[1]);
  const platformReadyCounts = (
    Object.entries(
      postingTasks
        .filter((task) => task.status === "Ready")
        .reduce<Record<string, number>>((acc, task) => {
          acc[task.platform] = (acc[task.platform] || 0) + 1;
          return acc;
        }, {})
    ) as [string, number][]
  ).sort((a, b) => b[1] - a[1]);
  const bestVehicleTypes = (
    Object.entries(
      inventoryContentProfiles.reduce<Record<string, number>>((acc, item) => {
        const key = item.profile.vehicleType;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {})
    ) as [string, number][]
  ).sort((a, b) => b[1] - a[1]);
  const contentRoiSignal = postingTasks.filter(
    (task) => task.status === "Posted"
  ).length
    ? Math.round(
        (hotLeads /
          Math.max(
            postingTasks.filter((task) => task.status === "Posted").length,
            1
          )) *
          100
      )
    : 0;
  const postedToday = postingTasks.filter(
    (task) =>
      task.status === "Posted" &&
      task.postedAt?.startsWith(new Date().toLocaleDateString())
  ).length;
  const readyToPostCount = postingTasks.filter(
    (task) => task.status === "Ready"
  ).length;
  const needsContentInventory = inventoryContentProfiles.filter(
    (item) =>
      !postingTasks.some(
        (task) =>
          task.vehicle === item.vehicle.title && task.status === "Posted"
      )
  );
  const reviewsRequestedToday = reviewRecords.filter((review) =>
    review.linkSentAt?.startsWith(new Date().toLocaleDateString())
  ).length;
  const reviewsPostedCount = reviewRecords.filter(
    (review) => review.status === "Posted"
  ).length;
  const reviewsWaitingCount = reviewRecords.filter(
    (review) =>
      review.status === "Requested" || review.status === "Needs Follow-Up"
  ).length;
  const bestReviewPlatform = REVIEW_PLATFORMS.map((platform) => ({
    platform,
    count: reviewRecords.filter(
      (review) => review.platform === platform && review.status === "Posted"
    ).length,
  })).sort((a, b) => b.count - a.count)[0];
  const referralChecksPending = referralRecords.filter(
    (referral) =>
      referral.checkStatus === "Pending" || referral.checkStatus === "Approved"
  ).length;
  const referralChecksPaid = referralRecords.filter(
    (referral) => referral.checkStatus === "Paid"
  ).length;
  const referralsSold = referralRecords.filter(
    (referral) => referral.checkStatus !== "Not Eligible"
  ).length;
  const totalReferralPayouts = referralRecords
    .filter((referral) => referral.checkStatus === "Paid")
    .reduce((sum, referral) => sum + cleanMoney(referral.amount), 0);

  const deliveryMonthKey = new Date()
    .toLocaleDateString()
    .split("/")
    .slice(0, 2)
    .join("/");
  const deliveriesThisMonth = deliveryRecords.filter((delivery) =>
    String(delivery.deliveryDate || "").startsWith(deliveryMonthKey)
  ).length;
  const deliveryReviewsMissing = deliveryRecords.filter(
    (delivery) => delivery.reviewRequested && !delivery.reviewPosted
  ).length;
  const deliveryPhotosUploaded = deliveryRecords.filter(
    (delivery) => delivery.photoUploaded
  ).length;
  const deliveryVideosUploaded = deliveryRecords.filter(
    (delivery) => delivery.videoUploaded
  ).length;
  const deliveryReferralEligible = deliveryRecords.filter(
    (delivery) => delivery.referralEligible || delivery.isReferral
  ).length;

  const arturoFocusToday = followUpQueue.some(
    (item) => item.priority === "Overdue"
  )
    ? "Start with overdue follow-ups before creating new content."
    : priorityLeads.length
    ? `Contact ${priorityLeads[0].lead.name || "your hottest lead"} first — ${
        priorityLeads[0].strategy.nextBestAction
      }.`
    : needsContentInventory.length
    ? `Create content for ${needsContentInventory[0].vehicle.title} — it needs a fresh post.`
    : readyToPostCount
    ? "Mark ready posts as posted and push them across TikTok, Facebook, IG, WhatsApp, Marketplace, and OfferUp."
    : "Add new leads or inventory, then build a posting checklist.";

  const buildReviewRequestMessage = (review: ReviewRecord = reviewDraft) => {
    const reviewLink = REVIEW_LINKS[review.platform];
    const platformLine = reviewLink
      ? `${review.platform}: ${reviewLink}`
      : "Autotrader: I will send the Autotrader review link separately.";
    return `Hi ${
      review.customerName || "there"
    }, thank you again for choosing Chicago Cars US${
      review.vehicle ? ` for your ${review.vehicle}` : ""
    }.\n\nWould you mind taking a minute to share your experience?\n\n${platformLine}\n\nI truly appreciate your support.\n\n-Arturo\nDriveWithArturo\n(872) 364-0886`;
  };

  const buildBusinessCardMessage = () =>
    `Hi, this is Arturo with Chicago Cars US.\n\nHere's my digital business card:\n${POPL_BUSINESS_CARD_URL}\n\nFeel free to reach out anytime with questions.\n\n-Arturo\nDriveWithArturo\n(872) 364-0886`;

  const buildReferralMessage = (referral: ReferralRecord = referralDraft) =>
    `Thank you for the referral${
      referral.referrerName ? `, ${referral.referrerName}` : ""
    }! Your $100 referral check is being processed. I appreciate you trusting me and sending people my way.\n\n-Arturo\nDriveWithArturo\n(872) 364-0886`;

  const openShareUrl = (
    channel: "sms" | "email" | "whatsapp",
    message: string,
    subject = "DriveWithArturo"
  ) => {
    const encodedMessage = encodeURIComponent(message);
    const encodedSubject = encodeURIComponent(subject);
    if (channel === "sms")
      window.location.href = `sms:?&body=${encodedMessage}`;
    if (channel === "email")
      window.location.href = `mailto:?subject=${encodedSubject}&body=${encodedMessage}`;
    if (channel === "whatsapp")
      window.open(`https://wa.me/?text=${encodedMessage}`, "_blank");
  };

  const buildDeliveryPackage = (delivery: DeliveryRecord = deliveryDraft) => {
    const name = delivery.customerName || "our customer";
    const vehicle = delivery.vehicle || "their new vehicle";
    return {
      tiktok: `Another happy customer drove home today! 🔥\n\nCongratulations to ${name} on their new ${vehicle}.\n\nThank you for trusting Chicago Cars US and DriveWithArturo.\n\nWho's next? 📲 872-364-0886\n\n#DriveWithArturo #ChicagoCarsUS #CarTok`,
      facebook: `Congratulations to ${name} on their new ${vehicle}!\n\nThank you for trusting me and Chicago Cars US with your vehicle purchase. Enjoy the new ride and welcome to the DriveWithArturo family!`,
      review: `Hi ${name}, thank you again for choosing Chicago Cars US for your ${vehicle}. Would you mind taking a minute to share your experience?\n\nGoogle: ${REVIEW_LINKS.Google}\nCars.com: ${REVIEW_LINKS["Cars.com"]}\nCarGurus: ${REVIEW_LINKS.CarGurus}\n\nI truly appreciate your support.\n\n-Arturo`,
      referral: `Know someone looking for a vehicle? Send them my way. When they buy, you receive a $100 referral check.\n\n-Arturo\nDriveWithArturo\n(872) 364-0886`,
      recap: `${name} took delivery of ${vehicle}${
        delivery.leadSource ? ` from ${delivery.leadSource}` : ""
      }. ${delivery.isBusinessOwner ? "Business owner angle. " : ""}${
        delivery.isFirstTimeBuyer ? "First-time buyer story. " : ""
      }${delivery.isReferral ? "Referral delivery. " : ""}`,
    };
  };

  const addDeliveryRecord = () => {
    if (!deliveryDraft.customerName && !deliveryDraft.vehicle)
      return alert("Add at least customer name or vehicle.");
    const record: DeliveryRecord = {
      ...deliveryDraft,
      id: Date.now(),
      deliveryDate:
        deliveryDraft.deliveryDate || new Date().toLocaleDateString(),
    };
    setDeliveryRecords((current) => [record, ...current]);

    if (record.reviewRequested) {
      setReviewRecords((current) => [
        {
          ...createEmptyReviewRecord(),
          id: Date.now() + 1,
          customerName: record.customerName,
          vehicle: record.vehicle,
          deliveryDate: record.deliveryDate,
          platform: "Google",
          status: "Requested",
          linkSentAt: new Date().toLocaleString(),
        },
        ...current,
      ]);
    }

    if (record.referralEligible || record.isReferral) {
      setReferralRecords((current) => [
        {
          ...createEmptyReferralRecord(),
          id: Date.now() + 2,
          referredCustomer: record.customerName,
          vehiclePurchased: record.vehicle,
          saleDate: record.deliveryDate,
          checkStatus: "Pending",
          amount: 100,
          notes: "Created from Delivery Center",
        },
        ...current,
      ]);
    }

    setDeliveryDraft(createEmptyDeliveryRecord());
  };

  const updateDeliveryRecord = (id: number, patch: Partial<DeliveryRecord>) => {
    setDeliveryRecords((current) =>
      current.map((delivery) =>
        delivery.id === id ? { ...delivery, ...patch } : delivery
      )
    );
  };

  const addReviewRecord = () => {
    if (!reviewDraft.customerName && !reviewDraft.vehicle)
      return alert("Add at least customer name or vehicle.");
    const now = new Date().toLocaleString();
    const record: ReviewRecord = {
      ...reviewDraft,
      id: Date.now(),
      linkSentAt:
        reviewDraft.status === "Requested" && !reviewDraft.linkSentAt
          ? now
          : reviewDraft.linkSentAt,
      postedAt:
        reviewDraft.status === "Posted" && !reviewDraft.postedAt
          ? now
          : reviewDraft.postedAt,
    };
    setReviewRecords((current) => [record, ...current]);
    setReviewDraft({
      id: 0,
      customerName: "",
      phone: "",
      email: "",
      vehicle: "",
      deliveryDate: "",
      platform: "Google",
      status: "Not Requested",
      linkSentAt: "",
      postedAt: "",
      screenshotSaved: false,
      referralConnected: false,
      notes: "",
    });
  };

  const updateReviewStatus = (id: number, status: ReviewStatus) => {
    const now = new Date().toLocaleString();
    setReviewRecords((current) =>
      current.map((review) =>
        review.id === id
          ? {
              ...review,
              status,
              linkSentAt:
                status === "Requested" && !review.linkSentAt
                  ? now
                  : review.linkSentAt,
              postedAt:
                status === "Posted" && !review.postedAt ? now : review.postedAt,
            }
          : review
      )
    );
  };

  const addReferralRecord = () => {
    if (!referralDraft.referrerName && !referralDraft.referredCustomer)
      return alert("Add referrer or referred customer.");
    const record: ReferralRecord = {
      ...referralDraft,
      id: Date.now(),
      amount: cleanMoney(referralDraft.amount || 100) || 100,
    };
    setReferralRecords((current) => [record, ...current]);
    setReferralDraft(createEmptyReferralRecord());
  };

  const updateReferralStatus = (
    id: number,
    checkStatus: ReferralCheckStatus
  ) => {
    const now = new Date().toLocaleDateString();
    setReferralRecords((current) =>
      current.map((referral) =>
        referral.id === id
          ? {
              ...referral,
              checkStatus,
              paidDate:
                checkStatus === "Paid" && !referral.paidDate
                  ? now
                  : referral.paidDate,
            }
          : referral
      )
    );
  };

  const addLead = () => {
    if (!leadForm.name && !leadForm.phone) {
      alert("Add at least a customer name or phone number.");
      return;
    }

    const newLead: Lead = {
      ...leadForm,
      id: Date.now(),
      createdAt: new Date().toLocaleString(),
    };
    setLeads((current) => [newLead, ...current]);
    setLeadForm(createEmptyLeadForm());
  };

  const updateLeadStatus = (id: number, status: LeadStatus) => {
    setLeads((current) =>
      current.map((lead) => (lead.id === id ? { ...lead, status } : lead))
    );
  };

  const createLeadFromDeal = () => {
    setLeadForm((current) => ({
      ...current,
      name: deal.customerName,
      phone: deal.phone,
      vehicle: deal.vehicleDescription,
      source: "OTD Calculator",
      temperature: result.balanceAfterDown > 0 ? "HOT" : "WARM",
      monthlyBudget: result.monthlyPayment
        ? `${money(result.monthlyPayment)}/mo estimate`
        : current.monthlyBudget,
      downPaymentAvailable: String(
        deal.downPayment || current.downPaymentAvailable || ""
      ),
      notes: `OTD: ${money(result.totalBeforeDown)} | Balance: ${money(
        result.balanceAfterDown
      )} | Est. Payment: ${money(result.monthlyPayment)}/mo`,
    }));
    setActiveTab("crm");
  };

  const saveDeal = () => {
    const newDeal: SavedDeal = {
      id: Date.now(),
      date: new Date().toLocaleString(),
      salesperson: "Arturo Garza",
      deal,
      result,
    };
    setSavedDeals((current) => [newDeal, ...current].slice(0, 50));
  };

  const buildShareText = () => {
    const customer = deal.customerName ? `${deal.customerName}, ` : "";
    return `${customer}here is your estimated out-the-door breakdown for ${
      deal.vehicleDescription || "the vehicle"
    }:

OTD Total: ${money(result.totalBeforeDown)}
Balance after down payment: ${money(result.balanceAfterDown)}
Estimated payment: ${money(result.monthlyPayment)}/mo
Sales tax: ${money(result.totalTax)}
Trade value: ${money(result.trade)}
Trade payoff: ${money(result.payoff)}
Trade allowance: ${money(result.tradeAllowance)}

Text Arturo: (872) 364-0886
Chicago Cars US · Hablamos Español`;
  };

  const shareDeal = async () => {
    const text = buildShareText();
    if (navigator.share) {
      try {
        await navigator.share({ title: "OTD Pricing Report", text });
        return;
      } catch {
        // Fall back to clipboard.
      }
    }
    await navigator.clipboard?.writeText(text);
    alert("Pricing summary copied. Paste it into text messages.");
  };

  const addInventoryVehicle = () => {
    if (!inventoryForm.title || !inventoryForm.price) {
      alert("Add vehicle title and price.");
      return;
    }
    const vehicle = {
      ...inventoryForm,
      id: Date.now(),
      price: cleanMoney(inventoryForm.price),
      mileage: cleanMoney(inventoryForm.mileage),
    };
    setInventory((current) => [vehicle, ...current]);
    setInventoryForm({
      id: 0,
      title: "",
      price: "",
      mileage: "",
      stockNumber: "",
      imageUrl: "",
      vehicleUrl: "",
      published: true,
      featured: true,
    });
  };

  const fillCalculatorFromVehicle = (vehicle: InventoryVehicle) => {
    setDeal((current) => ({
      ...current,
      vehicleDescription: vehicle.title,
      stockNumber: vehicle.stockNumber,
      listPrice: String(cleanMoney(vehicle.price)),
    }));
    setActiveTab("otd");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStudioPhotoUpload = (files: FileList | null) => {
    if (!files) return;
    const urls = Array.from(files)
      .filter((file) => file.type.startsWith("image/"))
      .map((file) => URL.createObjectURL(file));
    setStudioPhotos((current) => [...current, ...urls]);
    setStudioFrame(0);
  };

  const saveOverlayTemplate = () => {
    if (!overlayDraft.text.trim()) return alert("Type overlay text first.");
    const overlay = {
      ...overlayDraft,
      id: Date.now(),
      text: overlayDraft.text.trim(),
    };
    setOverlayTemplates((current) => [overlay, ...current]);
    setActiveOverlay(overlay);
    setOverlayDraft({
      id: 0,
      text: "",
      position: "center",
      color: "#ffffff",
      size: 30,
    });
  };

  const detectVehicleType = (title: string): VehicleContentProfile => {
    const lower = title.toLowerCase();
    const isVan = /(metris|transit|express|nv|promaster|sprinter|van)/i.test(
      lower
    );
    const isSuv =
      /(explorer|tahoe|suburban|yukon|highlander|pilot|mdx|xc90|q7|x5|suv|srx)/i.test(
        lower
      );
    const isLuxury =
      /(mercedes|benz|bmw|audi|lexus|cadillac|porsche|infiniti|acura|land rover|range rover)/i.test(
        lower
      );
    const isTruck =
      /(silverado|f-150|f150|ram|sierra|tundra|tacoma|truck)/i.test(lower);
    const isSport =
      /(mustang|camaro|corvette|s2000|wrx|m3|m4|s5|supra|sport|st)/i.test(
        lower
      );

    if (isVan) {
      return {
        vehicleType: "Business / Work Vehicle",
        contentScore: 96,
        businessScore: 98,
        familyScore: 62,
        luxuryScore: isLuxury ? 55 : 25,
        firstTimeBuyerScore: 70,
        reason:
          "Strong entrepreneur story, work-use value, mobile business potential, and easy before/after content angle.",
      };
    }

    if (isTruck) {
      return {
        vehicleType: "Truck / Work + Lifestyle",
        contentScore: 90,
        businessScore: 88,
        familyScore: 72,
        luxuryScore: 40,
        firstTimeBuyerScore: 76,
        reason:
          "Strong utility angle for work, family hauling, towing, and lifestyle content.",
      };
    }

    if (isSuv) {
      return {
        vehicleType: "Family SUV",
        contentScore: 88,
        businessScore: 45,
        familyScore: 96,
        luxuryScore: isLuxury ? 86 : 52,
        firstTimeBuyerScore: 82,
        reason:
          "High family appeal: space, comfort, safety, road trips, and payment-focused buyers.",
      };
    }

    if (isSport || isLuxury) {
      return {
        vehicleType: isSport ? "Performance / Enthusiast" : "Luxury",
        contentScore: 94,
        businessScore: 35,
        familyScore: 50,
        luxuryScore: isLuxury ? 96 : 70,
        firstTimeBuyerScore: 68,
        reason:
          "High scroll-stopping potential with lifestyle, sound, stance, luxury, and aspiration angles.",
      };
    }

    return {
      vehicleType: "Daily Driver",
      contentScore: 82,
      businessScore: 45,
      familyScore: 78,
      luxuryScore: 35,
      firstTimeBuyerScore: 92,
      reason:
        "Strong practical buyer angle: affordability, transportation, first-time buyer, and credit rebuild content.",
    };
  };

  const buildCutTiming = (bpm: number) => {
    const secondsPerBeat = 60 / Math.max(bpm, 1);
    return `Cut every ${secondsPerBeat.toFixed(2)} sec on beat, or every ${(
      secondsPerBeat * 2
    ).toFixed(2)} sec for smoother luxury pacing.`;
  };

  const generateMarketingPackage = (vehicleOverride?: InventoryVehicle) => {
    const vehicle =
      vehicleOverride?.title ||
      deal.vehicleDescription ||
      inventory[0]?.title ||
      "this vehicle";
    const price = money(
      vehicleOverride?.price || deal.listPrice || inventory[0]?.price || 0
    );
    const profile = detectVehicleType(vehicle);
    const bpm = Number(audioBpm) || 120;
    const businessAngle = profile.businessScore >= 85;
    const familyAngle = profile.familyScore >= 85;
    const luxuryAngle = profile.luxuryScore >= 85;

    const hooks = businessAngle
      ? [
          `POV: You stopped waiting for opportunity and bought the vehicle to build it.`,
          `This ${vehicle} is not just transportation — it is a business move.`,
          `Mobile detailers, contractors, delivery drivers — this one makes sense.`,
          `The right vehicle can change how your business shows up.`,
          `If you are tired of paying for rides, it might be time to own the keys.`,
        ]
      : familyAngle
      ? [
          `POV: You found the family vehicle that actually makes sense.`,
          `Space, comfort, and a payment that can make sense — this ${vehicle} is ready.`,
          `School runs, work, road trips, family time — this one checks boxes.`,
          `Stop scrolling if your family needs more room.`,
          `This could be the upgrade your family has been waiting for.`,
        ]
      : luxuryAngle
      ? [
          `Luxury does not have to wait until someday.`,
          `This ${vehicle} has presence before you even start it.`,
          `POV: You pulled up different without saying a word.`,
          `Premium look, strong presence, and DriveWithArturo energy.`,
          `If the vehicle checks all the boxes and the numbers make sense, let's make it happen.`,
        ]
      : [
          `Tired of wasting money on rides? Get your own keys today.`,
          `First-time buyer? Credit rebuilding? Let's see what's possible.`,
          `This ${vehicle} is ready for someone who is ready to move.`,
          `Stop scrolling — reliable transportation matters.`,
          `I trust our finance manager more than the weather forecast in Chicago.`,
        ];

    const storyboard: StoryboardScene[] = [
      {
        scene: "Scene 1",
        time: "0-3 sec",
        shot: "Best exterior hero shot",
        text: hooks[0],
      },
      {
        scene: "Scene 2",
        time: "3-7 sec",
        shot: businessAngle
          ? "Cargo/workspace or side profile"
          : familyAngle
          ? "Interior seats and space"
          : "Front 3/4 angle",
        text: profile.reason,
      },
      {
        scene: "Scene 3",
        time: "7-11 sec",
        shot: "Interior, wheels, badge, or feature close-up",
        text: `${vehicle} available now at Chicago Cars US.`,
      },
      {
        scene: "Scene 4",
        time: "11-15 sec",
        shot: "CTA shot with logo/contact overlay",
        text: "Text Arturo at (872) 364-0886 — Hablamos Español.",
      },
    ];

    const captions = [
      `${vehicle} available now 🔥 ${price}. Financing options available. Trade-ins welcome. Text Arturo at (872) 364-0886. #DriveWithArturo #ChicagoCars #CarTok`,
      `If the vehicle checks all the boxes and the numbers make sense, let's make it happen. ${vehicle} is available now at Chicago Cars US.`,
      `English/Español — ask for Arturo. Credit rebuilding, first-time buyers, trade-ins, and ITIN customers are welcome to explore options. Final approval depends on lender review.`,
    ];

    const voiceover = businessAngle
      ? `This is the ${vehicle}. For the right person, this is not just a vehicle — it is a business tool. Mobile detailing, delivery, contracting, service work, or growing your hustle, this can help you show up professionally. Text Arturo today and let's see what makes sense.`
      : familyAngle
      ? `This is the ${vehicle}. If your family needs more room, more comfort, and a vehicle that feels ready for real life, this one deserves a look. Come drive it first, and if the numbers make sense, let's make it happen.`
      : `This is the ${vehicle}, available now at Chicago Cars US. Whether you are a first-time buyer, rebuilding credit, or just need the right vehicle, let's see what's possible. I cannot guarantee approvals, but I trust our finance manager more than the weather forecast in Chicago.`;

    const packageData: MarketingPackage = {
      vehicle,
      profile,
      bpm,
      cutTiming: buildCutTiming(bpm),
      bestShotPlan: [
        "Hero front 3/4",
        "Side profile",
        "Interior cockpit",
        "Mileage/dashboard",
        "Best feature close-up",
        "Final CTA shot",
      ],
      hooks,
      captions,
      whatsappPost: `🚗 ${vehicle}\n${price}\nFinancing options available · Trade-ins welcome · Hablamos Español\nText Arturo: (872) 364-0886\n\nFinal numbers, availability, taxes, fees, and approvals must be confirmed by the dealership.`,
      marketplaceListing: `${vehicle} for sale at Chicago Cars US in Summit, IL. ${price}. Financing options available, trade-ins welcome, and bilingual help available with Arturo. Message for availability and an out-the-door estimate. Price plus taxes, registration, doc fee, dealer fees, and any applicable products.`,
      offerUpListing: `${vehicle} available now 🔥 ${price}. Clean presentation, financing options, trade-ins welcome, and quick response from Arturo. Message today to confirm availability and schedule a test drive.`,
      voiceover,
      storyboard,
      deliveryFacebookPost: `Congratulations ${
        clientNameForFrame || "to our customer"
      } on the ${
        deliveryVehicleForFrame || vehicle
      }! Thank you for trusting Arturo and Chicago Cars US. Wishing you many safe miles ahead. 🎉`,
      deliveryGoogleReviewRequest: `Hi ${
        clientNameForFrame || "there"
      }, thank you again for choosing Chicago Cars US. If Arturo made the process easy, a quick Google review would mean a lot.`,
      deliveryInstagramStory: `CONGRATULATIONS ${
        clientNameForFrame || ""
      } 🎉\n${
        deliveryVehicleForFrame || vehicle
      }\nDriveWithArturo · Chicago Cars US`,
      deliveryWhatsAppStatus: `🎉 Congratulations ${
        clientNameForFrame || "to our customer"
      }! Another happy delivery with DriveWithArturo at Chicago Cars US.`,
      deliveryTikTokCover: `${
        clientNameForFrame || "CUSTOMER"
      } GOT THE KEYS 🔑`,
      thumbnailText: businessAngle
        ? "WORK READY"
        : familyAngle
        ? "FAMILY READY"
        : luxuryAngle
        ? "LUXURY READY"
        : "AVAILABLE NOW",
      commercialAudioNote:
        "For promotional dealership content on TikTok, choose audio from TikTok's Commercial Music Library, then enter the BPM here so the cut timing matches the track.",
    };

    setMarketingPackage(packageData);
    setGeneratedHooks(hooks);
    setActiveTab("studio");
  };

  const generateHooks = () => {
    const vehicle =
      deal.vehicleDescription || inventory[0]?.title || "this vehicle";
    const price = money(deal.listPrice || inventory[0]?.price || 0);
    const profile = detectVehicleType(vehicle);
    const hooks = [
      profile.businessScore >= 85
        ? `What would your business look like with this ${vehicle}?`
        : profile.luxuryScore >= 85
        ? `This ${vehicle} has the kind of presence people notice before you even park.`
        : profile.familyScore >= 85
        ? `The family vehicle every parent wishes they found sooner — ${vehicle}.`
        : `Your first car does not have to be boring — ${vehicle}.`,
      `Stop scrolling — ${vehicle} just hit the lot and it’s ready today.`,
      profile.businessScore >= 85
        ? `This ${vehicle} is not just transportation — it could be the start of somebody's business move.`
        : profile.familyScore >= 85
        ? `Your family needed more room, and ${vehicle} just pulled up at the right time.`
        : `Bad credit? First-time buyer? ${vehicle} may still be possible with the right structure.`,
      `Tired of wasting money on rides? Get your own keys with DriveWithArturo.`,
      `${vehicle} for ${price}. Text Arturo before someone else takes it home.`,
      `I can't guarantee approvals, but I trust our finance manager more than the weather forecast in Chicago — let's see what's possible.`,
    ];
    setGeneratedHooks(hooks);
    setMarketingPackage(
      (current) =>
        current || {
          vehicle,
          profile,
          bpm: Number(audioBpm) || 120,
          cutTiming: buildCutTiming(Number(audioBpm) || 120),
          bestShotPlan: [
            "Hero front 3/4",
            "Side profile",
            "Interior cockpit",
            "Mileage/dashboard",
            "Best feature close-up",
            "Final CTA shot",
          ],
          hooks,
          captions: [
            `${vehicle} available now 🔥 ${price}. Text Arturo at (872) 364-0886. #DriveWithArturo #ChicagoCars #CarTok`,
          ],
          whatsappPost: `🚗 ${vehicle}
${price}
Financing options available · Trade-ins welcome · Hablamos Español
Text Arturo: (872) 364-0886`,
          marketplaceListing: `${vehicle} for sale at Chicago Cars US in Summit, IL. Message Arturo for availability and OTD estimate.`,
          offerUpListing: `${vehicle} available now 🔥 ${price}. Message today to confirm availability and schedule a test drive.`,
          voiceover: `${vehicle} available now at Chicago Cars US. If the vehicle checks all the boxes and the numbers make sense, let's make it happen.`,
          storyboard: [
            {
              scene: "Scene 1",
              time: "0-3 sec",
              shot: "Best exterior hero shot",
              text: hooks[0],
            },
            {
              scene: "Scene 2",
              time: "3-7 sec",
              shot: "Interior or feature shot",
              text: profile.reason,
            },
            {
              scene: "Scene 3",
              time: "7-11 sec",
              shot: "Side profile / badge / wheels",
              text: `${vehicle} available now.`,
            },
            {
              scene: "Scene 4",
              time: "11-15 sec",
              shot: "CTA with watermark",
              text: "Text Arturo at (872) 364-0886.",
            },
          ],
          commercialAudioNote:
            "For TikTok promo content, use TikTok Commercial Music Library audio.",
        }
    );
  };

  const generateReelPlan = () => {
    const vehicle =
      deal.vehicleDescription ||
      inventory[0]?.title ||
      deliveryVehicleForFrame ||
      "this vehicle";
    const profile = detectVehicleType(vehicle);
    const bpm = Number(audioBpm) || 120;
    const secondsPerBeat = 60 / Math.max(bpm, 1);
    const cutTiming = `Cut every ${secondsPerBeat.toFixed(
      2
    )} sec on beat, every ${(secondsPerBeat * 2).toFixed(
      2
    )} sec for smooth pacing, or every ${(secondsPerBeat / 2).toFixed(
      2
    )} sec for a fast hook sequence.`;
    const duration = Math.max(12, Math.min(45, Number(reelDuration) || 18));
    const businessAngle = profile.businessScore >= 85;
    const familyAngle = profile.familyScore >= 85;
    const luxuryAngle = profile.luxuryScore >= 85;

    const hook = businessAngle
      ? `This ${vehicle} is not just transportation — it is a business move.`
      : familyAngle
      ? `This is the family upgrade that actually makes sense.`
      : luxuryAngle
      ? `Luxury presence without waiting for someday.`
      : `Stop scrolling — this ${vehicle} is ready for someone serious.`;

    const sceneOrder: ReelScene[] = [
      {
        scene: "Hook",
        time: "0-3 sec",
        shot: "Best exterior hero shot",
        editDirection:
          "Hard cut on first beat. Add zoom-in or quick push effect.",
        onScreenText: hook,
      },
      {
        scene: "Proof",
        time: "3-7 sec",
        shot: businessAngle
          ? "Cargo/work area or side profile"
          : familyAngle
          ? "Interior seating/cargo space"
          : "Front 3/4 + wheel/detail shot",
        editDirection: "Cut every 1-2 beats. Keep motion moving forward.",
        onScreenText: profile.reason,
      },
      {
        scene: "Features",
        time: "7-12 sec",
        shot: "Interior, dashboard, mileage, wheels, badge, or best feature close-up",
        editDirection:
          "Use quick cuts, each image no longer than 1.25 seconds.",
        onScreenText: `${vehicle} available now at Chicago Cars US.`,
      },
      {
        scene: "Trust",
        time: "12-15 sec",
        shot: "Dealership, delivery photo, customer smile, or clean exterior angle",
        editDirection: "Slow down slightly. Let the trust line breathe.",
        onScreenText:
          "Financing options · Trade-ins welcome · Hablamos Español",
      },
      {
        scene: "CTA",
        time: `${Math.max(15, duration - 3)}-${duration} sec`,
        shot: "Final exterior shot with DriveWithArturo watermark",
        editDirection: "Hold final frame long enough to read the phone number.",
        onScreenText: "Text Arturo: (872) 364-0886",
      },
    ];

    const voiceover = businessAngle
      ? `This is the ${vehicle}. For the right person, this is not just a vehicle — it is a business tool. If you are ready to show up professionally and make moves, text Arturo today.`
      : familyAngle
      ? `This is the ${vehicle}. Space, comfort, and real-life practicality for the family. Come see it, drive it, and if the numbers make sense, let's make it happen.`
      : `This is the ${vehicle}, available now at Chicago Cars US. Whether you are rebuilding credit, buying for the first time, or just looking for the right vehicle, let's see what's possible.`;

    const plan: ReelPlan = {
      title: `${vehicle} Reel Plan`,
      platform: reelPlatform,
      duration,
      bpm,
      cutTiming,
      hook,
      voiceover,
      thumbnailText: businessAngle
        ? "BUSINESS MOVE"
        : familyAngle
        ? "FAMILY READY"
        : luxuryAngle
        ? "LUXURY LOOK"
        : "AVAILABLE NOW",
      caption: `${vehicle} available now at Chicago Cars US. Financing options available, trade-ins welcome, and Hablamos Español. Text Arturo at (872) 364-0886. Final numbers and approvals must be confirmed by the dealership.`,
      cta: "Text Arturo at (872) 364-0886 to confirm availability and schedule a test drive.",
      sceneOrder,
      exportChecklist: [
        "Use 9:16 for TikTok, Reels, Facebook Reels, and YouTube Shorts.",
        "Use audio from TikTok Commercial Music Library for promotional dealership content.",
        "Keep the first hook under 3 seconds.",
        "Make sure DriveWithArturo watermark and phone number are visible on the final frame.",
        "Confirm price, availability, taxes, fees, and approval language before posting.",
      ],
    };

    setReelPlan(plan);
    setGeneratedHooks([
      plan.hook,
      plan.thumbnailText,
      plan.caption,
      plan.voiceover,
    ]);
    setMarketingPackage((current) => current || null);
  };

  const getPostingCaptionForPlatform = (
    platform: PostingPlatform,
    packageData: MarketingPackage | null,
    plan: ReelPlan | null
  ) => {
    const vehicle =
      packageData?.vehicle ||
      plan?.title.replace(" Reel Plan", "") ||
      deal.vehicleDescription ||
      inventory[0]?.title ||
      "this vehicle";
    const baseCaption =
      packageData?.captions?.[0] ||
      plan?.caption ||
      `${vehicle} available now at Chicago Cars US. Text Arturo at (872) 364-0886.`;

    if (platform === "TikTok")
      return `${baseCaption} #DriveWithArturo #ChicagoCars #CarTok #UsedCars`;
    if (platform === "Instagram")
      return `${baseCaption}

Reels ready. DM Arturo to confirm availability.`;
    if (platform === "Facebook")
      return `${baseCaption}

Price plus taxes, registration, doc fee, dealer fees, and applicable products. Ask for an exact out-the-door quote.`;
    if (platform === "YouTube Shorts")
      return `${plan?.hook || packageData?.hooks?.[0] || vehicle}

${plan?.cta || "Text Arturo at (872) 364-0886."}`;
    if (platform === "WhatsApp")
      return (
        packageData?.whatsappPost ||
        `🚗 ${vehicle}
Available now · Financing options available · Hablamos Español
Text Arturo: (872) 364-0886`
      );
    if (platform === "Marketplace")
      return (
        packageData?.marketplaceListing ||
        `${vehicle} for sale at Chicago Cars US in Summit, IL. Message Arturo for availability and an OTD estimate.`
      );
    if (platform === "OfferUp")
      return (
        packageData?.offerUpListing ||
        `${vehicle} available now. Message Arturo to confirm availability and schedule a test drive.`
      );
    return `${vehicle} available now at Chicago Cars US. Text Arturo at (872) 364-0886 to confirm availability.`;
  };

  const buildPostingChecklist = (platform: PostingPlatform) => {
    const common = [
      "Confirm vehicle availability before posting",
      "Confirm price and required fee disclaimer",
      "Use DriveWithArturo watermark or branded final frame",
    ];

    if (platform === "TikTok")
      return [
        ...common,
        "Use TikTok Commercial Music Library audio",
        "Hook must be clear in first 3 seconds",
        "Add text CTA: Text Arturo (872) 364-0886",
      ];
    if (platform === "Instagram")
      return [
        ...common,
        "Use 9:16 Reel format",
        "Add location tag for Summit / Chicago area",
        "Post to Story after Reel",
      ];
    if (platform === "Facebook")
      return [
        ...common,
        "Post to page and relevant groups",
        "Include out-the-door disclaimer",
        "Reply fast to comments/messages",
      ];
    if (platform === "YouTube Shorts")
      return [
        ...common,
        "Use strong thumbnail text",
        "Keep title SEO friendly",
        "Pin phone/contact comment",
      ];
    if (platform === "WhatsApp")
      return [
        ...common,
        "Keep caption short",
        "Send to Status and hot buyers",
        "Use bilingual if needed",
      ];
    if (platform === "Marketplace")
      return [
        ...common,
        "Use SEO title",
        "Include stock number if available",
        "Message for exact OTD price disclaimer",
      ];
    if (platform === "OfferUp")
      return [
        ...common,
        "Use SEO title",
        "Keep description short and persuasive",
        "Ask buyer to message for availability",
      ];
    return [
      ...common,
      "Use dealership-safe wording",
      "Avoid approval promises",
      "Direct to secure application when needed",
    ];
  };

  const generatePostingTasks = () => {
    const vehicle =
      marketingPackage?.vehicle ||
      reelPlan?.title.replace(" Reel Plan", "") ||
      deal.vehicleDescription ||
      inventory[0]?.title ||
      "Current Vehicle";
    const stockNumber =
      deal.stockNumber ||
      inventory.find((item) => item.title === vehicle)?.stockNumber ||
      "";
    const createdAt = new Date().toLocaleString();
    const tasks = POSTING_PLATFORMS.map((platform) => ({
      id: Date.now() + Math.floor(Math.random() * 100000) + platform.length,
      vehicle,
      stockNumber,
      platform,
      status: "Ready" as PostingStatus,
      caption: getPostingCaptionForPlatform(
        platform,
        marketingPackage,
        reelPlan
      ),
      checklist: buildPostingChecklist(platform),
      createdAt,
    }));
    setPostingTasks((current) => [...tasks, ...current].slice(0, 150));
  };

  const updatePostingTaskStatus = (id: number, status: PostingStatus) => {
    setPostingTasks((current) =>
      current.map((task) =>
        task.id === id
          ? {
              ...task,
              status,
              postedAt:
                status === "Posted"
                  ? new Date().toLocaleString()
                  : task.postedAt,
            }
          : task
      )
    );
  };

  const generateDeliveryContentPackage = () => {
    const vehicle =
      deliveryVehicleForFrame ||
      deal.vehicleDescription ||
      inventory[0]?.title ||
      "their new vehicle";
    const name = clientNameForFrame || "our customer";
    const profile = detectVehicleType(vehicle);
    const packageData: MarketingPackage = {
      vehicle,
      profile,
      bpm: Number(audioBpm) || 120,
      cutTiming: buildCutTiming(Number(audioBpm) || 120),
      bestShotPlan: [
        "Customer holding keys",
        "Full vehicle with customer",
        "Handshake / thumbs up",
        "Close-up of smiles",
        "Final DriveWithArturo CTA",
      ],
      hooks: [
        `Another set of keys delivered — congratulations ${name}!`,
        `${name} came in for the right vehicle and left with the keys 🔑`,
        `This is what DriveWithArturo is about — real people, real deliveries.`,
      ],
      captions: [
        `Congratulations ${name} on the ${vehicle}! Thank you for trusting Arturo and Chicago Cars US. 🎉 #DriveWithArturo #ChicagoCars`,
        `Another happy customer delivery at Chicago Cars US. ${name}, enjoy your ${vehicle}!`,
      ],
      whatsappPost: `🎉 Congratulations ${name}!\n${vehicle}\nThank you for trusting DriveWithArturo at Chicago Cars US.`,
      marketplaceListing: `${vehicle} customer delivery highlight. Want help finding yours? Text Arturo at (872) 364-0886.`,
      offerUpListing: `${vehicle} delivery highlight. Message Arturo to find your next vehicle.`,
      voiceover: `Congratulations ${name} on the ${vehicle}. Thank you for trusting Arturo and Chicago Cars US. If you are next, text Arturo and let's see what's possible.`,
      storyboard: [
        {
          scene: "Scene 1",
          time: "0-3 sec",
          shot: "Customer and vehicle hero shot",
          text: `Congratulations ${name}!`,
        },
        {
          scene: "Scene 2",
          time: "3-7 sec",
          shot: "Keys / handshake / smile",
          text: `${vehicle} delivered at Chicago Cars US.`,
        },
        {
          scene: "Scene 3",
          time: "7-11 sec",
          shot: "Full vehicle shot",
          text: "Ready for your next vehicle?",
        },
        {
          scene: "Scene 4",
          time: "11-15 sec",
          shot: "DriveWithArturo branded frame",
          text: "Text Arturo: (872) 364-0886.",
        },
      ],
      commercialAudioNote:
        "Use TikTok Commercial Music Library audio for promotional dealership delivery posts.",
      deliveryFacebookPost: `🎉 Congratulations ${name} on the ${vehicle}! Thank you for trusting Arturo and Chicago Cars US. Wishing you many safe miles ahead!`,
      deliveryGoogleReviewRequest: `Hi ${name}, thank you again for choosing Chicago Cars US. If Arturo made the process easy, a quick Google review would mean a lot.`,
      deliveryInstagramStory: `CONGRATULATIONS ${name.toUpperCase()} 🎉\n${vehicle}\nDriveWithArturo · Chicago Cars US`,
      deliveryWhatsAppStatus: `🎉 Congratulations ${name}! Another happy delivery with DriveWithArturo at Chicago Cars US.`,
      deliveryTikTokCover: `${name.toUpperCase()} GOT THE KEYS 🔑`,
      thumbnailText: "CUSTOMER DELIVERY",
    };
    setDeliveryContentPackage(packageData);
    setMarketingPackage(packageData);
    setGeneratedHooks(packageData.hooks);
    setStudioFrameMode("delivery");
  };

  const generateFollowUp = (lead?: Lead) => {
    const selectedLead = lead || leads[0];
    const name = selectedLead?.name || deal.customerName || "there";
    const vehicle =
      selectedLead?.vehicle || deal.vehicleDescription || "the vehicle";
    const text = `Hey ${name}, this is Arturo with Chicago Cars US. Just following up on ${vehicle}. I can send you the full out-the-door price, financing options, and trade-in estimate today. What time works best for you to come in?`;
    setGeneratedFollowUp(text);
  };

  const getStudioBackground = () => {
    if (studioStyle === "clean")
      return "linear-gradient(135deg,#ffffff,#e5e7eb)";
    if (studioStyle === "night")
      return "linear-gradient(135deg,#020617,#111827 55%,#312e81)";
    if (studioStyle === "luxury")
      return "radial-gradient(circle at center,rgba(212,175,55,0.25),rgba(0,0,0,0.96) 65%)";
    if (studioStyle === "latino")
      return "linear-gradient(135deg,#064e3b,#111827 55%,#991b1b)";
    return "radial-gradient(circle at center,rgba(239,68,68,0.38),rgba(0,0,0,0.96) 68%)";
  };

  const exportStudioImage = async () => {
    const canvas = studioCanvasRef.current;
    if (!canvas || studioPhotos.length === 0) {
      alert("Upload at least one photo first.");
      return;
    }
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `drivewitharturo-studio-${Date.now()}.png`;
    a.click();
  };

  const exportStudioVideo = async () => {
    if (studioPhotos.length < 2) {
      alert("Upload at least 2 photos first.");
      return;
    }
    setExportingVideo(true);
    setExportedVideoUrl("");
    try {
      const width =
        videoFormat === "9:16" ? 720 : videoFormat === "1:1" ? 1080 : 1280;
      const height =
        videoFormat === "9:16" ? 1280 : videoFormat === "1:1" ? 1080 : 720;
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not supported.");
      const loadedImages = await Promise.all(
        studioPhotos.map(
          (src) =>
            new Promise<HTMLImageElement>((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = "anonymous";
              img.onload = () => resolve(img);
              img.onerror = reject;
              img.src = src;
            })
        )
      );
      const stream = canvas.captureStream(30);
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : "video/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks: BlobPart[] = [];
      recorder.ondataavailable = (event) =>
        event.data.size && chunks.push(event.data);
      const done = new Promise<Blob>((resolve) => {
        recorder.onstop = () =>
          resolve(new Blob(chunks, { type: "video/webm" }));
      });
      recorder.start();
      for (let frame = 0; frame < 180; frame += 1) {
        const img =
          loadedImages[
            Math.floor((frame / 180) * loadedImages.length) %
              loadedImages.length
          ];
        ctx.fillStyle = studioStyle === "clean" ? "#f8fafc" : "#020202";
        ctx.fillRect(0, 0, width, height);
        const ratio = Math.min(width / img.width, (height * 0.72) / img.height);
        const drawWidth = img.width * ratio;
        const drawHeight = img.height * ratio;
        ctx.drawImage(
          img,
          (width - drawWidth) / 2,
          height * 0.16,
          drawWidth,
          drawHeight
        );
        ctx.fillStyle = activeOverlay.color;
        ctx.font = `900 ${Math.round(
          activeOverlay.size * (width / 360)
        )}px Arial`;
        ctx.textAlign = "center";
        const y =
          activeOverlay.position === "top"
            ? height * 0.12
            : activeOverlay.position === "bottom"
            ? height * 0.84
            : height * 0.52;
        ctx.lineWidth = 10;
        ctx.strokeStyle = "rgba(0,0,0,0.75)";
        ctx.strokeText(activeOverlay.text, width / 2, y);
        ctx.fillText(activeOverlay.text, width / 2, y);
        if (studioFrameMode === "delivery") {
          drawDeliveryFrameOverlay(ctx, width, height);
        }
        drawDriveWithArturoWatermark(ctx, width, height);
        await new Promise((resolve) => window.setTimeout(resolve, 1000 / 30));
      }
      recorder.stop();
      const blob = await done;
      setExportedVideoUrl(URL.createObjectURL(blob));
    } catch (error) {
      alert(
        `Export failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setExportingVideo(false);
    }
  };

  const shareExportedVideo = async () => {
    if (!exportedVideoUrl) return alert("Export a video first.");
    const response = await fetch(exportedVideoUrl);
    const blob = await response.blob();
    const file = new File([blob], `drivewitharturo-video-${Date.now()}.webm`, {
      type: "video/webm",
    });
    const nav = navigator as Navigator & {
      canShare?: (data: ShareData) => boolean;
      share?: (data: ShareData) => Promise<void>;
    };
    if (nav.canShare?.({ files: [file] }) && nav.share) {
      await nav.share({ title: "DriveWithArturo Video", files: [file] });
    } else {
      alert("Sharing video files is not supported here. Use Download Video.");
    }
  };

  const exportDealsCsv = () => {
    const headers = [
      "Date",
      "Customer",
      "Phone",
      "Vehicle",
      "Stock",
      "OTD",
      "Balance",
      "Payment",
    ];
    const rows = savedDeals.map((item) => [
      item.date,
      item.deal.customerName,
      item.deal.phone,
      item.deal.vehicleDescription,
      item.deal.stockNumber,
      item.result.totalBeforeDown,
      item.result.balanceAfterDown,
      item.result.monthlyPayment,
    ]);
    const csv = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `drivewitharturo-deals-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTextFile = (
    filename: string,
    content: string,
    type = "text/plain;charset=utf-8"
  ) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const csvEscape = (value: unknown) =>
    `"${String(value ?? "").replace(/"/g, '""')}"`;

  const exportRowsCsv = (
    filename: string,
    headers: string[],
    rows: unknown[][]
  ) => {
    const csv = [headers, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    downloadTextFile(filename, csv, "text/csv;charset=utf-8");
  };

  const exportLeadsCsv = () => {
    exportRowsCsv(
      `drivewitharturo-leads-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        "Created",
        "Name",
        "Phone",
        "Email",
        "Vehicle",
        "Source",
        "Status",
        "Temperature",
        "Preferred Language",
        "Budget",
        "Down Payment",
        "Employment",
        "Income Range",
        "Residency",
        "ID Type",
        "Has Trade",
        "Trade Year",
        "Trade Make",
        "Trade Model",
        "Trade Trim",
        "Trade Mileage",
        "Trade Payoff",
        "Lienholder",
        "Notes",
        "Next Follow-Up",
      ],
      leads.map((lead) => [
        lead.createdAt,
        lead.name,
        lead.phone,
        lead.email,
        lead.vehicle,
        lead.source,
        lead.status,
        lead.temperature,
        lead.preferredLanguage,
        lead.monthlyBudget,
        lead.downPaymentAvailable,
        lead.employmentType,
        lead.monthlyIncomeRange,
        lead.residencyStatus,
        lead.identificationType,
        lead.hasTrade ? "Yes" : "No",
        lead.trade.year,
        lead.trade.make,
        lead.trade.model,
        lead.trade.trim,
        lead.trade.mileage,
        lead.trade.payoffAmount,
        lead.trade.lienholder,
        lead.notes,
        lead.nextFollowUp,
      ])
    );
  };

  const exportInventoryCsv = () => {
    exportRowsCsv(
      `drivewitharturo-inventory-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        "Title",
        "Price",
        "Mileage",
        "Stock",
        "Vehicle URL",
        "Image URL",
        "Published",
        "Featured",
        "Content Score",
        "Vehicle Type",
      ],
      inventory.map((vehicle) => {
        const profile = detectVehicleType(vehicle.title);
        return [
          vehicle.title,
          vehicle.price,
          vehicle.mileage,
          vehicle.stockNumber,
          vehicle.vehicleUrl,
          vehicle.imageUrl,
          vehicle.published ? "Yes" : "No",
          vehicle.featured ? "Yes" : "No",
          profile.contentScore,
          profile.vehicleType,
        ];
      })
    );
  };

  const exportPostingTasksCsv = () => {
    exportRowsCsv(
      `drivewitharturo-posting-tasks-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
      [
        "Created",
        "Vehicle",
        "Stock",
        "Platform",
        "Status",
        "Posted At",
        "Caption",
        "Checklist",
      ],
      postingTasks.map((task) => [
        task.createdAt,
        task.vehicle,
        task.stockNumber,
        task.platform,
        task.status,
        task.postedAt || "",
        task.caption,
        task.checklist.join(" | "),
      ])
    );
  };

  const exportReviewsCsv = () => {
    exportRowsCsv(
      `drivewitharturo-reviews-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        "Customer",
        "Phone",
        "Email",
        "Vehicle",
        "Delivery Date",
        "Platform",
        "Status",
        "Link Sent",
        "Posted",
        "Screenshot Saved",
        "Referral Connected",
        "Notes",
      ],
      reviewRecords.map((review) => [
        review.customerName,
        review.phone,
        review.email,
        review.vehicle,
        review.deliveryDate,
        review.platform,
        review.status,
        review.linkSentAt,
        review.postedAt,
        review.screenshotSaved ? "Yes" : "No",
        review.referralConnected ? "Yes" : "No",
        review.notes,
      ])
    );
  };

  const exportReferralChecksCsv = () => {
    exportRowsCsv(
      `drivewitharturo-referral-checks-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
      [
        "Referrer",
        "Phone",
        "Referred Customer",
        "Vehicle",
        "Sale Date",
        "Status",
        "Check Number",
        "Paid Date",
        "Amount",
        "Notes",
      ],
      referralRecords.map((referral) => [
        referral.referrerName,
        referral.referrerPhone,
        referral.referredCustomer,
        referral.vehiclePurchased,
        referral.saleDate,
        referral.checkStatus,
        referral.checkNumber,
        referral.paidDate,
        referral.amount,
        referral.notes,
      ])
    );
  };

  const exportAllDataJson = () => {
    const backupDate = new Date().toLocaleString();
    const backup = {
      app: "DriveWithArturo Studio",
      backupDate,
      versionlessBuild: true,
      data: {
        deals: savedDeals,
        leads,
        inventory,
        overlays: overlayTemplates,
        postingTasks,
        reviews: reviewRecords,
        referrals: referralRecords,
      },
    };
    localStorage.setItem(STORAGE_KEYS.lastBackup, backupDate);
    setLastBackupDate(backupDate);
    downloadTextFile(
      `drivewitharturo-full-backup-${new Date()
        .toISOString()
        .slice(0, 10)}.json`,
      JSON.stringify(backup, null, 2),
      "application/json;charset=utf-8"
    );
  };

  const buildSupabaseSchemaSql =
    () => `-- DriveWithArturo Studio Supabase starter schema
-- Run this later inside Supabase SQL Editor when you are ready for cloud sync.
-- Keep using local backup/export until cloud sync is tested.

create table if not exists dwa_leads (
  id bigint primary key,
  created_at text,
  name text,
  phone text,
  email text,
  vehicle text,
  source text,
  status text,
  temperature text,
  notes text,
  next_follow_up text,
  preferred_language text,
  monthly_budget text,
  down_payment_available text,
  employment_type text,
  monthly_income_range text,
  residency_status text,
  identification_type text,
  income_verification jsonb,
  has_trade boolean,
  trade jsonb,
  updated_at timestamptz default now()
);

create table if not exists dwa_inventory (
  id bigint primary key,
  title text,
  price numeric,
  mileage numeric,
  stock_number text,
  image_url text,
  vehicle_url text,
  published boolean,
  featured boolean,
  content_profile jsonb,
  updated_at timestamptz default now()
);

create table if not exists dwa_deals (
  id bigint primary key,
  date text,
  salesperson text,
  deal jsonb,
  result jsonb,
  updated_at timestamptz default now()
);

create table if not exists dwa_posting_tasks (
  id bigint primary key,
  vehicle text,
  stock_number text,
  platform text,
  status text,
  caption text,
  checklist jsonb,
  created_at text,
  posted_at text,
  updated_at timestamptz default now()
);

create table if not exists dwa_overlay_templates (
  id bigint primary key,
  text text,
  position text,
  color text,
  size numeric,
  updated_at timestamptz default now()
);
`;

  const buildCloudMigrationPayload = () => {
    const migrationDate = new Date().toLocaleString();
    return {
      app: "DriveWithArturo Studio",
      migrationDate,
      cloudReady: true,
      notes: [
        "Export full backup before attempting cloud sync.",
        "This migration file is prepared for Supabase import testing.",
        "No SSN or ITIN numbers should be stored in this app.",
      ],
      counts: {
        leads: leads.length,
        inventory: inventory.length,
        deals: savedDeals.length,
        postingTasks: postingTasks.length,
        reviews: reviewRecords.length,
        referrals: referralRecords.length,
        overlays: overlayTemplates.length,
      },
      supabaseTables: [
        "dwa_leads",
        "dwa_inventory",
        "dwa_deals",
        "dwa_posting_tasks",
        "dwa_reviews",
        "dwa_referrals",
        "dwa_overlay_templates",
      ],
      data: {
        leads: leads.map((lead) => ({
          ...lead,
          cloudSyncId: `lead-${lead.id}`,
          updatedAt: migrationDate,
        })),
        inventory: inventory.map((vehicle) => ({
          ...vehicle,
          cloudSyncId: `inventory-${vehicle.id}`,
          contentProfile: detectVehicleType(vehicle.title),
          updatedAt: migrationDate,
        })),
        deals: savedDeals.map((savedDeal) => ({
          ...savedDeal,
          cloudSyncId: `deal-${savedDeal.id}`,
          updatedAt: migrationDate,
        })),
        postingTasks: postingTasks.map((task) => ({
          ...task,
          cloudSyncId: `posting-${task.id}`,
          updatedAt: migrationDate,
        })),
        reviews: reviewRecords.map((review) => ({
          ...review,
          cloudSyncId: `review-${review.id}`,
          updatedAt: migrationDate,
        })),
        referrals: referralRecords.map((referral) => ({
          ...referral,
          cloudSyncId: `referral-${referral.id}`,
          updatedAt: migrationDate,
        })),
        overlays: overlayTemplates.map((overlay) => ({
          ...overlay,
          cloudSyncId: `overlay-${overlay.id}`,
          updatedAt: migrationDate,
        })),
      },
    };
  };

  const exportCloudMigrationJson = () => {
    const migration = buildCloudMigrationPayload();
    downloadTextFile(
      `drivewitharturo-cloud-migration-${new Date()
        .toISOString()
        .slice(0, 10)}.json`,
      JSON.stringify(migration, null, 2),
      "application/json;charset=utf-8"
    );
  };

  const exportSupabaseSchemaSql = () => {
    downloadTextFile(
      `drivewitharturo-supabase-schema-${new Date()
        .toISOString()
        .slice(0, 10)}.sql`,
      buildSupabaseSchemaSql(),
      "text/sql;charset=utf-8"
    );
  };

  const importBackupJson = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const parsed = JSON.parse(text) as {
          data?: {
            deals?: SavedDeal[];
            leads?: Lead[];
            inventory?: InventoryVehicle[];
            overlays?: OverlayTemplate[];
            postingTasks?: PostingTask[];
            reviews?: ReviewRecord[];
            referrals?: ReferralRecord[];
            deliveries?: DeliveryRecord[];
          };
        };
        if (!parsed.data)
          throw new Error("Backup file is missing a data section.");
        if (
          !window.confirm(
            "Import this backup? This will replace current deals, leads, inventory, overlays, posting tasks, reviews, and referrals in this browser."
          )
        )
          return;

        const nextDeals = Array.isArray(parsed.data.deals)
          ? parsed.data.deals
          : [];
        const nextLeads = Array.isArray(parsed.data.leads)
          ? parsed.data.leads
          : [];
        const nextInventory = Array.isArray(parsed.data.inventory)
          ? parsed.data.inventory
          : [];
        const nextOverlays = Array.isArray(parsed.data.overlays)
          ? parsed.data.overlays
          : DEFAULT_OVERLAYS;
        const nextPostingTasks = Array.isArray(parsed.data.postingTasks)
          ? parsed.data.postingTasks
          : [];
        const nextReviews = Array.isArray(parsed.data.reviews)
          ? parsed.data.reviews
          : [];
        const nextReferrals = Array.isArray(parsed.data.referrals)
          ? parsed.data.referrals
          : [];
        const nextDeliveries = Array.isArray(parsed.data.deliveries)
          ? parsed.data.deliveries.map(normalizeDeliveryRecord)
          : [];

        setSavedDeals(nextDeals);
        setLeads(nextLeads);
        setInventory(nextInventory);
        setOverlayTemplates(nextOverlays);
        setPostingTasks(nextPostingTasks);
        setReviewRecords(nextReviews);
        setReferralRecords(nextReferrals);
        setDeliveryRecords(nextDeliveries);

        localStorage.setItem(STORAGE_KEYS.deals, JSON.stringify(nextDeals));
        localStorage.setItem(STORAGE_KEYS.leads, JSON.stringify(nextLeads));
        localStorage.setItem(
          STORAGE_KEYS.inventory,
          JSON.stringify(nextInventory)
        );
        localStorage.setItem(
          STORAGE_KEYS.overlays,
          JSON.stringify(nextOverlays)
        );
        localStorage.setItem(
          STORAGE_KEYS.postingTasks,
          JSON.stringify(nextPostingTasks)
        );
        localStorage.setItem(STORAGE_KEYS.reviews, JSON.stringify(nextReviews));
        localStorage.setItem(
          STORAGE_KEYS.referrals,
          JSON.stringify(nextReferrals)
        );
        alert("Backup imported successfully.");
      } catch (error) {
        alert(
          `Import failed: ${
            error instanceof Error ? error.message : "Invalid backup file"
          }`
        );
      }
    };
    input.click();
  };

  const clearDemoWorkspaceData = () => {
    if (
      !window.confirm(
        "Clear all saved deals, leads, inventory, overlays, posting tasks, reviews, and referrals from this browser? Export a backup first if this is real data."
      )
    )
      return;
    localStorage.removeItem(STORAGE_KEYS.deals);
    localStorage.removeItem(STORAGE_KEYS.leads);
    localStorage.removeItem(STORAGE_KEYS.inventory);
    localStorage.removeItem(STORAGE_KEYS.overlays);
    localStorage.removeItem(STORAGE_KEYS.postingTasks);
    localStorage.removeItem(STORAGE_KEYS.reviews);
    localStorage.removeItem(STORAGE_KEYS.referrals);
    localStorage.removeItem(STORAGE_KEYS.deliveries);
    setSavedDeals([]);
    setLeads([]);
    setInventory([]);
    setOverlayTemplates(DEFAULT_OVERLAYS);
    setPostingTasks([]);
    setReviewRecords([]);
    setReferralRecords([]);
    setDeliveryRecords([]);
    alert("Workspace data cleared in this browser.");
  };

  const chatReply = (text: string) => buildArturoReply(text, deal, result);

  const sendChat = () => {
    const message = chatInput.trim();
    if (!message) return;
    setChatMessages((current) => [
      ...current,
      { id: Date.now(), sender: "user", text: message },
      { id: Date.now() + 1, sender: "bot", text: chatReply(message) },
    ]);
    setChatInput("");
  };

  const cardStyle: React.CSSProperties = {
    background:
      "linear-gradient(145deg, rgba(12,12,14,0.92), rgba(24,24,27,0.78))",
    border: "1px solid rgba(255,255,255,0.13)",
    borderRadius: 22,
    padding: 22,
    boxShadow: "0 24px 70px rgba(0,0,0,0.48)",
    backdropFilter: "blur(12px)",
    color: "white",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 13,
    padding: "13px 14px",
    fontSize: 15,
    outline: "none",
    color: "white",
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06))",
    boxSizing: "border-box",
  };

  const buttonStyle: React.CSSProperties = {
    border: "1px solid rgba(255,255,255,0.18)",
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.14), rgba(255,255,255,0.06))",
    color: "white",
    borderRadius: 14,
    padding: "11px 14px",
    cursor: "pointer",
    fontWeight: 850,
  };

  const primaryButton: React.CSSProperties = {
    ...buttonStyle,
    background: "linear-gradient(135deg, #ef4444, #991b1b)",
    borderColor: "rgba(239,68,68,0.65)",
  };

  const Field = useCallback(
    ({ label, children }: { label: string; children: React.ReactNode }) => (
      <label style={{ display: "grid", gap: 8 }}>
        <span style={{ fontWeight: 850, fontSize: 13, color: "#e5e7eb" }}>
          {label}
        </span>
        {children}
      </label>
    ),
    []
  );

  const MoneyField = ({
    label,
    value,
    onChange,
  }: {
    label: string;
    value: string | number;
    onChange: (value: string) => void;
  }) => (
    <Field label={label}>
      <div style={{ position: "relative" }}>
        <span
          style={{
            position: "absolute",
            left: 14,
            top: 13,
            color: "#ef4444",
            fontWeight: 900,
          }}
        >
          $
        </span>
        <input
          value={String(value ?? "")}
          onChange={(event) => onChange(cleanNumericInput(event.target.value))}
          inputMode="decimal"
          style={{ ...inputStyle, paddingLeft: 30 }}
        />
      </div>
    </Field>
  );

  const StatCard = ({
    label,
    value,
    sub,
  }: {
    label: string;
    value: string | number;
    sub?: string;
  }) => (
    <div style={cardStyle}>
      <div style={{ color: "#a1a1aa", fontSize: 13, marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ color: "#ef4444", fontSize: 30, fontWeight: 950 }}>
        {value}
      </div>
      {sub && (
        <div style={{ color: "#d4d4d8", marginTop: 6, fontSize: 13 }}>
          {sub}
        </div>
      )}
    </div>
  );

  const Line = ({
    label,
    value,
    strong = false,
  }: {
    label: string;
    value: string;
    strong?: boolean;
  }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 14,
        padding: "11px 0",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        fontSize: strong ? 18 : 14,
        fontWeight: strong ? 950 : 550,
      }}
    >
      <span style={{ color: strong ? "#fff" : "#d4d4d8" }}>{label}</span>
      <span style={{ textAlign: "right", color: strong ? "#ef4444" : "#fff" }}>
        {value}
      </span>
    </div>
  );

  const TabButton = ({
    id,
    label,
  }: {
    id: typeof activeTab;
    label: string;
  }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        ...buttonStyle,
        background:
          activeTab === id
            ? "linear-gradient(135deg, #ef4444, #991b1b)"
            : "rgba(0,0,0,0.38)",
        borderColor:
          activeTab === id ? "rgba(239,68,68,0.75)" : "rgba(255,255,255,0.14)",
      }}
    >
      {label}
    </button>
  );

  const buildFollowUpSequence = (lead: Lead) => {
    const vehicle = lead.vehicle || "the vehicle";
    const name = lead.name || "there";
    const strategy = buildLeadStrategy(lead);
    return {
      sms: `Hi ${name}, Arturo here from Chicago Cars US. I wanted to follow up on ${vehicle}. ${strategy.recommendedReply}`,
      whatsapp: `🚗 ${name}, following up on ${vehicle}.\n\n${strategy.recommendedReply}\n\nText Arturo: (872) 364-0886\nHablamos Español`,
      email: `Hi ${name},\n\nI wanted to follow up on ${vehicle}. ${strategy.strategy}\n\n${strategy.recommendedReply}\n\nRespectfully,\nArturo\nChicago Cars US\n(872) 364-0886`,
      spanish: `Hola ${name}, soy Arturo de Chicago Cars US. Le estoy dando seguimiento sobre ${vehicle}. No puedo garantizar aprobaciones, pero vamos a revisar la información correcta y ver qué opciones pueden estar disponibles. ¿Qué hora le funciona para venir?`,
    };
  };

  const renderSalesFloor = () => (
    <div style={{ display: "grid", gap: 18 }}>
      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>AI Sales Floor</h2>
        <p style={{ color: "#d4d4d8", marginTop: -6, lineHeight: 1.6 }}>
          Live lead triage, AI Arturo coaching, re-engagement follow-up, and
          deal desk actions. No SSN or ITIN numbers collected here.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 14,
          }}
        >
          <StatCard
            label="Priority Leads"
            value={priorityLeads.length}
            sub="Need action now"
          />
          <StatCard
            label="Follow-Ups Due"
            value={followUpDue.length}
            sub="Text, email, or WhatsApp"
          />
          <StatCard
            label="Working Deals"
            value={workingDeals.length}
            sub="Active deal desk"
          />
          <StatCard
            label="Trade Opportunities"
            value={pendingTrades.length}
            sub="Equity / payoff review"
          />
        </div>
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Hot Lead Inbox</h2>
        {priorityLeads.length === 0 ? (
          <p style={{ color: "#d4d4d8" }}>
            No priority leads yet. Add leads in CRM to activate AI Arturo
            coaching.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {priorityLeads.map(({ lead, strategy }) => {
              const followUp = buildFollowUpSequence(lead);
              return (
                <div
                  key={`salesfloor-${lead.id}`}
                  style={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 18,
                    padding: 16,
                    background: "rgba(0,0,0,0.24)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 20, fontWeight: 950 }}>
                        {lead.name || "Unnamed Lead"}
                      </div>
                      <div style={{ color: "#d4d4d8", marginTop: 4 }}>
                        {lead.phone || "No phone"} ·{" "}
                        {lead.source || "Unknown Source"}
                      </div>
                      <div
                        style={{
                          color: "#facc15",
                          marginTop: 6,
                          fontWeight: 850,
                        }}
                      >
                        {lead.vehicle || "No vehicle selected"}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          color:
                            strategy.leadScore >= 80
                              ? "#ef4444"
                              : strategy.leadScore >= 60
                              ? "#facc15"
                              : "#a1a1aa",
                          fontWeight: 950,
                          fontSize: 18,
                        }}
                      >
                        {strategy.leadScore}% Lead ·{" "}
                        {strategy.appointmentProbability}% Appt
                      </div>
                      <div style={{ color: "#d4d4d8", marginTop: 6 }}>
                        {strategy.customerType}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: 10,
                      marginTop: 14,
                    }}
                  >
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 14,
                        background: "rgba(239,68,68,0.1)",
                        border: "1px solid rgba(239,68,68,0.24)",
                      }}
                    >
                      <b>Recommended Action:</b>
                      <br />
                      {strategy.nextBestAction}
                    </div>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 14,
                        background: "rgba(250,204,21,0.08)",
                        border: "1px solid rgba(250,204,21,0.18)",
                      }}
                    >
                      <b>Likely Objection:</b>
                      <br />
                      {strategy.mainObjection}
                    </div>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 14,
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.12)",
                      }}
                    >
                      <b>Strategy:</b>
                      <br />
                      {strategy.strategy}
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      borderRadius: 14,
                      background: "rgba(0,0,0,0.32)",
                      color: "#f4f4f5",
                      lineHeight: 1.55,
                    }}
                  >
                    <b style={{ color: "#fff" }}>AI Arturo Reply:</b>{" "}
                    {strategy.recommendedReply}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: 8,
                      marginTop: 12,
                    }}
                  >
                    <button
                      onClick={() =>
                        navigator.clipboard?.writeText(followUp.sms)
                      }
                      style={primaryButton}
                    >
                      Copy SMS
                    </button>
                    <button
                      onClick={() =>
                        navigator.clipboard?.writeText(followUp.whatsapp)
                      }
                      style={buttonStyle}
                    >
                      Copy WhatsApp
                    </button>
                    <button
                      onClick={() =>
                        navigator.clipboard?.writeText(followUp.email)
                      }
                      style={buttonStyle}
                    >
                      Copy Email
                    </button>
                    <button
                      onClick={() =>
                        navigator.clipboard?.writeText(followUp.spanish)
                      }
                      style={buttonStyle}
                    >
                      Copy Spanish
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Smart Lead Automation</h2>
        <p style={{ color: "#d4d4d8", marginTop: -6, lineHeight: 1.6 }}>
          AI Arturo automatically categorizes active leads by score, customer
          type, objection, and next best action.
        </p>
        {smartAutomationQueue.length === 0 ? (
          <p style={{ color: "#d4d4d8" }}>
            Add active CRM leads to activate smart automation.
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            {smartAutomationQueue.map(({ lead, strategy }) => (
              <div
                key={`automation-${lead.id}`}
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(0,0,0,0.24)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <div
                  style={{ color: "#ef4444", fontWeight: 950, fontSize: 16 }}
                >
                  {lead.name || "Unnamed Lead"}
                </div>
                <div style={{ color: "#facc15", marginTop: 6 }}>
                  {strategy.customerType}
                </div>
                <div style={{ color: "#fff", fontWeight: 900, marginTop: 8 }}>
                  Score {strategy.leadScore}% · Appointment{" "}
                  {strategy.appointmentProbability}%
                </div>
                <div
                  style={{ color: "#d4d4d8", marginTop: 8, lineHeight: 1.45 }}
                >
                  {strategy.nextBestAction}
                </div>
                <div style={{ color: "#a1a1aa", marginTop: 8, fontSize: 12 }}>
                  Reason: {strategy.reasons.slice(0, 2).join(" · ")}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Follow-Up Queue</h2>
        {followUpQueue.length === 0 ? (
          <p style={{ color: "#d4d4d8" }}>
            No follow-ups scheduled. Add a Next Follow-Up inside the CRM lead.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {followUpQueue.slice(0, 10).map(({ lead, strategy, priority }) => {
              const followUp = buildFollowUpSequence(lead);
              return (
                <div
                  key={`followup-${lead.id}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    gap: 12,
                    alignItems: "center",
                    padding: 12,
                    borderRadius: 16,
                    background: "rgba(0,0,0,0.24)",
                    border:
                      priority === "Overdue"
                        ? "1px solid rgba(239,68,68,0.5)"
                        : "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div>
                    <b
                      style={{
                        color: priority === "Overdue" ? "#ef4444" : "#fff",
                      }}
                    >
                      {priority}
                    </b>{" "}
                    · {lead.name || "Unnamed Lead"} ·{" "}
                    <span style={{ color: "#facc15" }}>
                      {lead.vehicle || "No vehicle"}
                    </span>
                    <div
                      style={{ color: "#d4d4d8", marginTop: 4, fontSize: 13 }}
                    >
                      {lead.nextFollowUp} · {strategy.nextBestAction}
                    </div>
                  </div>
                  <button
                    onClick={() => navigator.clipboard?.writeText(followUp.sms)}
                    style={buttonStyle}
                  >
                    Copy Follow-Up
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Appointment Board</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            gap: 10,
          }}
        >
          <StatCard
            label="Appointment Set"
            value={appointmentBoard.set.length}
          />
          <StatCard
            label="Working Deal"
            value={appointmentBoard.working.length}
          />
          <StatCard
            label="Delivered / Sold"
            value={appointmentBoard.sold.length}
          />
          <StatCard
            label="Lost / No Show"
            value={appointmentBoard.lost.length}
          />
        </div>
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>AI Trade Analyzer</h2>
        {tradeAnalysisBoard.length === 0 ? (
          <p style={{ color: "#d4d4d8" }}>
            No active trade analysis yet. Add trade year, make, model, mileage,
            payoff, and lienholder inside CRM.
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 12,
            }}
          >
            {tradeAnalysisBoard.slice(0, 6).map(({ lead, analysis }) => (
              <div
                key={`trade-analysis-${lead.id}`}
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(0,0,0,0.24)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <div style={{ color: "#ef4444", fontWeight: 950 }}>
                  {lead.trade.year} {lead.trade.make} {lead.trade.model}{" "}
                  {lead.trade.trim}
                </div>
                <div style={{ color: "#d4d4d8", marginTop: 6 }}>
                  {lead.name || "Unnamed Lead"} ·{" "}
                  {formatPlainNumber(lead.trade.mileage)} mi
                </div>
                <div style={{ marginTop: 10, color: "#fff", fontWeight: 900 }}>
                  Trade Range: {money(analysis.rangeLow)} -{" "}
                  {money(analysis.rangeHigh)}
                </div>
                <div
                  style={{
                    color: analysis.equity >= 0 ? "#22c55e" : "#ef4444",
                    fontWeight: 900,
                    marginTop: 6,
                  }}
                >
                  Equity: {money(analysis.equity)} · Risk: {analysis.riskLevel}
                </div>
                <div
                  style={{ color: "#d4d4d8", marginTop: 8, lineHeight: 1.45 }}
                >
                  {analysis.strategy}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Deal Desk Pipeline</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 10,
          }}
        >
          {(
            [
              "New",
              "Contacted",
              "Appointment Set",
              "Working Deal",
              "Sold",
              "Lost",
            ] as LeadStatus[]
          ).map((status) => {
            const count = leads.filter((lead) => lead.status === status).length;
            return <StatCard key={status} label={status} value={count} />;
          })}
        </div>
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Shadow Arturo Learning Snapshot</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.24)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Top Lead Sources</b>
            <div style={{ marginTop: 8, color: "#d4d4d8", lineHeight: 1.6 }}>
              {topLeadSources.length
                ? topLeadSources.map(([source, count]) => (
                    <div key={source}>
                      {source}: <b style={{ color: "#fff" }}>{count}</b>
                    </div>
                  ))
                : "No source data yet."}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.24)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Customer Types</b>
            <div style={{ marginTop: 8, color: "#d4d4d8", lineHeight: 1.6 }}>
              {topCustomerTypes.length
                ? topCustomerTypes.map(([type, count]) => (
                    <div key={type}>
                      {type}: <b style={{ color: "#fff" }}>{count}</b>
                    </div>
                  ))
                : "No customer type data yet."}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.24)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Top Objections</b>
            <div style={{ marginTop: 8, color: "#d4d4d8", lineHeight: 1.6 }}>
              {topObjections.length
                ? topObjections.map(([objection, count]) => (
                    <div key={objection}>
                      {objection}: <b style={{ color: "#fff" }}>{count}</b>
                    </div>
                  ))
                : "No objections yet."}
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderDashboard = () => (
    <div style={{ display: "grid", gap: 18 }}>
      <section style={cardStyle}>
        <p
          style={{
            color: "#d4d4d8",
            marginTop: 0,
            marginBottom: 0,
            fontSize: 15,
            lineHeight: 1.6,
          }}
        >
          Morning dashboard for leads, appointments, trades, follow-ups, content
          opportunities, and Shadow Arturo strategy.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            gap: 12,
            marginTop: 18,
          }}
        >
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.28)",
            }}
          >
            <div style={{ color: "#a1a1aa", fontSize: 12 }}>Today's Leads</div>
            <div style={{ color: "#ef4444", fontSize: 30, fontWeight: 950 }}>
              {todaysLeads.length}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(250,204,21,0.08)",
              border: "1px solid rgba(250,204,21,0.28)",
            }}
          >
            <div style={{ color: "#a1a1aa", fontSize: 12 }}>Appointments</div>
            <div style={{ color: "#facc15", fontSize: 30, fontWeight: 950 }}>
              {appointments}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(34,197,94,0.08)",
              border: "1px solid rgba(34,197,94,0.24)",
            }}
          >
            <div style={{ color: "#a1a1aa", fontSize: 12 }}>
              Sold / Close Ratio
            </div>
            <div style={{ color: "#22c55e", fontSize: 30, fontWeight: 950 }}>
              {soldLeads} · {closeRatio}%
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(59,130,246,0.08)",
              border: "1px solid rgba(59,130,246,0.24)",
            }}
          >
            <div style={{ color: "#a1a1aa", fontSize: 12 }}>Working Deals</div>
            <div style={{ color: "#93c5fd", fontSize: 30, fontWeight: 950 }}>
              {workingDeals.length}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(14,165,233,0.08)",
              border: "1px solid rgba(14,165,233,0.24)",
            }}
          >
            <div style={{ color: "#a1a1aa", fontSize: 12 }}>Follow-Ups Due</div>
            <div style={{ color: "#7dd3fc", fontSize: 30, fontWeight: 950 }}>
              {followUpDue.length}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(168,85,247,0.08)",
              border: "1px solid rgba(168,85,247,0.24)",
            }}
          >
            <div style={{ color: "#a1a1aa", fontSize: 12 }}>Pending Trades</div>
            <div style={{ color: "#c4b5fd", fontSize: 30, fontWeight: 950 }}>
              {pendingTrades.length}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <div style={{ color: "#a1a1aa", fontSize: 12 }}>
              Est. Front Gross Signal
            </div>
            <div style={{ color: "#fff", fontSize: 30, fontWeight: 950 }}>
              {money(potentialFrontGross)}
            </div>
          </div>
        </div>
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Shadow Arturo Priority Board</h2>
        {priorityLeads.length === 0 ? (
          <p style={{ color: "#d4d4d8" }}>
            No active priority leads yet. Add CRM leads to activate strategy
            cards.
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 12,
            }}
          >
            {priorityLeads.map(({ lead, strategy }) => (
              <div
                key={`v7-priority-${lead.id}`}
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(0,0,0,0.24)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <b style={{ color: "#ef4444", fontSize: 17 }}>
                    {lead.name || "Unnamed Lead"}
                  </b>
                  <span
                    style={{
                      color:
                        strategy.leadScore >= 80
                          ? "#ef4444"
                          : strategy.leadScore >= 60
                          ? "#facc15"
                          : "#a1a1aa",
                      fontWeight: 950,
                    }}
                  >
                    {strategy.leadScore}% · {strategy.appointmentProbability}%
                    Appt
                  </span>
                </div>
                <div
                  style={{ color: "#facc15", marginTop: 6, fontWeight: 850 }}
                >
                  {strategy.customerType}
                </div>
                <div
                  style={{ color: "#d4d4d8", marginTop: 8, lineHeight: 1.45 }}
                >
                  <b style={{ color: "#fff" }}>Next:</b>{" "}
                  {strategy.nextBestAction}
                </div>
                <div
                  style={{ color: "#d4d4d8", marginTop: 8, lineHeight: 1.45 }}
                >
                  <b style={{ color: "#fff" }}>Reply:</b>{" "}
                  {strategy.recommendedReply}
                </div>
                <button
                  onClick={() =>
                    navigator.clipboard?.writeText(strategy.recommendedReply)
                  }
                  style={{ ...buttonStyle, marginTop: 10, width: "100%" }}
                >
                  Copy Strategy Reply
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Learning Engine Snapshot</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.24)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Top Lead Sources</b>
            <div
              style={{
                marginTop: 10,
                display: "grid",
                gap: 8,
                color: "#d4d4d8",
              }}
            >
              {topLeadSources.length ? (
                topLeadSources.map(([source, count]) => (
                  <div key={source}>
                    {source}: <b style={{ color: "#fff" }}>{count}</b>
                  </div>
                ))
              ) : (
                <div>No source data yet.</div>
              )}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.24)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Customer Types</b>
            <div
              style={{
                marginTop: 10,
                display: "grid",
                gap: 8,
                color: "#d4d4d8",
              }}
            >
              {topCustomerTypes.length ? (
                topCustomerTypes.map(([type, count]) => (
                  <div key={type}>
                    {type}: <b style={{ color: "#fff" }}>{count}</b>
                  </div>
                ))
              ) : (
                <div>No customer type data yet.</div>
              )}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.24)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Top Objections</b>
            <div
              style={{
                marginTop: 10,
                display: "grid",
                gap: 8,
                color: "#d4d4d8",
              }}
            >
              {topObjections.length ? (
                topObjections.map(([objection, count]) => (
                  <div key={objection}>
                    {objection}: <b style={{ color: "#fff" }}>{count}</b>
                  </div>
                ))
              ) : (
                <div>No objections detected yet.</div>
              )}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.24)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Inventory Content Focus</b>
            <div
              style={{
                marginTop: 10,
                display: "grid",
                gap: 8,
                color: "#d4d4d8",
              }}
            >
              {priorityInventory.length ? (
                priorityInventory.map((item) => (
                  <div key={`focus-${item.vehicle.id}`}>
                    {item.vehicle.title}:{" "}
                    <b style={{ color: "#fff" }}>{item.profile.contentScore}</b>
                  </div>
                ))
              ) : (
                <div>Add inventory to activate content focus.</div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 14,
        }}
      >
        <StatCard
          label="OTD Total"
          value={money(result.totalBeforeDown)}
          sub="Current calculator"
        />
        <StatCard
          label="Monthly Estimate"
          value={`${money(result.monthlyPayment)}/mo`}
          sub={`${deal.apr}% APR · ${deal.term} months`}
        />
        <StatCard
          label="Hot Leads"
          value={hotLeads}
          sub={`${leads.length} total leads`}
        />
        <StatCard
          label="Appointments"
          value={appointments}
          sub={`${soldLeads} sold marked`}
        />
        <StatCard
          label="Inventory"
          value={inventory.length}
          sub={`${publishedInventory.length} published`}
        />
        <StatCard
          label="Saved Pipeline"
          value={money(totalPipeline)}
          sub={`${savedDeals.length} saved deals`}
        />
        <StatCard
          label="AI Content Score"
          value={averageMarketScore || "N/A"}
          sub={
            topInventoryUnit
              ? `Top: ${topInventoryUnit.vehicle.title}`
              : "Add inventory"
          }
        />
      </div>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Command Center</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          <button style={primaryButton} onClick={() => setActiveTab("otd")}>
            Build OTD Quote
          </button>
          <button style={buttonStyle} onClick={createLeadFromDeal}>
            Send Quote to CRM
          </button>
          <button style={buttonStyle} onClick={() => setActiveTab("studio")}>
            Open Video Studio
          </button>
          <button
            style={buttonStyle}
            onClick={() => generateMarketingPackage()}
          >
            Generate Marketing Package
          </button>
        </div>
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>AI Arturo Intelligence Engine</h2>
        <p style={{ color: "#d4d4d8", marginTop: -6 }}>
          Bilingual, trust-first, appointment-focused. No SSN or ITIN numbers
          collected on this site.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.24)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Signature Finance Line</b>
            <div style={{ marginTop: 8, color: "#f4f4f5", lineHeight: 1.5 }}>
              {AI_ARTURO.signatureFinanceLine}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.24)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Top Content Opportunity</b>
            <div style={{ marginTop: 8, color: "#f4f4f5", lineHeight: 1.5 }}>
              {topInventoryUnit
                ? `${topInventoryUnit.vehicle.title} · Content Score ${topInventoryUnit.profile.contentScore}`
                : "Add inventory to activate AI scoring."}
            </div>
          </div>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.24)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Compliance Guardrail</b>
            <div style={{ marginTop: 8, color: "#f4f4f5", lineHeight: 1.5 }}>
              Only ask whether they have SSN, ITIN, both, or prefer to discuss
              in person. Never collect the numbers here.
            </div>
          </div>
        </div>
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Lead Intelligence Snapshot</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          {leads.slice(0, 3).map((lead) => {
            const strategy = buildLeadStrategy(lead);
            return (
              <div
                key={`dashboard-strategy-${lead.id}`}
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(0,0,0,0.24)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <b style={{ color: "#ef4444" }}>
                  {lead.name || "Unnamed Lead"}
                </b>
                <div
                  style={{ marginTop: 6, color: "#facc15", fontWeight: 900 }}
                >
                  Score {strategy.leadScore}% ·{" "}
                  {strategy.appointmentProbability}% appointment
                </div>
                <div
                  style={{ marginTop: 6, color: "#f4f4f5", lineHeight: 1.5 }}
                >
                  {strategy.customerType} · {strategy.nextBestAction}
                </div>
              </div>
            );
          })}
          {leads.length === 0 && (
            <div style={{ color: "#d4d4d8" }}>
              Add CRM leads to activate Shadow Arturo recommendations.
            </div>
          )}
        </div>
      </section>

      {generatedHooks.length > 0 && <GeneratedHooks />}
      {generatedFollowUp && <GeneratedFollowUp />}
    </div>
  );

  const renderOtd = () => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.35fr) minmax(320px, 0.65fr)",
        gap: 20,
      }}
    >
      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Out-The-Door Calculator Plus</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 14,
          }}
        >
          <Field label="Customer Name">
            <input
              value={deal.customerName}
              onChange={(event) =>
                updateDeal("customerName", event.target.value)
              }
              style={inputStyle}
            />
          </Field>
          <Field label="Phone">
            <input
              value={deal.phone}
              onChange={(event) => updateDeal("phone", event.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Vehicle">
            <input
              value={deal.vehicleDescription}
              onChange={(event) =>
                updateDeal("vehicleDescription", event.target.value)
              }
              style={inputStyle}
            />
          </Field>
          <Field label="Stock #">
            <input
              value={deal.stockNumber}
              onChange={(event) =>
                updateDeal("stockNumber", event.target.value)
              }
              style={inputStyle}
            />
          </Field>
          <MoneyField
            label="Selling Price"
            value={deal.listPrice}
            onChange={(value) => updateDeal("listPrice", value)}
          />
          <MoneyField
            label="Trade ACV"
            value={deal.tradeValue}
            onChange={(value) => updateDeal("tradeValue", value)}
          />
          <MoneyField
            label="Trade Payoff"
            value={deal.tradePayoff}
            onChange={(value) => updateDeal("tradePayoff", value)}
          />
          <MoneyField
            label="Doc Fee"
            value={deal.docFee}
            onChange={(value) => updateDeal("docFee", value)}
          />
          <MoneyField
            label="Prep / Handling"
            value={deal.prepFee}
            onChange={(value) => updateDeal("prepFee", value)}
          />
          <MoneyField
            label="Title / Plates"
            value={deal.titleReg}
            onChange={(value) => updateDeal("titleReg", value)}
          />
          <MoneyField
            label="Government Fees"
            value={deal.governmentFees}
            onChange={(value) => updateDeal("governmentFees", value)}
          />
          <MoneyField
            label="Taxable ERT"
            value={deal.taxableERTFee}
            onChange={(value) => updateDeal("taxableERTFee", value)}
          />
          <MoneyField
            label="Warranty"
            value={deal.serviceContract}
            onChange={(value) => updateDeal("serviceContract", value)}
          />
          <MoneyField
            label="GAP"
            value={deal.gap}
            onChange={(value) => updateDeal("gap", value)}
          />
          <MoneyField
            label="Shipping"
            value={deal.shippingCost}
            onChange={(value) => updateDeal("shippingCost", value)}
          />
          <MoneyField
            label="Down Payment"
            value={deal.downPayment}
            onChange={(value) => updateDeal("downPayment", value)}
          />
          <Field label="APR %">
            <input
              value={deal.apr}
              inputMode="decimal"
              onChange={(event) =>
                updateDeal("apr", cleanNumericInput(event.target.value))
              }
              style={inputStyle}
            />
          </Field>
          <Field label="Term">
            <input
              value={deal.term}
              inputMode="numeric"
              onChange={(event) =>
                updateDeal("term", cleanNumericInput(event.target.value))
              }
              style={inputStyle}
            />
          </Field>
          <Field label="Tax Area">
            <select
              value={deal.taxArea}
              onChange={(event) =>
                updateDeal("taxArea", event.target.value as TaxAreaKey)
              }
              style={inputStyle}
            >
              {Object.entries(TAX_AREAS).map(([key, area]) => (
                <option key={key} value={key}>
                  {area.label} · {area.display}
                </option>
              ))}
            </select>
          </Field>
          {deal.taxArea === "custom" && (
            <div style={{ display: "grid", gap: 10 }}>
              <Field label="Custom Tax % (only if taxable)">
                <input
                  value={deal.customRate ?? ""}
                  inputMode="decimal"
                  onChange={(event) =>
                    updateDeal(
                      "customRate",
                      cleanNumericInput(event.target.value)
                    )
                  }
                  style={inputStyle}
                />
              </Field>
              <label
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid rgba(250,204,21,0.45)",
                  background: "rgba(250,204,21,0.08)",
                  color: "#fff",
                  fontWeight: 850,
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(deal.outOfStateTaxable)}
                  onChange={(event) =>
                    updateDeal("outOfStateTaxable", event.target.checked)
                  }
                />
                Taxable out-of-state deal
              </label>
              <div style={{ color: "#facc15", fontWeight: 800, fontSize: 13 }}>
                Out-of-state default: no title/plate fee, no government fee, ERT
                only, and no sales tax unless checked taxable.
              </div>
            </div>
          )}
        </div>
      </section>

      <aside
        style={{
          ...cardStyle,
          position: "sticky",
          top: 18,
          alignSelf: "start",
        }}
      >
        <h2 style={{ marginTop: 0 }}>Deal Summary</h2>
        <Line label="Selling Price" value={money(result.price)} />
        <Line label="Taxable Subtotal" value={money(result.taxableSubtotal)} />
        <Line label="Base Tax" value={money(result.baseTax)} />
        <Line label="City Tax" value={money(result.cityTax)} />
        <Line label="Rounded Tax" value={money(result.totalTax)} />
        <Line label="Trade Allowance" value={money(result.tradeAllowance)} />
        <Line label="Tax Savings" value={money(result.taxSavings)} />
        <Line label="Fees + Products" value={money(result.nonTaxableFees)} />
        <Line label="OTD Total" value={money(result.totalBeforeDown)} strong />
        <Line
          label="Balance After Down"
          value={money(result.balanceAfterDown)}
          strong
        />
        <Line
          label="Estimated Payment"
          value={`${money(result.monthlyPayment)}/mo`}
          strong
        />
        <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
          <button onClick={saveDeal} style={primaryButton}>
            Save Deal
          </button>
          <button onClick={shareDeal} style={buttonStyle}>
            Share by Text
          </button>
          <button onClick={createLeadFromDeal} style={buttonStyle}>
            Create CRM Lead
          </button>
          <button onClick={() => window.print()} style={buttonStyle}>
            Print / Save PDF
          </button>
        </div>
      </aside>
    </div>
  );

  const renderCrm = () => {
    const readinessScore = getLeadReadinessScore(leadForm);
    const tradeEstimate = getTradeEquityEstimate(leadForm);

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 0.55fr) minmax(0, 1fr)",
          gap: 18,
        }}
      >
        <section style={cardStyle}>
          <h2 style={{ marginTop: 0 }}>
            CRM Lead Capture + Finance Pre-Screener
          </h2>
          <p style={{ color: "#d4d4d8", marginTop: -6 }}>
            Lead qualification only. Do not collect SSN or ITIN numbers here —
            customers complete the secure application on ChicagoCarsUS.com.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              marginBottom: 14,
            }}
          >
            <StatCard
              label="Readiness Score"
              value={`${readinessScore}%`}
              sub="No sensitive ID numbers collected"
            />
            <StatCard
              label="Lead Temp"
              value={leadForm.temperature}
              sub="AI-ready handoff"
            />
            <StatCard
              label="Trade Equity"
              value={tradeEstimate ? money(tradeEstimate.equity) : "N/A"}
              sub={
                tradeEstimate
                  ? `Est. value ${money(tradeEstimate.estimatedValue)}`
                  : "Add trade details"
              }
            />
          </div>

          <div style={{ display: "grid", gap: 16 }}>
            <div
              style={{
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 16,
                padding: 14,
                background: "rgba(0,0,0,0.22)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Customer</h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 12,
                }}
              >
                <Field label="Name">
                  <input
                    value={leadForm.name}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                    style={inputStyle}
                  />
                </Field>
                <Field label="Phone">
                  <input
                    value={leadForm.phone}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        phone: event.target.value,
                      }))
                    }
                    style={inputStyle}
                  />
                </Field>
                <Field label="Email">
                  <input
                    value={leadForm.email}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        email: event.target.value,
                      }))
                    }
                    style={inputStyle}
                  />
                </Field>
                <Field label="Preferred Language">
                  <select
                    value={leadForm.preferredLanguage}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        preferredLanguage: event.target
                          .value as PreferredLanguage,
                      }))
                    }
                    style={inputStyle}
                  >
                    <option>English</option>
                    <option>Español</option>
                    <option>Bilingual</option>
                  </select>
                </Field>
              </div>
            </div>

            <div
              style={{
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 16,
                padding: 14,
                background: "rgba(0,0,0,0.22)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Vehicle + Budget</h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 12,
                }}
              >
                <Field label="Vehicle">
                  <input
                    value={leadForm.vehicle}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        vehicle: event.target.value,
                      }))
                    }
                    style={inputStyle}
                  />
                </Field>
                <Field label="Source">
                  <input
                    value={leadForm.source}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        source: event.target.value,
                      }))
                    }
                    style={inputStyle}
                  />
                </Field>
                <Field label="Monthly Budget">
                  <select
                    value={leadForm.monthlyBudget}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        monthlyBudget: event.target.value,
                      }))
                    }
                    style={inputStyle}
                  >
                    <option value="">Select range</option>
                    <option>Under $300</option>
                    <option>$300-$400</option>
                    <option>$400-$500</option>
                    <option>$500-$600</option>
                    <option>$600+</option>
                    <option>Not Sure</option>
                  </select>
                </Field>
                <Field label="Down Payment Available">
                  <select
                    value={leadForm.downPaymentAvailable}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        downPaymentAvailable: event.target.value,
                      }))
                    }
                    style={inputStyle}
                  >
                    <option value="">Select range</option>
                    <option>$0</option>
                    <option>$500-$1,000</option>
                    <option>$1,000-$2,500</option>
                    <option>$2,500-$5,000</option>
                    <option>$5,000+</option>
                  </select>
                </Field>
              </div>
            </div>

            <div
              style={{
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 16,
                padding: 14,
                background: "rgba(0,0,0,0.22)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Financing Readiness</h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 12,
                }}
              >
                <Field label="Employment Type">
                  <select
                    value={leadForm.employmentType}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        employmentType: event.target.value,
                      }))
                    }
                    style={inputStyle}
                  >
                    <option value="">Select</option>
                    <option>W-2 Employee</option>
                    <option>1099 / Self Employed</option>
                    <option>Cash Income</option>
                    <option>Retired</option>
                    <option>Military Veteran</option>
                    <option>Other</option>
                  </select>
                </Field>
                <Field label="Monthly Income Range">
                  <select
                    value={leadForm.monthlyIncomeRange}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        monthlyIncomeRange: event.target.value,
                      }))
                    }
                    style={inputStyle}
                  >
                    <option value="">Select range</option>
                    <option>Under $2,000</option>
                    <option>$2,000-$3,500</option>
                    <option>$3,500-$5,000</option>
                    <option>$5,000-$7,500</option>
                    <option>$7,500+</option>
                    <option>Prefer To Discuss</option>
                  </select>
                </Field>
                <Field label="Residency Status">
                  <select
                    value={leadForm.residencyStatus}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        residencyStatus: event.target.value,
                      }))
                    }
                    style={inputStyle}
                  >
                    <option value="">Select</option>
                    <option>US Citizen</option>
                    <option>Permanent Resident</option>
                    <option>Work Permit</option>
                    <option>Other</option>
                    <option>Prefer To Discuss In Person</option>
                  </select>
                </Field>
                <Field label="Which do they have? No numbers collected.">
                  <select
                    value={leadForm.identificationType}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        identificationType: event.target
                          .value as IdentificationType,
                      }))
                    }
                    style={inputStyle}
                  >
                    <option>SSN</option>
                    <option>ITIN</option>
                    <option>Both</option>
                    <option>Prefer To Discuss In Person</option>
                  </select>
                </Field>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 900, marginBottom: 8 }}>
                  Income Verification Available
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {INCOME_VERIFICATION_OPTIONS.map((option) => (
                    <button
                      key={option}
                      onClick={() => toggleIncomeVerification(option)}
                      style={{
                        ...buttonStyle,
                        background: leadForm.incomeVerification.includes(option)
                          ? "linear-gradient(135deg, #ef4444, #991b1b)"
                          : buttonStyle.background,
                        borderColor: leadForm.incomeVerification.includes(
                          option
                        )
                          ? "rgba(239,68,68,0.7)"
                          : "rgba(255,255,255,0.18)",
                      }}
                    >
                      {leadForm.incomeVerification.includes(option) ? "✓ " : ""}
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div
              style={{
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 16,
                padding: 14,
                background: "rgba(0,0,0,0.22)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <h3 style={{ margin: 0 }}>Trade-In Information</h3>
                <button
                  onClick={() =>
                    setLeadForm((current) => ({
                      ...current,
                      hasTrade: !current.hasTrade,
                    }))
                  }
                  style={leadForm.hasTrade ? primaryButton : buttonStyle}
                >
                  {leadForm.hasTrade ? "Trade-In YES" : "No Trade / Add Trade"}
                </button>
              </div>

              {leadForm.hasTrade && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: 12,
                    marginTop: 14,
                  }}
                >
                  <Field label="Year">
                    <input
                      value={leadForm.trade.year}
                      onChange={(event) =>
                        updateLeadTrade("year", event.target.value)
                      }
                      style={inputStyle}
                    />
                  </Field>
                  <Field label="Make">
                    <input
                      value={leadForm.trade.make}
                      onChange={(event) =>
                        updateLeadTrade("make", event.target.value)
                      }
                      style={inputStyle}
                    />
                  </Field>
                  <Field label="Model">
                    <input
                      value={leadForm.trade.model}
                      onChange={(event) =>
                        updateLeadTrade("model", event.target.value)
                      }
                      style={inputStyle}
                    />
                  </Field>
                  <Field label="Trim">
                    <input
                      value={leadForm.trade.trim}
                      onChange={(event) =>
                        updateLeadTrade("trim", event.target.value)
                      }
                      style={inputStyle}
                    />
                  </Field>
                  <Field label="Mileage">
                    <input
                      value={leadForm.trade.mileage}
                      inputMode="numeric"
                      onChange={(event) =>
                        updateLeadTrade(
                          "mileage",
                          cleanNumericInput(event.target.value)
                        )
                      }
                      style={inputStyle}
                    />
                  </Field>
                  <Field label="Frame Damage">
                    <select
                      value={leadForm.trade.frameDamage}
                      onChange={(event) =>
                        updateLeadTrade(
                          "frameDamage",
                          event.target.value as TradeInfo["frameDamage"]
                        )
                      }
                      style={inputStyle}
                    >
                      <option>Yes</option>
                      <option>No</option>
                      <option>Not Sure</option>
                    </select>
                  </Field>
                  <Field label="Rust">
                    <select
                      value={leadForm.trade.rust}
                      onChange={(event) =>
                        updateLeadTrade(
                          "rust",
                          event.target.value as TradeInfo["rust"]
                        )
                      }
                      style={inputStyle}
                    >
                      <option>Yes</option>
                      <option>No</option>
                      <option>Minor Surface Rust</option>
                      <option>Not Sure</option>
                    </select>
                  </Field>
                  <Field label="Title Status">
                    <select
                      value={leadForm.trade.titleStatus}
                      onChange={(event) =>
                        updateLeadTrade(
                          "titleStatus",
                          event.target.value as TradeInfo["titleStatus"]
                        )
                      }
                      style={inputStyle}
                    >
                      <option>Clean</option>
                      <option>Rebuilt</option>
                      <option>Salvage</option>
                      <option>Not Sure</option>
                    </select>
                  </Field>
                  <Field label="Payoff Amount">
                    <input
                      value={leadForm.trade.payoffAmount}
                      inputMode="decimal"
                      onChange={(event) =>
                        updateLeadTrade(
                          "payoffAmount",
                          cleanNumericInput(event.target.value)
                        )
                      }
                      style={inputStyle}
                    />
                  </Field>
                  <Field label="Lienholder">
                    <input
                      value={leadForm.trade.lienholder}
                      onChange={(event) =>
                        updateLeadTrade("lienholder", event.target.value)
                      }
                      style={inputStyle}
                    />
                  </Field>
                  <Field label="Any Major Issues?">
                    <textarea
                      value={leadForm.trade.majorIssues}
                      onChange={(event) =>
                        updateLeadTrade("majorIssues", event.target.value)
                      }
                      style={{ ...inputStyle, minHeight: 88 }}
                    />
                  </Field>
                </div>
              )}
            </div>

            <div
              style={{
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 16,
                padding: 14,
                background: "rgba(0,0,0,0.22)",
              }}
            >
              <Field label="Internal Notes">
                <textarea
                  value={leadForm.notes}
                  onChange={(event) =>
                    setLeadForm((current) => ({
                      ...current,
                      notes: event.target.value,
                    }))
                  }
                  style={{ ...inputStyle, minHeight: 90 }}
                />
              </Field>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: 10,
                  marginTop: 12,
                }}
              >
                <Field label="Temperature">
                  <select
                    value={leadForm.temperature}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        temperature: event.target.value as LeadTemperature,
                      }))
                    }
                    style={inputStyle}
                  >
                    <option>HOT</option>
                    <option>WARM</option>
                    <option>COLD</option>
                  </select>
                </Field>
                <Field label="Next Follow-Up">
                  <input
                    value={leadForm.nextFollowUp}
                    onChange={(event) =>
                      setLeadForm((current) => ({
                        ...current,
                        nextFollowUp: event.target.value,
                      }))
                    }
                    style={inputStyle}
                    placeholder="Today 5 PM"
                  />
                </Field>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 10,
                  marginTop: 14,
                }}
              >
                <button onClick={addLead} style={primaryButton}>
                  Add Lead
                </button>
                <button onClick={() => generateFollowUp()} style={buttonStyle}>
                  Generate Follow-Up
                </button>
                <a
                  href="https://www.chicagocarsus.com/finance-your-car/pre-approved"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    ...buttonStyle,
                    textDecoration: "none",
                    textAlign: "center",
                  }}
                >
                  Secure Credit App
                </a>
              </div>
            </div>
          </div>
        </section>

        <section style={cardStyle}>
          <h2 style={{ marginTop: 0 }}>Customer Pipeline</h2>
          {leads.length === 0 ? (
            <p style={{ color: "#d4d4d8" }}>No leads yet.</p>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {leads.map((lead) => {
                const leadScore = getLeadReadinessScore(lead);
                const tradeInfo = getTradeEquityEstimate(lead);
                const strategy = buildLeadStrategy(lead);
                return (
                  <div
                    key={lead.id}
                    style={{
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: 16,
                      padding: 14,
                      background: "rgba(0,0,0,0.22)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 19, fontWeight: 950 }}>
                          {lead.name || "Unnamed Lead"}
                        </div>
                        <div style={{ color: "#d4d4d8", fontSize: 13 }}>
                          {lead.phone} · {lead.source} ·{" "}
                          {lead.preferredLanguage}
                        </div>
                        <div style={{ color: "#facc15", marginTop: 6 }}>
                          {lead.vehicle}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div
                          style={{
                            color:
                              lead.temperature === "HOT"
                                ? "#ef4444"
                                : lead.temperature === "WARM"
                                ? "#facc15"
                                : "#a1a1aa",
                            fontWeight: 950,
                          }}
                        >
                          {lead.temperature} · {leadScore}% Ready
                        </div>
                        <select
                          value={lead.status}
                          onChange={(event) =>
                            updateLeadStatus(
                              lead.id,
                              event.target.value as LeadStatus
                            )
                          }
                          style={{ ...inputStyle, marginTop: 8, minWidth: 170 }}
                        >
                          {(
                            [
                              "New",
                              "Contacted",
                              "Appointment Set",
                              "Working Deal",
                              "Sold",
                              "Lost",
                            ] as LeadStatus[]
                          ).map((status) => (
                            <option key={status}>{status}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(160px, 1fr))",
                        gap: 8,
                        marginTop: 12,
                        color: "#d4d4d8",
                        fontSize: 13,
                      }}
                    >
                      <div>
                        <b style={{ color: "#fff" }}>Budget:</b>{" "}
                        {lead.monthlyBudget || "N/A"}
                      </div>
                      <div>
                        <b style={{ color: "#fff" }}>Down:</b>{" "}
                        {lead.downPaymentAvailable || "N/A"}
                      </div>
                      <div>
                        <b style={{ color: "#fff" }}>ID Type:</b>{" "}
                        {lead.identificationType || "N/A"}
                      </div>
                      <div>
                        <b style={{ color: "#fff" }}>Income:</b>{" "}
                        {lead.monthlyIncomeRange || "N/A"}
                      </div>
                    </div>

                    {lead.hasTrade && (
                      <div
                        style={{
                          marginTop: 12,
                          padding: 12,
                          borderRadius: 14,
                          background: "rgba(239,68,68,0.08)",
                          border: "1px solid rgba(239,68,68,0.25)",
                          color: "#f4f4f5",
                        }}
                      >
                        <b>Trade:</b> {lead.trade.year} {lead.trade.make}{" "}
                        {lead.trade.model} {lead.trade.trim} ·{" "}
                        {formatPlainNumber(lead.trade.mileage)} mi
                        <br />
                        <b>Issues:</b> {lead.trade.majorIssues || "None listed"}{" "}
                        · Frame: {lead.trade.frameDamage} · Rust:{" "}
                        {lead.trade.rust}
                        <br />
                        <b>Payoff:</b> {money(lead.trade.payoffAmount)} ·{" "}
                        <b>Lienholder:</b> {lead.trade.lienholder || "N/A"}
                        {tradeInfo && (
                          <>
                            <br />
                            <b>AI Equity Hunter:</b> Est. value{" "}
                            {money(tradeInfo.estimatedValue)} · Equity{" "}
                            {money(tradeInfo.equity)}
                          </>
                        )}
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        borderRadius: 14,
                        background: "rgba(250,204,21,0.08)",
                        border: "1px solid rgba(250,204,21,0.28)",
                        color: "#f4f4f5",
                      }}
                    >
                      <b style={{ color: "#facc15" }}>
                        Shadow Arturo Strategy Card
                      </b>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(160px, 1fr))",
                          gap: 8,
                          marginTop: 8,
                          fontSize: 13,
                        }}
                      >
                        <div>
                          <b>Customer Type:</b> {strategy.customerType}
                        </div>
                        <div>
                          <b>Appointment:</b> {strategy.appointmentProbability}%
                        </div>
                        <div>
                          <b>Objection:</b> {strategy.mainObjection}
                        </div>
                        <div>
                          <b>Next Action:</b> {strategy.nextBestAction}
                        </div>
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          color: "#d4d4d8",
                          lineHeight: 1.5,
                        }}
                      >
                        <b style={{ color: "#fff" }}>Strategy:</b>{" "}
                        {strategy.strategy}
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          color: "#d4d4d8",
                          lineHeight: 1.5,
                        }}
                      >
                        <b style={{ color: "#fff" }}>Recommended Reply:</b>{" "}
                        {strategy.recommendedReply}
                      </div>
                      <div
                        style={{ marginTop: 8, color: "#a1a1aa", fontSize: 12 }}
                      >
                        Reasons: {strategy.reasons.join(" · ")}
                      </div>
                    </div>

                    {lead.notes && (
                      <div
                        style={{
                          color: "#d4d4d8",
                          marginTop: 10,
                          lineHeight: 1.5,
                        }}
                      >
                        {lead.notes}
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                        marginTop: 12,
                      }}
                    >
                      <button
                        onClick={() => generateFollowUp(lead)}
                        style={buttonStyle}
                      >
                        AI Follow-Up
                      </button>
                      <button
                        onClick={() =>
                          navigator.clipboard?.writeText(
                            strategy.recommendedReply
                          )
                        }
                        style={buttonStyle}
                      >
                        Copy AI Reply
                      </button>
                      <a
                        href="https://www.chicagocarsus.com/finance-your-car/pre-approved"
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...buttonStyle, textDecoration: "none" }}
                      >
                        Send Secure App
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    );
  };

  const renderInventory = () => (
    <div style={{ display: "grid", gap: 18 }}>
      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Inventory Manager</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
          }}
        >
          <Field label="Vehicle Title">
            <input
              value={inventoryForm.title}
              onChange={(event) =>
                setInventoryForm((current) => ({
                  ...current,
                  title: event.target.value,
                }))
              }
              style={inputStyle}
            />
          </Field>
          <MoneyField
            label="Price"
            value={inventoryForm.price}
            onChange={(value) =>
              setInventoryForm((current) => ({ ...current, price: value }))
            }
          />
          <Field label="Mileage">
            <input
              value={inventoryForm.mileage}
              inputMode="numeric"
              onChange={(event) =>
                setInventoryForm((current) => ({
                  ...current,
                  mileage: cleanNumericInput(event.target.value),
                }))
              }
              style={inputStyle}
            />
          </Field>
          <Field label="Stock #">
            <input
              value={inventoryForm.stockNumber}
              onChange={(event) =>
                setInventoryForm((current) => ({
                  ...current,
                  stockNumber: event.target.value,
                }))
              }
              style={inputStyle}
            />
          </Field>
          <Field label="Image URL">
            <input
              value={inventoryForm.imageUrl}
              onChange={(event) =>
                setInventoryForm((current) => ({
                  ...current,
                  imageUrl: event.target.value,
                }))
              }
              style={inputStyle}
            />
          </Field>
          <Field label="Vehicle URL">
            <input
              value={inventoryForm.vehicleUrl}
              onChange={(event) =>
                setInventoryForm((current) => ({
                  ...current,
                  vehicleUrl: event.target.value,
                }))
              }
              style={inputStyle}
            />
          </Field>
        </div>
        <button
          onClick={addInventoryVehicle}
          style={{ ...primaryButton, marginTop: 14 }}
        >
          Add Vehicle
        </button>
      </section>

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 14,
        }}
      >
        {inventory.map((vehicle) => (
          <div key={vehicle.id} style={cardStyle}>
            <div
              style={{
                height: 160,
                borderRadius: 16,
                background: vehicle.imageUrl
                  ? `url(${vehicle.imageUrl}) center / cover no-repeat`
                  : "linear-gradient(135deg,#7f1d1d,#09090b)",
                display: "grid",
                placeItems: "center",
                marginBottom: 14,
              }}
            >
              {!vehicle.imageUrl && "Vehicle Photo"}
            </div>
            <div style={{ fontSize: 19, fontWeight: 950 }}>{vehicle.title}</div>
            <div
              style={{
                color: "#ef4444",
                fontSize: 26,
                fontWeight: 950,
                marginTop: 6,
              }}
            >
              {money(vehicle.price)}
            </div>
            <div style={{ color: "#d4d4d8", marginTop: 4 }}>
              {toNumber(vehicle.mileage).toLocaleString()} miles · Stock #
              {vehicle.stockNumber || "N/A"}
            </div>
            {(() => {
              const profile = detectVehicleType(vehicle.title);
              return (
                <div
                  style={{
                    marginTop: 10,
                    padding: 10,
                    borderRadius: 12,
                    background: "rgba(239,68,68,0.08)",
                    border: "1px solid rgba(239,68,68,0.22)",
                    color: "#f4f4f5",
                    fontSize: 13,
                  }}
                >
                  <b>AI Content Score:</b> {profile.contentScore}/100 ·{" "}
                  <b>Type:</b> {profile.vehicleType}
                  <br />
                  <span style={{ color: "#d4d4d8" }}>{profile.reason}</span>
                </div>
              );
            })()}
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 12,
              }}
            >
              <button
                onClick={() => fillCalculatorFromVehicle(vehicle)}
                style={primaryButton}
              >
                Calculate OTD
              </button>
              <button
                onClick={() => generateMarketingPackage(vehicle)}
                style={primaryButton}
              >
                🎬 Generate Marketing Package
              </button>
              <button
                onClick={() =>
                  setInventory((current) =>
                    current.map((item) =>
                      item.id === vehicle.id
                        ? { ...item, published: !item.published }
                        : item
                    )
                  )
                }
                style={buttonStyle}
              >
                {vehicle.published ? "Published" : "Hidden"}
              </button>
              <button
                onClick={() =>
                  setInventory((current) =>
                    current.filter((item) => item.id !== vehicle.id)
                  )
                }
                style={buttonStyle}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </section>
    </div>
  );

  const drawDriveWithArturoWatermark = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ) => {
    const boxWidth = Math.min(width * 0.72, 560);
    const boxHeight = Math.max(58, height * 0.055);
    let x = (width - boxWidth) / 2;
    let y = height - boxHeight - Math.max(26, height * 0.03);

    if (watermarkPosition === "bottom-left") x = Math.max(22, width * 0.035);
    if (watermarkPosition === "bottom-right")
      x = width - boxWidth - Math.max(22, width * 0.035);
    if (watermarkPosition === "center") y = (height - boxHeight) / 2;

    ctx.save();
    ctx.globalAlpha = Math.max(0.1, Math.min(1, watermarkOpacity / 100));

    if (watermarkPosition === "diagonal") {
      ctx.translate(width / 2, height / 2);
      ctx.rotate(-Math.PI / 7);
      ctx.fillStyle = "rgba(0,0,0,0.58)";
      ctx.strokeStyle = "rgba(212,175,55,0.95)";
      ctx.lineWidth = 5;
      ctx.fillRect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight);
      ctx.strokeRect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight);
      ctx.fillStyle = "#d4af37";
      ctx.font = `900 ${Math.round(boxHeight * 0.42)}px Arial`;
      ctx.textAlign = "center";
      ctx.fillText("DRIVEWITHARTURO", 0, -boxHeight * 0.04);
      ctx.fillStyle = "#ffffff";
      ctx.font = `800 ${Math.round(boxHeight * 0.23)}px Arial`;
      ctx.fillText("Chicago Cars US · (872) 364-0886", 0, boxHeight * 0.28);
      ctx.restore();
      return;
    }

    ctx.fillStyle = "rgba(0,0,0,0.72)";
    ctx.strokeStyle = "rgba(212,175,55,0.95)";
    ctx.lineWidth = 4;
    ctx.fillRect(x, y, boxWidth, boxHeight);
    ctx.strokeRect(x, y, boxWidth, boxHeight);

    ctx.fillStyle = "#d4af37";
    ctx.font = `900 ${Math.round(boxHeight * 0.42)}px Arial`;
    ctx.textAlign = "center";
    ctx.fillText("DRIVEWITHARTURO", x + boxWidth / 2, y + boxHeight * 0.46);

    ctx.fillStyle = "#ffffff";
    ctx.font = `800 ${Math.round(boxHeight * 0.23)}px Arial`;
    ctx.fillText(
      "Chicago Cars US · Text Arturo: (872) 364-0886",
      x + boxWidth / 2,
      y + boxHeight * 0.77
    );
    ctx.restore();
  };

  const drawDeliveryFrameOverlay = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ) => {
    ctx.save();
    ctx.strokeStyle = "rgba(212,175,55,0.98)";
    ctx.lineWidth = Math.max(12, width * 0.018);
    ctx.strokeRect(
      ctx.lineWidth / 2,
      ctx.lineWidth / 2,
      width - ctx.lineWidth,
      height - ctx.lineWidth
    );

    const topBarHeight = Math.max(90, height * 0.095);
    const bottomBarHeight = Math.max(110, height * 0.105);

    ctx.fillStyle = "rgba(0,0,0,0.78)";
    ctx.fillRect(0, 0, width, topBarHeight);
    ctx.fillRect(0, height - bottomBarHeight, width, bottomBarHeight);

    const deliveryHeadline =
      deliveryTemplate === "welcome"
        ? "WELCOME TO THE FAMILY"
        : deliveryTemplate === "veteran"
        ? "VETERAN BUYER"
        : deliveryTemplate === "family"
        ? "FAMILY VEHICLE"
        : deliveryTemplate === "business"
        ? "BUSINESS MOVE"
        : deliveryTemplate === "firstTime"
        ? "FIRST TIME BUYER"
        : deliveryTemplate === "referral"
        ? "REFERRAL DELIVERY"
        : "CONGRATULATIONS";

    ctx.fillStyle = "#d4af37";
    ctx.textAlign = "center";
    ctx.font = `900 ${Math.round(topBarHeight * 0.31)}px Arial`;
    ctx.fillText(deliveryHeadline, width / 2, topBarHeight * 0.43);

    ctx.fillStyle = "#ffffff";
    ctx.font = `800 ${Math.round(topBarHeight * 0.22)}px Arial`;
    ctx.fillText(
      clientNameForFrame || "OUR CUSTOMER",
      width / 2,
      topBarHeight * 0.73
    );

    ctx.fillStyle = "#ffffff";
    ctx.font = `900 ${Math.round(bottomBarHeight * 0.25)}px Arial`;
    ctx.fillText(
      deliveryVehicleForFrame ||
        deal.vehicleDescription ||
        "NEW VEHICLE DELIVERY",
      width / 2,
      height - bottomBarHeight * 0.56
    );

    ctx.fillStyle = "#d4af37";
    ctx.font = `900 ${Math.round(bottomBarHeight * 0.22)}px Arial`;
    ctx.fillText(
      "DRIVEWITHARTURO · CHICAGO CARS US",
      width / 2,
      height - bottomBarHeight * 0.25
    );
    ctx.restore();
  };

  const drawStudioPreview = (canvas: HTMLCanvasElement | null) => {
    if (!canvas || studioPhotos.length === 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const width = canvas.width;
      const height = canvas.height;
      ctx.fillStyle = studioStyle === "clean" ? "#f8fafc" : "#020202";
      ctx.fillRect(0, 0, width, height);
      const ratio = Math.min(width / img.width, (height * 0.72) / img.height);
      const drawWidth = img.width * ratio;
      const drawHeight = img.height * ratio;
      ctx.drawImage(
        img,
        (width - drawWidth) / 2,
        height * 0.16,
        drawWidth,
        drawHeight
      );
      ctx.fillStyle = activeOverlay.color;
      ctx.font = `900 ${activeOverlay.size * 2}px Arial`;
      ctx.textAlign = "center";
      const y =
        activeOverlay.position === "top"
          ? height * 0.13
          : activeOverlay.position === "bottom"
          ? height * 0.84
          : height * 0.52;
      ctx.lineWidth = 8;
      ctx.strokeStyle = "rgba(0,0,0,0.8)";
      ctx.strokeText(activeOverlay.text, width / 2, y);
      ctx.fillText(activeOverlay.text, width / 2, y);
      if (studioFrameMode === "delivery") {
        drawDeliveryFrameOverlay(ctx, width, height);
      }
      drawDriveWithArturoWatermark(ctx, width, height);
    };
    img.src = studioPhotos[studioFrame % studioPhotos.length];
  };

  useEffect(
    () => drawStudioPreview(studioCanvasRef.current),
    [
      studioPhotos,
      studioFrame,
      studioStyle,
      activeOverlay,
      videoFormat,
      studioFrameMode,
      clientNameForFrame,
      deliveryVehicleForFrame,
      deliveryTemplate,
      watermarkPosition,
      watermarkOpacity,
    ]
  );

  const renderStudio = () => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(280px, 0.45fr) minmax(0, 1fr)",
        gap: 18,
      }}
    >
      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Content Studio</h2>
        <label
          style={{
            display: "grid",
            placeItems: "center",
            minHeight: 135,
            border: "2px dashed rgba(239,68,68,0.45)",
            borderRadius: 18,
            cursor: "pointer",
            textAlign: "center",
            padding: 18,
          }}
        >
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => handleStudioPhotoUpload(event.target.files)}
            style={{ display: "none" }}
          />
          <div>
            <div style={{ fontSize: 34 }}>📸</div>
            <b>Upload Vehicle Photos</b>
            <div style={{ color: "#d4d4d8", fontSize: 13 }}>
              Best: 12–36 photos
            </div>
          </div>
        </label>
        <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
          <Field label="Format">
            <select
              value={videoFormat}
              onChange={(event) =>
                setVideoFormat(event.target.value as VideoFormat)
              }
              style={inputStyle}
            >
              <option>9:16</option>
              <option>1:1</option>
              <option>16:9</option>
            </select>
          </Field>
          <Field label="Studio Style">
            <select
              value={studioStyle}
              onChange={(event) =>
                setStudioStyle(event.target.value as StudioStyle)
              }
              style={inputStyle}
            >
              <option value="luxury">Luxury</option>
              <option value="aggressive">Fast Aggressive</option>
              <option value="clean">Clean White</option>
              <option value="night">Night Street</option>
              <option value="latino">Latino Promo</option>
            </select>
          </Field>
          <Field label="Spin Speed">
            <select
              value={studioSpinSpeed}
              onChange={(event) =>
                setStudioSpinSpeed(event.target.value as StudioSpinSpeed)
              }
              style={inputStyle}
            >
              <option value="slow">Slow Spin</option>
              <option value="normal">Normal Spin</option>
              <option value="fast">Fast Spin</option>
            </select>
          </Field>
          <Field label="Watermark Position">
            <select
              value={watermarkPosition}
              onChange={(event) =>
                setWatermarkPosition(event.target.value as WatermarkPosition)
              }
              style={inputStyle}
            >
              <option value="bottom-center">Bottom Center</option>
              <option value="bottom-left">Bottom Left</option>
              <option value="bottom-right">Bottom Right</option>
              <option value="center">Center</option>
              <option value="diagonal">Diagonal</option>
            </select>
          </Field>
          <Field label={`Watermark Opacity ${watermarkOpacity}%`}>
            <input
              type="range"
              min="10"
              max="100"
              value={watermarkOpacity}
              onChange={(event) =>
                setWatermarkOpacity(Number(event.target.value))
              }
              style={{ width: "100%" }}
            />
          </Field>
          <Field label="Frame Overlay">
            <select
              value={studioFrameMode}
              onChange={(event) =>
                setStudioFrameMode(event.target.value as StudioFrameMode)
              }
              style={inputStyle}
            >
              <option value="vehicle">Vehicle Marketing</option>
              <option value="delivery">Client Delivery Picture</option>
            </select>
          </Field>
          {studioFrameMode === "delivery" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <Field label="Client Name">
                <input
                  value={clientNameForFrame}
                  onChange={(event) =>
                    setClientNameForFrame(event.target.value)
                  }
                  placeholder="Example: Alan"
                  style={inputStyle}
                />
              </Field>
              <Field label="Delivery Vehicle">
                <input
                  value={deliveryVehicleForFrame}
                  onChange={(event) =>
                    setDeliveryVehicleForFrame(event.target.value)
                  }
                  placeholder="Example: 2020 Ford Explorer ST"
                  style={inputStyle}
                />
              </Field>
              <Field label="Delivery Template">
                <select
                  value={deliveryTemplate}
                  onChange={(event) =>
                    setDeliveryTemplate(event.target.value as DeliveryTemplate)
                  }
                  style={inputStyle}
                >
                  <option value="congratulations">Congratulations</option>
                  <option value="welcome">Welcome To The Family</option>
                  <option value="veteran">Veteran Buyer</option>
                  <option value="family">Family Vehicle</option>
                  <option value="business">Business Owner</option>
                  <option value="firstTime">First Time Buyer</option>
                  <option value="referral">Referral</option>
                </select>
              </Field>
              <button
                onClick={generateDeliveryContentPackage}
                style={primaryButton}
              >
                🎉 Generate Delivery Content
              </button>
            </div>
          )}
          <Field label="TikTok CML Audio BPM">
            <input
              value={audioBpm}
              inputMode="numeric"
              onChange={(event) =>
                setAudioBpm(
                  Number(cleanNumericInput(event.target.value)) || 120
                )
              }
              style={inputStyle}
            />
          </Field>
          <Field label="Reel Platform">
            <select
              value={reelPlatform}
              onChange={(event) => setReelPlatform(event.target.value)}
              style={inputStyle}
            >
              <option>TikTok / Reels / Shorts</option>
              <option>TikTok</option>
              <option>Instagram Reels</option>
              <option>Facebook Reels</option>
              <option>YouTube Shorts</option>
            </select>
          </Field>
          <Field label="Reel Duration Seconds">
            <input
              value={reelDuration}
              inputMode="numeric"
              onChange={(event) =>
                setReelDuration(
                  Number(cleanNumericInput(event.target.value)) || 18
                )
              }
              style={inputStyle}
            />
          </Field>
          <button onClick={generateReelPlan} style={primaryButton}>
            🎬 Create Reel Plan
          </button>
          <button
            onClick={() => generateMarketingPackage()}
            style={primaryButton}
          >
            🚀 Generate Marketing Package
          </button>
          <button onClick={generatePostingTasks} style={buttonStyle}>
            📅 Build Posting Checklist
          </button>
          <Field label="Overlay">
            <select
              value={activeOverlay.id}
              onChange={(event) =>
                setActiveOverlay(
                  overlayTemplates.find(
                    (item) => item.id === Number(event.target.value)
                  ) || overlayTemplates[0]
                )
              }
              style={inputStyle}
            >
              {overlayTemplates.map((overlay) => (
                <option key={overlay.id} value={overlay.id}>
                  {overlay.text}
                </option>
              ))}
            </select>
          </Field>
          <Field label="New Overlay Text">
            <input
              value={overlayDraft.text}
              onChange={(event) =>
                setOverlayDraft((current) => ({
                  ...current,
                  text: event.target.value,
                }))
              }
              style={inputStyle}
            />
          </Field>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
          >
            <Field label="Position">
              <select
                value={overlayDraft.position}
                onChange={(event) =>
                  setOverlayDraft((current) => ({
                    ...current,
                    position: event.target.value as OverlayTemplate["position"],
                  }))
                }
                style={inputStyle}
              >
                <option>top</option>
                <option>center</option>
                <option>bottom</option>
              </select>
            </Field>
            <Field label="Color">
              <input
                type="color"
                value={overlayDraft.color}
                onChange={(event) =>
                  setOverlayDraft((current) => ({
                    ...current,
                    color: event.target.value,
                  }))
                }
                style={{ ...inputStyle, height: 48 }}
              />
            </Field>
          </div>
          <button onClick={saveOverlayTemplate} style={buttonStyle}>
            Save Overlay
          </button>
          <button onClick={generateHooks} style={primaryButton}>
            Generate Viral Hooks
          </button>
          <button onClick={exportStudioImage} style={buttonStyle}>
            Export Image
          </button>
          <button
            onClick={exportStudioVideo}
            disabled={exportingVideo}
            style={primaryButton}
          >
            {exportingVideo ? "Exporting..." : "Export Video"}
          </button>
        </div>
      </section>

      <section style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <h2 style={{ margin: 0 }}>Preview</h2>
          <button
            onClick={() => setStudioPlaying((playing) => !playing)}
            style={buttonStyle}
          >
            {studioPlaying ? "Pause Spin" : "Play Spin"}
          </button>
        </div>
        <div
          style={{
            margin: "0 auto",
            width:
              videoFormat === "9:16" ? 360 : videoFormat === "1:1" ? 460 : 620,
            maxWidth: "100%",
            aspectRatio:
              videoFormat === "9:16"
                ? "9/16"
                : videoFormat === "1:1"
                ? "1/1"
                : "16/9",
            borderRadius: 24,
            overflow: "hidden",
            background: getStudioBackground(),
            display: "grid",
            placeItems: "center",
            position: "relative",
          }}
        >
          {studioPhotos.length ? (
            <canvas
              ref={studioCanvasRef}
              width={720}
              height={
                videoFormat === "9:16"
                  ? 1280
                  : videoFormat === "1:1"
                  ? 720
                  : 405
              }
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <div style={{ color: "#d4d4d8", textAlign: "center", padding: 24 }}>
              Upload photos to preview the studio.
            </div>
          )}
        </div>
        {studioPhotos.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 8,
              overflowX: "auto",
              marginTop: 14,
              paddingBottom: 8,
            }}
          >
            {studioPhotos.map((photo, index) => (
              <button
                key={`${photo}-${index}`}
                onClick={() => setStudioFrame(index)}
                style={{
                  border:
                    studioFrame === index
                      ? "3px solid #ef4444"
                      : "1px solid rgba(255,255,255,0.2)",
                  borderRadius: 12,
                  padding: 0,
                  overflow: "hidden",
                  background: "transparent",
                }}
              >
                <img
                  src={photo}
                  alt="thumb"
                  style={{
                    width: 72,
                    height: 72,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </button>
            ))}
          </div>
        )}
        {exportedVideoUrl && (
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 16,
            }}
          >
            <button onClick={shareExportedVideo} style={primaryButton}>
              Share Video
            </button>
            <a
              href={exportedVideoUrl}
              download="drivewitharturo-video.webm"
              style={{ ...buttonStyle, textDecoration: "none" }}
            >
              Download Video
            </a>
          </div>
        )}
        <ReelPlanPanel />
        <MarketingPackagePanel />
        <PostingManagerPanel />

        {deliveryContentPackage && studioFrameMode === "delivery" && (
          <section style={{ ...cardStyle, marginTop: 18 }}>
            <h2 style={{ marginTop: 0 }}>🎉 Delivery Content Creator</h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 12,
              }}
            >
              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(0,0,0,0.25)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <b>Facebook Post</b>
                <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                  {deliveryContentPackage.deliveryFacebookPost}
                </div>
              </div>
              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(0,0,0,0.25)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <b>Google Review Request</b>
                <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                  {deliveryContentPackage.deliveryGoogleReviewRequest}
                </div>
              </div>
              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(0,0,0,0.25)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <b>Instagram Story</b>
                <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                  {deliveryContentPackage.deliveryInstagramStory}
                </div>
              </div>
              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(0,0,0,0.25)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <b>WhatsApp Status</b>
                <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                  {deliveryContentPackage.deliveryWhatsAppStatus}
                </div>
              </div>
              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(0,0,0,0.25)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <b>TikTok Cover</b>
                <div style={{ marginTop: 8 }}>
                  {deliveryContentPackage.deliveryTikTokCover}
                </div>
              </div>
            </div>
          </section>
        )}
        {generatedHooks.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <GeneratedHooks />
          </div>
        )}
      </section>
    </div>
  );

  const ReelPlanPanel = () =>
    reelPlan ? (
      <section style={{ ...cardStyle, marginTop: 18 }}>
        <h2 style={{ marginTop: 0 }}>🎬 AI Reel Builder</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
            marginBottom: 14,
          }}
        >
          <StatCard
            label="Platform"
            value={reelPlan.platform}
            sub={`${reelPlan.duration} second plan`}
          />
          <StatCard
            label="Audio BPM"
            value={reelPlan.bpm}
            sub={reelPlan.cutTiming}
          />
          <StatCard
            label="Thumbnail"
            value={reelPlan.thumbnailText}
            sub="Use as TikTok/Reels cover text"
          />
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.25)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Hook</b>
            <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
              {reelPlan.hook}
            </div>
          </div>

          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.25)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Voiceover Timing</b>
            <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
              {reelPlan.voiceover}
            </div>
          </div>

          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.25)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Scene Order</b>
            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              {reelPlan.sceneOrder.map((scene) => (
                <div
                  key={`${scene.scene}-${scene.time}`}
                  style={{ borderLeft: "4px solid #ef4444", paddingLeft: 12 }}
                >
                  <div>
                    <b>{scene.scene}</b> · {scene.time}
                  </div>
                  <div style={{ color: "#d4d4d8" }}>Shot: {scene.shot}</div>
                  <div style={{ color: "#d4d4d8" }}>
                    Edit: {scene.editDirection}
                  </div>
                  <div style={{ color: "#fff" }}>
                    Text: {scene.onScreenText}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.25)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Caption Pack</b>
            <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
              {reelPlan.caption}
            </div>
          </div>

          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.25)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <b style={{ color: "#ef4444" }}>Export Checklist</b>
            <ul style={{ marginBottom: 0 }}>
              {reelPlan.exportChecklist.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <button
            onClick={() =>
              navigator.clipboard?.writeText(
                [
                  reelPlan.hook,
                  reelPlan.voiceover,
                  reelPlan.caption,
                  reelPlan.cta,
                ].join("\n\n")
              )
            }
            style={primaryButton}
          >
            Copy Reel Package
          </button>
        </div>
      </section>
    ) : null;

  const MarketingPackagePanel = () =>
    marketingPackage ? (
      <section style={{ ...cardStyle, marginTop: 18 }}>
        <h2 style={{ marginTop: 0 }}>🎬 AI Director Marketing Package</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
            marginBottom: 14,
          }}
        >
          <StatCard
            label="Vehicle Type"
            value={marketingPackage.profile.vehicleType}
            sub={marketingPackage.vehicle}
          />
          <StatCard
            label="Content Score"
            value={`${marketingPackage.profile.contentScore}/100`}
            sub={marketingPackage.profile.reason}
          />
          <StatCard
            label="Beat Timing"
            value={`${marketingPackage.bpm} BPM`}
            sub={marketingPackage.cutTiming}
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 12,
          }}
        >
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.25)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#facc15" }}>Best Shot Plan</h3>
            {marketingPackage.bestShotPlan.map((shot, index) => (
              <div key={shot} style={{ marginBottom: 8 }}>
                {index + 1}. {shot}
              </div>
            ))}
          </div>

          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(0,0,0,0.25)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#facc15" }}>Storyboard</h3>
            {marketingPackage.storyboard.map((scene) => (
              <div key={scene.scene} style={{ marginBottom: 12 }}>
                <b>{scene.scene}</b> · {scene.time}
                <br />
                <span style={{ color: "#d4d4d8" }}>{scene.shot}</span>
                <br />
                <span>{scene.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.24)",
            }}
          >
            <b>Commercial Audio Reminder:</b>{" "}
            {marketingPackage.commercialAudioNote}
          </div>
          <Field label="Voiceover Script">
            <textarea
              readOnly
              value={marketingPackage.voiceover}
              style={{ ...inputStyle, minHeight: 110 }}
            />
          </Field>
          <Field label="TikTok / Captions">
            <textarea
              readOnly
              value={marketingPackage.captions.join("\n\n")}
              style={{ ...inputStyle, minHeight: 130 }}
            />
          </Field>
          <Field label="WhatsApp Post">
            <textarea
              readOnly
              value={marketingPackage.whatsappPost}
              style={{ ...inputStyle, minHeight: 100 }}
            />
          </Field>
          <Field label="Marketplace Listing">
            <textarea
              readOnly
              value={marketingPackage.marketplaceListing}
              style={{ ...inputStyle, minHeight: 110 }}
            />
          </Field>
          <Field label="OfferUp Listing">
            <textarea
              readOnly
              value={marketingPackage.offerUpListing}
              style={{ ...inputStyle, minHeight: 100 }}
            />
          </Field>
          <button
            onClick={() =>
              navigator.clipboard?.writeText(
                JSON.stringify(marketingPackage, null, 2)
              )
            }
            style={primaryButton}
          >
            Copy Full Package
          </button>
        </div>
      </section>
    ) : null;

  const PostingManagerPanel = () => (
    <section style={{ ...cardStyle, marginTop: 18 }}>
      <h2 style={{ marginTop: 0 }}>📅 AI Posting Manager</h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <StatCard
          label="Ready To Post"
          value={readyToPostCount}
          sub="Captions and checklists prepared"
        />
        <StatCard
          label="Posted Today"
          value={postedToday}
          sub="Marked posted in this workspace"
        />
        <StatCard
          label="Needs Content"
          value={needsContentInventory.length}
          sub="Inventory without posted content"
        />
      </div>

      <div
        style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}
      >
        <button onClick={generatePostingTasks} style={primaryButton}>
          Generate Posting Tasks
        </button>
        <button onClick={() => setPostingTasks([])} style={buttonStyle}>
          Clear Posting Board
        </button>
      </div>

      {postingTasks.length === 0 ? (
        <p style={{ color: "#d4d4d8" }}>
          Generate a marketing package or reel plan, then build a posting
          checklist for every platform.
        </p>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {postingTasks.slice(0, 24).map((task) => (
            <div
              key={task.id}
              style={{
                padding: 14,
                borderRadius: 16,
                background: "rgba(0,0,0,0.25)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <div>
                  <b style={{ color: "#facc15" }}>{task.platform}</b> ·{" "}
                  {task.vehicle}
                  {task.stockNumber && (
                    <span style={{ color: "#a1a1aa" }}>
                      {" "}
                      · Stock #{task.stockNumber}
                    </span>
                  )}
                </div>
                <select
                  value={task.status}
                  onChange={(event) =>
                    updatePostingTaskStatus(
                      task.id,
                      event.target.value as PostingStatus
                    )
                  }
                  style={{ ...inputStyle, maxWidth: 170 }}
                >
                  {POSTING_STATUSES.map((status) => (
                    <option key={status}>{status}</option>
                  ))}
                </select>
              </div>
              <textarea
                readOnly
                value={task.caption}
                style={{ ...inputStyle, minHeight: 92, marginTop: 10 }}
              />
              <div
                style={{
                  display: "grid",
                  gap: 4,
                  marginTop: 10,
                  color: "#d4d4d8",
                  fontSize: 13,
                }}
              >
                {task.checklist.map((item) => (
                  <div key={item}>✓ {item}</div>
                ))}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  marginTop: 12,
                }}
              >
                <button
                  onClick={() => navigator.clipboard?.writeText(task.caption)}
                  style={buttonStyle}
                >
                  Copy Caption
                </button>
                <button
                  onClick={() => updatePostingTaskStatus(task.id, "Posted")}
                  style={primaryButton}
                >
                  Mark Posted
                </button>
                <button
                  onClick={() =>
                    setPostingTasks((current) =>
                      current.filter((item) => item.id !== task.id)
                    )
                  }
                  style={buttonStyle}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  const renderAnalytics = () => (
    <div style={{ display: "grid", gap: 18 }}>
      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Analytics + ROI Dashboard</h2>
        <p style={{ color: "#d4d4d8", marginTop: -6 }}>
          See what is creating leads, what needs follow-up, and where Arturo
          should focus today.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 12,
          }}
        >
          <StatCard
            label="Posted Content"
            value={
              postingTasks.filter((task) => task.status === "Posted").length
            }
            sub="All platforms"
          />
          <StatCard
            label="Ready To Post"
            value={readyToPostCount}
            sub="Waiting for action"
          />
          <StatCard
            label="Hot Leads"
            value={hotLeads}
            sub="High-priority opportunities"
          />
          <StatCard
            label="Follow-Ups"
            value={followUpQueue.length}
            sub="Due / scheduled"
          />
          <StatCard
            label="Needs Content"
            value={needsContentInventory.length}
            sub="Inventory without posted content"
          />
          <StatCard
            label="Content ROI Signal"
            value={`${contentRoiSignal}%`}
            sub="Hot leads per posted content"
          />
        </div>
      </section>

      <section
        style={{
          ...cardStyle,
          borderColor: "rgba(250,204,21,0.45)",
          boxShadow: "0 0 40px rgba(250,204,21,0.12)",
        }}
      >
        <h2 style={{ marginTop: 0, color: "#facc15" }}>
          What Arturo Should Focus On Today
        </h2>
        <div style={{ fontSize: 22, fontWeight: 950, lineHeight: 1.35 }}>
          {arturoFocusToday}
        </div>
      </section>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 14,
        }}
      >
        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Leads by Source</h3>
          {leadsBySource.length ? (
            leadsBySource
              .slice(0, 8)
              .map(([source, count]) => (
                <Line key={source} label={source} value={`${count}`} />
              ))
          ) : (
            <p style={{ color: "#d4d4d8" }}>No lead source data yet.</p>
          )}
        </section>

        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Sold by Source</h3>
          {soldBySource.length ? (
            soldBySource
              .slice(0, 8)
              .map(([source, count]) => (
                <Line key={source} label={source} value={`${count}`} />
              ))
          ) : (
            <p style={{ color: "#d4d4d8" }}>
              Mark leads as Sold to start tracking close sources.
            </p>
          )}
        </section>

        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Posted by Platform</h3>
          {postedByPlatform.length ? (
            postedByPlatform
              .slice(0, 8)
              .map(([platform, count]) => (
                <Line key={platform} label={platform} value={`${count}`} />
              ))
          ) : (
            <p style={{ color: "#d4d4d8" }}>
              Mark posting tasks as Posted to track platform output.
            </p>
          )}
        </section>

        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Ready Content by Platform</h3>
          {platformReadyCounts.length ? (
            platformReadyCounts
              .slice(0, 8)
              .map(([platform, count]) => (
                <Line key={platform} label={platform} value={`${count}`} />
              ))
          ) : (
            <p style={{ color: "#d4d4d8" }}>
              No ready platform tasks right now.
            </p>
          )}
        </section>

        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Best Vehicle Content Types</h3>
          {bestVehicleTypes.length ? (
            bestVehicleTypes
              .slice(0, 8)
              .map(([type, count]) => (
                <Line key={type} label={type} value={`${count}`} />
              ))
          ) : (
            <p style={{ color: "#d4d4d8" }}>
              Add inventory to activate vehicle-type analytics.
            </p>
          )}
        </section>

        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Vehicles Needing Content</h3>
          {needsContentInventory.length ? (
            needsContentInventory.slice(0, 8).map((item) => (
              <div
                key={item.vehicle.id}
                style={{
                  padding: 11,
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <b>{item.vehicle.title}</b>
                <div style={{ color: "#d4d4d8", fontSize: 13 }}>
                  Content Score: {item.profile.contentScore} ·{" "}
                  {item.profile.vehicleType}
                </div>
              </div>
            ))
          ) : (
            <p style={{ color: "#d4d4d8" }}>
              All inventory has posted content or no inventory added.
            </p>
          )}
        </section>
      </div>
    </div>
  );

  const GeneratedHooks = () => (
    <section style={cardStyle}>
      <h2 style={{ marginTop: 0 }}>AI Viral Hooks</h2>
      <div style={{ display: "grid", gap: 10 }}>
        {generatedHooks.map((hook, index) => (
          <div
            key={index}
            style={{
              padding: 13,
              borderRadius: 14,
              background: "rgba(0,0,0,0.26)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            {hook}
          </div>
        ))}
      </div>
    </section>
  );

  const GeneratedFollowUp = () => (
    <section style={cardStyle}>
      <h2 style={{ marginTop: 0 }}>AI Follow-Up</h2>
      <textarea
        readOnly
        value={generatedFollowUp}
        style={{ ...inputStyle, minHeight: 110 }}
      />
      <button
        onClick={() => navigator.clipboard?.writeText(generatedFollowUp)}
        style={{ ...primaryButton, marginTop: 10 }}
      >
        Copy Follow-Up
      </button>
    </section>
  );

  const renderDeliveryCenter = () => (
    <div style={{ display: "grid", gap: 18 }}>
      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Delivery Center</h2>
        <p style={{ color: "#d4d4d8", marginTop: -6 }}>
          Track sold customers, delivery content, review requests, and referral
          opportunities from one place.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
          }}
        >
          <StatCard
            label="Deliveries This Month"
            value={deliveriesThisMonth}
            sub="Customer handoffs"
          />
          <StatCard
            label="Reviews Missing"
            value={deliveryReviewsMissing}
            sub="Requested but not posted"
          />
          <StatCard
            label="Referral Eligible"
            value={deliveryReferralEligible}
            sub="$100 check opportunities"
          />
          <StatCard
            label="Photos Uploaded"
            value={deliveryPhotosUploaded}
            sub="Delivery content ready"
          />
          <StatCard
            label="Videos Uploaded"
            value={deliveryVideosUploaded}
            sub="Reel opportunities"
          />
        </div>
      </section>

      <section style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Add Delivery</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          <Field label="Customer Name">
            <input
              value={deliveryDraft.customerName ?? ""}
              onChange={(event) =>
                setDeliveryDraft((current) => ({
                  ...current,
                  customerName: event.target.value,
                }))
              }
              style={inputStyle}
            />
          </Field>
          <Field label="Vehicle Delivered">
            <input
              value={deliveryDraft.vehicle ?? ""}
              onChange={(event) =>
                setDeliveryDraft((current) => ({
                  ...current,
                  vehicle: event.target.value,
                }))
              }
              style={inputStyle}
            />
          </Field>
          <Field label="Delivery Date">
            <input
              value={deliveryDraft.deliveryDate ?? ""}
              onChange={(event) =>
                setDeliveryDraft((current) => ({
                  ...current,
                  deliveryDate: event.target.value,
                }))
              }
              style={inputStyle}
              placeholder="MM/DD/YYYY"
            />
          </Field>
          <Field label="Lead Source">
            <input
              value={deliveryDraft.leadSource ?? ""}
              onChange={(event) =>
                setDeliveryDraft((current) => ({
                  ...current,
                  leadSource: event.target.value,
                }))
              }
              style={inputStyle}
              placeholder="TikTok, Facebook, Referral, Walk-In..."
            />
          </Field>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 10,
            marginTop: 14,
          }}
        >
          {[
            ["isReferral", "Referral customer"],
            ["isBusinessOwner", "Business owner"],
            ["isFirstTimeBuyer", "First-time buyer"],
            ["reviewRequested", "Review requested"],
            ["reviewPosted", "Review posted"],
            ["referralEligible", "$100 referral eligible"],
            ["photoUploaded", "Photo uploaded"],
            ["videoUploaded", "Video uploaded"],
          ].map(([key, label]) => (
            <label
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "#e5e7eb",
              }}
            >
              <input
                type="checkbox"
                checked={Boolean(deliveryDraft[key as keyof DeliveryRecord])}
                onChange={(event) =>
                  setDeliveryDraft((current) => ({
                    ...current,
                    [key]: event.target.checked,
                  }))
                }
              />
              {label}
            </label>
          ))}
        </div>

        <Field label="Delivery Notes">
          <textarea
            value={deliveryDraft.notes ?? ""}
            onChange={(event) =>
              setDeliveryDraft((current) => ({
                ...current,
                notes: event.target.value,
              }))
            }
            style={{ ...inputStyle, minHeight: 90 }}
          />
        </Field>

        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}
        >
          <button onClick={addDeliveryRecord} style={primaryButton}>
            Save Delivery
          </button>
          <button
            onClick={() =>
              navigator.clipboard?.writeText(buildDeliveryPackage().tiktok)
            }
            style={buttonStyle}
          >
            Copy TikTok Caption
          </button>
          <button
            onClick={() =>
              navigator.clipboard?.writeText(buildDeliveryPackage().facebook)
            }
            style={buttonStyle}
          >
            Copy Facebook Post
          </button>
          <button
            onClick={() =>
              navigator.clipboard?.writeText(buildDeliveryPackage().review)
            }
            style={buttonStyle}
          >
            Copy Review Request
          </button>
          <button
            onClick={() =>
              navigator.clipboard?.writeText(buildDeliveryPackage().referral)
            }
            style={buttonStyle}
          >
            Copy Referral Message
          </button>
        </div>
      </section>

      <section style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Monthly Delivery Wall</h3>
        {deliveryRecords.length ? (
          <div style={{ display: "grid", gap: 12 }}>
            {deliveryRecords.slice(0, 12).map((delivery) => {
              const packageData = buildDeliveryPackage(delivery);
              return (
                <div
                  key={delivery.id}
                  style={{
                    padding: 14,
                    borderRadius: 16,
                    background: "rgba(0,0,0,0.28)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <b style={{ fontSize: 18 }}>
                        {delivery.customerName || "Customer"}
                      </b>
                      <div style={{ color: "#d4d4d8", fontSize: 13 }}>
                        {delivery.vehicle || "Vehicle"} ·{" "}
                        {delivery.deliveryDate || "Delivery date"} ·{" "}
                        {delivery.leadSource || "No source"}
                      </div>
                    </div>
                    <div style={{ color: "#facc15", fontWeight: 900 }}>
                      {delivery.reviewPosted
                        ? "⭐ Review Posted"
                        : delivery.reviewRequested
                        ? "⏳ Review Pending"
                        : "⭐ Review Needed"}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 10,
                    }}
                  >
                    {delivery.isReferral && (
                      <span
                        style={{
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: "rgba(250,204,21,0.14)",
                          border: "1px solid rgba(250,204,21,0.35)",
                          color: "#facc15",
                          fontSize: 12,
                          fontWeight: 900,
                        }}
                      >
                        Referral
                      </span>
                    )}
                    {delivery.isBusinessOwner && (
                      <span
                        style={{
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: "rgba(250,204,21,0.14)",
                          border: "1px solid rgba(250,204,21,0.35)",
                          color: "#facc15",
                          fontSize: 12,
                          fontWeight: 900,
                        }}
                      >
                        Business Owner
                      </span>
                    )}
                    {delivery.isFirstTimeBuyer && (
                      <span
                        style={{
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: "rgba(250,204,21,0.14)",
                          border: "1px solid rgba(250,204,21,0.35)",
                          color: "#facc15",
                          fontSize: 12,
                          fontWeight: 900,
                        }}
                      >
                        First-Time Buyer
                      </span>
                    )}
                    {delivery.photoUploaded && (
                      <span
                        style={{
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: "rgba(250,204,21,0.14)",
                          border: "1px solid rgba(250,204,21,0.35)",
                          color: "#facc15",
                          fontSize: 12,
                          fontWeight: 900,
                        }}
                      >
                        Photo Ready
                      </span>
                    )}
                    {delivery.videoUploaded && (
                      <span
                        style={{
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: "rgba(250,204,21,0.14)",
                          border: "1px solid rgba(250,204,21,0.35)",
                          color: "#facc15",
                          fontSize: 12,
                          fontWeight: 900,
                        }}
                      >
                        Video Ready
                      </span>
                    )}
                    {(delivery.referralEligible || delivery.isReferral) && (
                      <span
                        style={{
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: "rgba(250,204,21,0.14)",
                          border: "1px solid rgba(250,204,21,0.35)",
                          color: "#facc15",
                          fontSize: 12,
                          fontWeight: 900,
                        }}
                      >
                        $100 Check
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 12,
                    }}
                  >
                    <button
                      onClick={() =>
                        navigator.clipboard?.writeText(packageData.tiktok)
                      }
                      style={buttonStyle}
                    >
                      Copy TikTok
                    </button>
                    <button
                      onClick={() =>
                        navigator.clipboard?.writeText(packageData.facebook)
                      }
                      style={buttonStyle}
                    >
                      Copy Facebook
                    </button>
                    <button
                      onClick={() =>
                        navigator.clipboard?.writeText(packageData.review)
                      }
                      style={buttonStyle}
                    >
                      Copy Review
                    </button>
                    <button
                      onClick={() =>
                        navigator.clipboard?.writeText(packageData.referral)
                      }
                      style={buttonStyle}
                    >
                      Copy Referral
                    </button>
                    <button
                      onClick={() =>
                        updateDeliveryRecord(delivery.id, {
                          reviewRequested: true,
                        })
                      }
                      style={buttonStyle}
                    >
                      Mark Review Requested
                    </button>
                    <button
                      onClick={() =>
                        updateDeliveryRecord(delivery.id, {
                          reviewPosted: true,
                        })
                      }
                      style={buttonStyle}
                    >
                      Mark Review Posted
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p style={{ color: "#d4d4d8" }}>
            No deliveries added yet. Add each sold customer here after delivery.
          </p>
        )}
      </section>

      <section style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>AI Monthly Recap Builder</h3>
        <p style={{ color: "#d4d4d8" }}>
          Copy this after selecting your last deliveries and use it as a TikTok
          / Facebook recap starter.
        </p>
        <textarea
          readOnly
          value={`May/Monthly recap idea:\n\nThank you to every client who trusted DriveWithArturo and Chicago Cars US. ${deliveryRecords
            .slice(0, 10)
            .map(
              (delivery) =>
                `${delivery.customerName || "Customer"} - ${
                  delivery.vehicle || "Vehicle"
                }`
            )
            .join(
              " | "
            )}\n\nReviews earned: ${reviewsPostedCount}\nReferral checks owed/paid: ${referralChecksPending}/${referralChecksPaid}\n\nWho's next? Text Arturo at (872) 364-0886. Hablamos Español.`}
          style={{ ...inputStyle, minHeight: 140 }}
        />
      </section>
    </div>
  );

  const renderReviewsReferrals = () => (
    <div style={{ display: "grid", gap: 18 }}>
      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Reviews & Referrals Hub</h2>
        <p style={{ color: "#d4d4d8", marginTop: -6 }}>
          Track review requests, posted reviews, Popl business card shares,
          TikTok links, and $100 referral checks.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
          }}
        >
          <StatCard
            label="Reviews Requested Today"
            value={reviewsRequestedToday}
            sub="Links sent today"
          />
          <StatCard
            label="Reviews Posted"
            value={reviewsPostedCount}
            sub={
              bestReviewPlatform?.count
                ? `Top: ${bestReviewPlatform.platform}`
                : "Waiting for first review"
            }
          />
          <StatCard
            label="Waiting on Review"
            value={reviewsWaitingCount}
            sub="Requested / follow-up needed"
          />
          <StatCard
            label="Referral Checks Pending"
            value={referralChecksPending}
            sub="$100 checks to process"
          />
          <StatCard
            label="Referral Checks Paid"
            value={referralChecksPaid}
            sub={money(totalReferralPayouts)}
          />
          <StatCard
            label="Referrals Sold"
            value={referralsSold}
            sub="Referral-linked deliveries"
          />
        </div>
      </section>

      <section
        style={{
          ...cardStyle,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 16,
        }}
      >
        <div>
          <h3 style={{ marginTop: 0 }}>Quick Review Links</h3>
          <div style={{ display: "grid", gap: 10 }}>
            {REVIEW_PLATFORMS.map((platform) => (
              <div
                key={platform}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  gap: 8,
                  alignItems: "center",
                  padding: 10,
                  borderRadius: 14,
                  background: "rgba(0,0,0,0.24)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <b>{platform}</b>
                {REVIEW_LINKS[platform] ? (
                  <a
                    href={REVIEW_LINKS[platform]}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      ...buttonStyle,
                      textDecoration: "none",
                      textAlign: "center",
                    }}
                  >
                    Open
                  </a>
                ) : (
                  <span style={{ color: "#a1a1aa", fontSize: 12 }}>
                    Add link later
                  </span>
                )}
                <button
                  onClick={() =>
                    REVIEW_LINKS[platform]
                      ? navigator.clipboard?.writeText(REVIEW_LINKS[platform])
                      : alert("Add Autotrader review link when available.")
                  }
                  style={buttonStyle}
                >
                  Copy
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 style={{ marginTop: 0 }}>Digital Business Card</h3>
          <div
            style={{
              padding: 16,
              borderRadius: 18,
              background:
                "linear-gradient(135deg, rgba(250,204,21,0.16), rgba(239,68,68,0.12))",
              border: "1px solid rgba(250,204,21,0.35)",
            }}
          >
            <div style={{ fontSize: 26, fontWeight: 1000, color: "#facc15" }}>
              Arturo Garza
            </div>
            <div style={{ fontWeight: 900 }}>
              DriveWithArturo · Chicago Cars US
            </div>
            <div style={{ color: "#d4d4d8", marginTop: 6 }}>
              Bilingual Automotive Sales Consultant
            </div>
            <div style={{ fontSize: 22, fontWeight: 1000, marginTop: 10 }}>
              (872) 364-0886
            </div>
            <a
              href={POPL_BUSINESS_CARD_URL}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#93c5fd", wordBreak: "break-all" }}
            >
              {POPL_BUSINESS_CARD_URL}
            </a>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: 8,
              marginTop: 12,
            }}
          >
            <button
              onClick={() => openShareUrl("sms", buildBusinessCardMessage())}
              style={primaryButton}
            >
              Text Card
            </button>
            <button
              onClick={() =>
                openShareUrl(
                  "email",
                  buildBusinessCardMessage(),
                  "Arturo Garza Digital Business Card"
                )
              }
              style={buttonStyle}
            >
              Email Card
            </button>
            <button
              onClick={() =>
                openShareUrl("whatsapp", buildBusinessCardMessage())
              }
              style={buttonStyle}
            >
              WhatsApp
            </button>
            <button
              onClick={() =>
                navigator.clipboard?.writeText(POPL_BUSINESS_CARD_URL)
              }
              style={buttonStyle}
            >
              Copy Link
            </button>
          </div>
        </div>

        <div>
          <h3 style={{ marginTop: 0 }}>TikTok Hub</h3>
          <div style={{ display: "grid", gap: 10 }}>
            <a
              href={TIKTOK_LINKS.chicagoCars}
              target="_blank"
              rel="noreferrer"
              style={{
                ...primaryButton,
                textDecoration: "none",
                textAlign: "center",
              }}
            >
              Open Chicago Cars US TikTok
            </a>
            <a
              href={TIKTOK_LINKS.driveWithArturo}
              target="_blank"
              rel="noreferrer"
              style={{
                ...buttonStyle,
                textDecoration: "none",
                textAlign: "center",
              }}
            >
              Open DriveWithArturo TikTok
            </a>
            <button
              onClick={() =>
                navigator.clipboard?.writeText(
                  `${TIKTOK_LINKS.chicagoCars}\n${TIKTOK_LINKS.driveWithArturo}`
                )
              }
              style={buttonStyle}
            >
              Copy TikTok Links
            </button>
          </div>
        </div>
      </section>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 0.55fr) minmax(0, 1fr)",
          gap: 18,
        }}
      >
        <section style={cardStyle}>
          <h2 style={{ marginTop: 0 }}>Add Review Request</h2>
          <div style={{ display: "grid", gap: 12 }}>
            <label style={{ display: "grid", gap: 8 }}>
              <span style={{ fontWeight: 850, fontSize: 13, color: "#e5e7eb" }}>
                Customer Name
              </span>
              <input
                value={reviewDraft.customerName || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setReviewDraft((current) => ({
                    ...(current || EMPTY_REVIEW_RECORD),
                    customerName: event.target.value,
                  }))
                }
                style={inputStyle}
              />
            </label>
            <label style={{ display: "grid", gap: 8 }}>
              <span style={{ fontWeight: 850, fontSize: 13, color: "#e5e7eb" }}>
                Phone
              </span>
              <input
                value={reviewDraft.phone || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setReviewDraft((current) => ({
                    ...(current || EMPTY_REVIEW_RECORD),
                    phone: event.target.value,
                  }))
                }
                style={inputStyle}
              />
            </label>
            <label style={{ display: "grid", gap: 8 }}>
              <span style={{ fontWeight: 850, fontSize: 13, color: "#e5e7eb" }}>
                Email
              </span>
              <input
                value={reviewDraft.email || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setReviewDraft((current) => ({
                    ...(current || EMPTY_REVIEW_RECORD),
                    email: event.target.value,
                  }))
                }
                style={inputStyle}
              />
            </label>
            <label style={{ display: "grid", gap: 8 }}>
              <span style={{ fontWeight: 850, fontSize: 13, color: "#e5e7eb" }}>
                Vehicle Sold
              </span>
              <input
                value={reviewDraft.vehicle || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setReviewDraft((current) => ({
                    ...(current || EMPTY_REVIEW_RECORD),
                    vehicle: event.target.value,
                  }))
                }
                style={inputStyle}
              />
            </label>
            <label style={{ display: "grid", gap: 8 }}>
              <span style={{ fontWeight: 850, fontSize: 13, color: "#e5e7eb" }}>
                Delivery Date
              </span>
              <input
                value={reviewDraft.deliveryDate || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setReviewDraft((current) => ({
                    ...(current || EMPTY_REVIEW_RECORD),
                    deliveryDate: event.target.value,
                  }))
                }
                placeholder="Today / 06-06-2026"
                style={inputStyle}
              />
            </label>
            <label style={{ display: "grid", gap: 8 }}>
              <span style={{ fontWeight: 850, fontSize: 13, color: "#e5e7eb" }}>
                Platform Requested
              </span>
              <select
                value={reviewDraft.platform || "Google"}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  setReviewDraft((current) => ({
                    ...(current || EMPTY_REVIEW_RECORD),
                    platform: event.target.value as ReviewPlatform,
                  }))
                }
                style={inputStyle}
              >
                {REVIEW_PLATFORMS.map((platform) => (
                  <option key={platform}>{platform}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 8 }}>
              <span style={{ fontWeight: 850, fontSize: 13, color: "#e5e7eb" }}>
                Status
              </span>
              <select
                value={reviewDraft.status || "Not Requested"}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  setReviewDraft((current) => ({
                    ...(current || EMPTY_REVIEW_RECORD),
                    status: event.target.value as ReviewStatus,
                  }))
                }
                style={inputStyle}
              >
                {REVIEW_STATUSES.map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 8 }}>
              <span style={{ fontWeight: 850, fontSize: 13, color: "#e5e7eb" }}>
                Notes
              </span>
              <textarea
                value={reviewDraft.notes || ""}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setReviewDraft((current) => ({
                    ...(current || EMPTY_REVIEW_RECORD),
                    notes: event.target.value,
                  }))
                }
                style={{ ...inputStyle, minHeight: 80 }}
              />
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: 8,
              }}
            >
              <button onClick={addReviewRecord} style={primaryButton}>
                Add Review
              </button>
              <button
                onClick={() =>
                  navigator.clipboard?.writeText(buildReviewRequestMessage())
                }
                style={buttonStyle}
              >
                Copy Request
              </button>
              <button
                onClick={() => openShareUrl("sms", buildReviewRequestMessage())}
                style={buttonStyle}
              >
                Text
              </button>
              <button
                onClick={() =>
                  openShareUrl("whatsapp", buildReviewRequestMessage())
                }
                style={buttonStyle}
              >
                WhatsApp
              </button>
              <button
                onClick={() =>
                  openShareUrl(
                    "email",
                    buildReviewRequestMessage(),
                    "Thank you for choosing Chicago Cars US"
                  )
                }
                style={buttonStyle}
              >
                Email
              </button>
            </div>
          </div>
        </section>

        <section style={cardStyle}>
          <h2 style={{ marginTop: 0 }}>Review Pipeline</h2>
          {reviewRecords.length === 0 ? (
            <p style={{ color: "#d4d4d8" }}>No review requests tracked yet.</p>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {reviewRecords.map((review) => (
                <div
                  key={review.id}
                  style={{
                    padding: 14,
                    borderRadius: 16,
                    background: "rgba(0,0,0,0.24)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <b style={{ fontSize: 18 }}>
                        {review.customerName || "Customer"}
                      </b>
                      <div style={{ color: "#d4d4d8" }}>
                        {review.vehicle || "Vehicle not listed"} ·{" "}
                        {review.platform}
                      </div>
                    </div>
                    <select
                      value={review.status}
                      onChange={(event) =>
                        updateReviewStatus(
                          review.id,
                          event.target.value as ReviewStatus
                        )
                      }
                      style={{ ...inputStyle, maxWidth: 190 }}
                    >
                      {REVIEW_STATUSES.map((status) => (
                        <option key={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ color: "#a1a1aa", fontSize: 13, marginTop: 8 }}>
                    Sent: {review.linkSentAt || "Not sent"} · Posted:{" "}
                    {review.postedAt || "Waiting"}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      marginTop: 10,
                    }}
                  >
                    <button
                      onClick={() =>
                        navigator.clipboard?.writeText(
                          buildReviewRequestMessage(review)
                        )
                      }
                      style={buttonStyle}
                    >
                      Copy Request
                    </button>
                    <button
                      onClick={() =>
                        openShareUrl("sms", buildReviewRequestMessage(review))
                      }
                      style={buttonStyle}
                    >
                      Text
                    </button>
                    <button
                      onClick={() =>
                        openShareUrl(
                          "whatsapp",
                          buildReviewRequestMessage(review)
                        )
                      }
                      style={buttonStyle}
                    >
                      WhatsApp
                    </button>
                    {REVIEW_LINKS[review.platform] && (
                      <a
                        href={REVIEW_LINKS[review.platform]}
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...buttonStyle, textDecoration: "none" }}
                      >
                        Open Link
                      </a>
                    )}
                  </div>
                  {review.notes && (
                    <div style={{ color: "#d4d4d8", marginTop: 8 }}>
                      {review.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 0.55fr) minmax(0, 1fr)",
          gap: 18,
        }}
      >
        <section style={cardStyle}>
          <h2 style={{ marginTop: 0 }}>$100 Referral Check Tracker</h2>
          <div style={{ display: "grid", gap: 12 }}>
            <Field label="Referring Person">
              <input
                value={referralDraft.referrerName}
                onChange={(event) =>
                  setReferralDraft((current) => ({
                    ...current,
                    referrerName: event.target.value,
                  }))
                }
                style={inputStyle}
              />
            </Field>
            <Field label="Referrer Phone">
              <input
                value={referralDraft.referrerPhone}
                onChange={(event) =>
                  setReferralDraft((current) => ({
                    ...current,
                    referrerPhone: event.target.value,
                  }))
                }
                style={inputStyle}
              />
            </Field>
            <Field label="Customer Referred">
              <input
                value={referralDraft.referredCustomer}
                onChange={(event) =>
                  setReferralDraft((current) => ({
                    ...current,
                    referredCustomer: event.target.value,
                  }))
                }
                style={inputStyle}
              />
            </Field>
            <Field label="Vehicle Purchased">
              <input
                value={referralDraft.vehiclePurchased}
                onChange={(event) =>
                  setReferralDraft((current) => ({
                    ...current,
                    vehiclePurchased: event.target.value,
                  }))
                }
                style={inputStyle}
              />
            </Field>
            <Field label="Sale Date">
              <input
                value={referralDraft.saleDate}
                onChange={(event) =>
                  setReferralDraft((current) => ({
                    ...current,
                    saleDate: event.target.value,
                  }))
                }
                style={inputStyle}
              />
            </Field>
            <Field label="Check Status">
              <select
                value={referralDraft.checkStatus}
                onChange={(event) =>
                  setReferralDraft((current) => ({
                    ...current,
                    checkStatus: event.target.value as ReferralCheckStatus,
                  }))
                }
                style={inputStyle}
              >
                {REFERRAL_STATUSES.map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </Field>
            <MoneyField
              label="Referral Amount"
              value={referralDraft.amount}
              onChange={(value) =>
                setReferralDraft((current) => ({
                  ...current,
                  amount: cleanMoney(value),
                }))
              }
            />
            <Field label="Notes">
              <textarea
                value={referralDraft.notes}
                onChange={(event) =>
                  setReferralDraft((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }
                style={{ ...inputStyle, minHeight: 80 }}
              />
            </Field>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: 8,
              }}
            >
              <button onClick={addReferralRecord} style={primaryButton}>
                Add Referral
              </button>
              <button
                onClick={() =>
                  navigator.clipboard?.writeText(buildReferralMessage())
                }
                style={buttonStyle}
              >
                Copy Message
              </button>
              <button
                onClick={() => openShareUrl("sms", buildReferralMessage())}
                style={buttonStyle}
              >
                Text
              </button>
              <button
                onClick={() => openShareUrl("whatsapp", buildReferralMessage())}
                style={buttonStyle}
              >
                WhatsApp
              </button>
            </div>
          </div>
        </section>

        <section style={cardStyle}>
          <h2 style={{ marginTop: 0 }}>Referral Check Pipeline</h2>
          {referralRecords.length === 0 ? (
            <p style={{ color: "#d4d4d8" }}>No referral checks tracked yet.</p>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {referralRecords.map((referral) => (
                <div
                  key={referral.id}
                  style={{
                    padding: 14,
                    borderRadius: 16,
                    background: "rgba(0,0,0,0.24)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <b style={{ fontSize: 18 }}>
                        {referral.referrerName || "Referrer"}
                      </b>
                      <div style={{ color: "#d4d4d8" }}>
                        Referred {referral.referredCustomer || "customer"} ·{" "}
                        {referral.vehiclePurchased || "vehicle"}
                      </div>
                    </div>
                    <select
                      value={referral.checkStatus}
                      onChange={(event) =>
                        updateReferralStatus(
                          referral.id,
                          event.target.value as ReferralCheckStatus
                        )
                      }
                      style={{ ...inputStyle, maxWidth: 180 }}
                    >
                      {REFERRAL_STATUSES.map((status) => (
                        <option key={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ color: "#a1a1aa", fontSize: 13, marginTop: 8 }}>
                    Amount: {money(referral.amount || 100)} · Check #:{" "}
                    {referral.checkNumber || "N/A"} · Paid:{" "}
                    {referral.paidDate || "Not paid"}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      marginTop: 10,
                    }}
                  >
                    <button
                      onClick={() =>
                        navigator.clipboard?.writeText(
                          buildReferralMessage(referral)
                        )
                      }
                      style={buttonStyle}
                    >
                      Copy Thank You
                    </button>
                    <button
                      onClick={() =>
                        openShareUrl("sms", buildReferralMessage(referral))
                      }
                      style={buttonStyle}
                    >
                      Text
                    </button>
                    <button
                      onClick={() =>
                        openShareUrl("whatsapp", buildReferralMessage(referral))
                      }
                      style={buttonStyle}
                    >
                      WhatsApp
                    </button>
                  </div>
                  {referral.notes && (
                    <div style={{ color: "#d4d4d8", marginTop: 8 }}>
                      {referral.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );

  const renderAdmin = () => (
    <section style={cardStyle}>
      <h2 style={{ marginTop: 0 }}>Admin + Reports</h2>
      {!adminUnlocked ? (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            placeholder="Admin PIN"
            value={pin}
            onChange={(event) => setPin(event.target.value)}
            style={{ ...inputStyle, maxWidth: 180 }}
          />
          <button
            onClick={() => setAdminUnlocked(pin === ADMIN_PIN)}
            style={primaryButton}
          >
            Unlock
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          <div
            style={{
              padding: 16,
              borderRadius: 18,
              background: "rgba(212,175,55,0.10)",
              border: "1px solid rgba(212,175,55,0.35)",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 950, color: "#facc15" }}>
              Data Backup + Import/Export Center
            </div>
            <div style={{ marginTop: 6, color: "#f4f4f5", lineHeight: 1.45 }}>
              Last backup: <b>{lastBackupDate}</b>. Export a full JSON backup
              before clearing data, switching computers, or making major
              upgrades.
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 10,
            }}
          >
            <button onClick={exportAllDataJson} style={primaryButton}>
              Export Full Backup JSON
            </button>
            <button onClick={importBackupJson} style={buttonStyle}>
              Import Backup JSON
            </button>
            <button onClick={exportDealsCsv} style={buttonStyle}>
              Export Deals CSV
            </button>
            <button onClick={exportLeadsCsv} style={buttonStyle}>
              Export Leads CSV
            </button>
            <button onClick={exportInventoryCsv} style={buttonStyle}>
              Export Inventory CSV
            </button>
            <button onClick={exportPostingTasksCsv} style={buttonStyle}>
              Export Posting Tasks CSV
            </button>
            <button onClick={exportReviewsCsv} style={buttonStyle}>
              Export Reviews CSV
            </button>
            <button onClick={exportReferralChecksCsv} style={buttonStyle}>
              Export Referral Checks CSV
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
              gap: 10,
            }}
          >
            <StatCard
              label="Saved Deals"
              value={savedDeals.length}
              sub="Included in backup"
            />
            <StatCard
              label="Leads"
              value={leads.length}
              sub="Included in backup"
            />
            <StatCard
              label="Inventory"
              value={inventory.length}
              sub="Included in backup"
            />
            <StatCard
              label="Posting Tasks"
              value={postingTasks.length}
              sub="Included in backup"
            />
          </div>

          <div
            style={{
              padding: 16,
              borderRadius: 18,
              background: "rgba(59,130,246,0.10)",
              border: "1px solid rgba(59,130,246,0.35)",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 950, color: "#93c5fd" }}>
              Cloud Sync Prep + Supabase Ready Mode
            </div>
            <div style={{ marginTop: 6, color: "#f4f4f5", lineHeight: 1.45 }}>
              Your data is still stored safely in this browser using local
              storage. This section prepares the app for a future Supabase cloud
              database without changing how the app works today.
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                gap: 10,
                marginTop: 14,
              }}
            >
              <StatCard
                label="Local Leads"
                value={leads.length}
                sub="Ready for migration export"
              />
              <StatCard
                label="Local Inventory"
                value={inventory.length}
                sub="Content profiles included"
              />
              <StatCard
                label="Local Deals"
                value={savedDeals.length}
                sub="OTD history included"
              />
              <StatCard
                label="Posting Tasks"
                value={postingTasks.length}
                sub="Platform workflow included"
              />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 10,
                marginTop: 14,
              }}
            >
              <button onClick={exportCloudMigrationJson} style={primaryButton}>
                Export Cloud Migration JSON
              </button>
              <button onClick={exportSupabaseSchemaSql} style={buttonStyle}>
                Export Supabase Schema SQL
              </button>
              <button
                onClick={() =>
                  navigator.clipboard?.writeText(buildSupabaseSchemaSql())
                }
                style={buttonStyle}
              >
                Copy Supabase Schema
              </button>
            </div>
            <div
              style={{
                marginTop: 14,
                display: "grid",
                gap: 8,
                color: "#d4d4d8",
                lineHeight: 1.5,
              }}
            >
              <div>✅ Future-ready IDs added during migration export</div>
              <div>
                ✅ Tables prepared: leads, inventory, deals, posting tasks,
                overlay templates
              </div>
              <div>✅ Inventory exports include AI content profiles</div>
              <div>⚠️ Export a full backup JSON before testing cloud sync</div>
              <div>🔒 No SSN or ITIN numbers should ever be stored here</div>
            </div>
          </div>

          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.24)",
            }}
          >
            <div style={{ fontWeight: 950, color: "#ef4444", marginBottom: 8 }}>
              Danger Zone
            </div>
            <div style={{ color: "#d4d4d8", marginBottom: 12 }}>
              Only use this to remove demo/test data after exporting a backup.
            </div>
            <button
              onClick={clearDemoWorkspaceData}
              style={{
                ...buttonStyle,
                borderColor: "rgba(239,68,68,0.55)",
                color: "#fecaca",
              }}
            >
              Clear All Workspace Data
            </button>
          </div>
        </div>
      )}
    </section>
  );

  const unlockApp = () => {
    if (passcodeInput.trim() === MASTER_PASSCODE) {
      setAppUnlocked(true);
      sessionStorage.setItem("dwa_master_unlock", "true");
      setPasscodeInput("");
    } else {
      alert("Incorrect passcode");
    }
  };

  const lockApp = () => {
    sessionStorage.removeItem("dwa_master_unlock");
    setAppUnlocked(false);
    setPasscodeInput("");
  };

  const Chatbot = () => (
    <div style={{ position: "fixed", right: 18, bottom: 18, zIndex: 50 }}>
      {chatOpen && (
        <div
          style={{
            width: "min(390px, calc(100vw - 36px))",
            maxHeight: "72vh",
            display: "grid",
            gridTemplateRows: "auto 1fr auto",
            background: "rgba(12,12,14,0.98)",
            border: "1px solid rgba(239,68,68,0.38)",
            borderRadius: 22,
            overflow: "hidden",
            color: "white",
            marginBottom: 12,
          }}
        >
          <div
            style={{
              padding: 16,
              borderBottom: "1px solid rgba(255,255,255,0.12)",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <b>🤖 DriveWithArturo AI</b>
            <button
              onClick={() => setChatOpen(false)}
              style={{ ...buttonStyle, padding: "6px 10px" }}
            >
              ×
            </button>
          </div>
          <div
            style={{ padding: 14, overflowY: "auto", display: "grid", gap: 10 }}
          >
            {chatMessages.map((message) => (
              <div
                key={message.id}
                style={{
                  justifySelf: message.sender === "user" ? "end" : "start",
                  maxWidth: "86%",
                  padding: "10px 12px",
                  borderRadius: 16,
                  background:
                    message.sender === "user"
                      ? "linear-gradient(135deg,#ef4444,#991b1b)"
                      : "rgba(255,255,255,0.09)",
                  lineHeight: 1.4,
                }}
              >
                {message.text}
              </div>
            ))}
          </div>
          <div
            style={{
              padding: 14,
              borderTop: "1px solid rgba(255,255,255,0.12)",
              display: "flex",
              gap: 8,
            }}
          >
            <input
              value={chatInput}
              onChange={(event) => setChatInput(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && sendChat()}
              placeholder="Ask about OTD, credit, trade..."
              style={inputStyle}
            />
            <button onClick={sendChat} style={primaryButton}>
              Send
            </button>
          </div>
        </div>
      )}
      <button
        onClick={() => setChatOpen((open) => !open)}
        style={{ ...primaryButton, borderRadius: 999, padding: "14px 18px" }}
      >
        {chatOpen ? "Close Chat" : "🤖 Ask Arturo AI"}
      </button>
    </div>
  );

  if (!appUnlocked) {
    return (
      <main
        style={{
          minHeight: "100vh",
          padding: 24,
          background:
            "radial-gradient(circle at top, rgba(250,204,21,0.18), transparent 32%), linear-gradient(135deg, #050505, #111827 55%, #3f0f0f)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontFamily: "Inter, system-ui, sans-serif",
          color: "white",
        }}
      >
        <section
          style={{
            width: "100%",
            maxWidth: 520,
            borderRadius: 28,
            padding: 34,
            background:
              "linear-gradient(145deg, rgba(12,12,14,0.96), rgba(24,24,27,0.88))",
            border: "1px solid rgba(250,204,21,0.5)",
            boxShadow:
              "0 30px 90px rgba(0,0,0,0.65), 0 0 45px rgba(250,204,21,0.13)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              color: "#facc15",
              fontWeight: 1000,
              letterSpacing: 3,
              fontSize: 13,
              marginBottom: 8,
            }}
          >
            PRIVATE ACCESS
          </div>
          <h1
            style={{ margin: 0, fontSize: 38, lineHeight: 1, fontWeight: 1000 }}
          >
            DriveWithArturo Studio
          </h1>
          <p
            style={{ color: "#d4d4d8", margin: "14px 0 26px", lineHeight: 1.5 }}
          >
            CRM • Inventory • Deal Desk • Studio • Analytics
          </p>

          <input
            type="password"
            inputMode="numeric"
            placeholder="Enter Passcode"
            value={passcodeInput}
            onChange={(event) => setPasscodeInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") unlockApp();
            }}
            style={{
              width: "100%",
              padding: "16px 18px",
              borderRadius: 14,
              border: "1px solid rgba(255,255,255,0.18)",
              background: "rgba(0,0,0,0.45)",
              color: "#fff",
              fontSize: 20,
              textAlign: "center",
              outline: "none",
              boxSizing: "border-box",
            }}
          />

          <button
            onClick={unlockApp}
            style={{
              width: "100%",
              marginTop: 16,
              padding: "16px 18px",
              borderRadius: 14,
              border: "none",
              background: "linear-gradient(135deg, #facc15, #d4af37)",
              color: "#000",
              fontWeight: 1000,
              fontSize: 18,
              cursor: "pointer",
              boxShadow: "0 14px 30px rgba(250,204,21,0.25)",
            }}
          >
            🔓 Unlock Studio
          </button>

          <div style={{ marginTop: 26, fontWeight: 1000, fontSize: 20 }}>
            Built for Chicago Cars US
          </div>
          <div
            style={{
              marginTop: 8,
              color: "#facc15",
              fontSize: 26,
              fontWeight: 1000,
            }}
          >
            📞 (872) 364-0886
          </div>
        </section>
      </main>
    );
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        padding: 24,
        background:
          "linear-gradient(rgba(0,0,0,0.25), rgba(0,0,0,0.86)), url('/bg.jpg') center top / cover fixed no-repeat",
        fontFamily: "Inter, system-ui, sans-serif",
        color: "white",
      }}
    >
      <style>{`
        select option { background: #18181b; color: #ffffff; }
        input::placeholder, textarea::placeholder { color: rgba(255,255,255,0.55); }
        @media print { button, nav, input, textarea, select { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      `}</style>

      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <div
            style={{
              color: "#ef4444",
              fontWeight: 950,
              letterSpacing: 2,
              fontSize: 13,
            }}
          >
            DRIVEWITHARTURO.BIZ
          </div>
          <h1 style={{ margin: "4px 0 8px", fontSize: 42, lineHeight: 1 }}>
            DriveWithArturo Studio
          </h1>
          <div
            style={{
              display: "inline-block",
              color: "#ffffff",
              fontSize: 25,
              fontWeight: 1000,
              letterSpacing: 0.2,
              padding: "10px 16px",
              borderRadius: 999,
              background:
                "linear-gradient(135deg, rgba(239,68,68,0.95), rgba(153,27,27,0.95))",
              border: "1px solid rgba(250,204,21,0.65)",
              boxShadow: "0 0 28px rgba(239,68,68,0.35)",
            }}
          >
            Built for Chicago Cars US · TEXT ARTURO: (872) 364-0886
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={() => setLanguage(language === "en" ? "es" : "en")}
            style={buttonStyle}
          >
            {language === "en" ? "🇲🇽 Español" : "🇺🇸 English"}
          </button>
          <button
            onClick={lockApp}
            style={{
              ...buttonStyle,
              background: "linear-gradient(135deg, #dc2626, #7f1d1d)",
              borderColor: "rgba(239,68,68,0.7)",
            }}
          >
            🔒 Lock
          </button>
          <button onClick={() => setActiveTab("admin")} style={buttonStyle}>
            Admin
          </button>
        </div>
      </header>

      <nav
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 22,
          position: "sticky",
          top: 0,
          zIndex: 25,
          padding: "10px 0",
          backdropFilter: "blur(10px)",
        }}
      >
        <TabButton id="dashboard" label="Dashboard" />
        <TabButton id="salesfloor" label="AI Sales Floor" />
        <TabButton id="analytics" label="Analytics" />
        <TabButton id="reviews" label="Reviews & Referrals" />
        <TabButton id="delivery" label="Delivery Center" />
        <TabButton id="crm" label="Leads" />
        <TabButton id="inventory" label="Inventory" />
        <TabButton id="otd" label="Deal Desk" />
        <TabButton id="studio" label="Studio" />
      </nav>

      {activeTab === "dashboard" && renderDashboard()}
      {activeTab === "salesfloor" && renderSalesFloor()}
      {activeTab === "analytics" && renderAnalytics()}
      {activeTab === "reviews" && renderReviewsReferrals()}
      {activeTab === "delivery" && renderDeliveryCenter()}
      {activeTab === "otd" && renderOtd()}
      {activeTab === "crm" && renderCrm()}
      {activeTab === "inventory" && renderInventory()}
      {activeTab === "studio" && renderStudio()}
      {activeTab === "admin" && renderAdmin()}

      {generatedHooks.length > 0 &&
        activeTab !== "dashboard" &&
        activeTab !== "studio" && (
          <div style={{ marginTop: 18 }}>
            <GeneratedHooks />
          </div>
        )}
      {generatedFollowUp &&
        activeTab !== "dashboard" &&
        activeTab !== "crm" && (
          <div style={{ marginTop: 18 }}>
            <GeneratedFollowUp />
          </div>
        )}

      <Chatbot />
    </main>
  );
}
